import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Link, useNavigate, useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import '@/App.css';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';
import { FileText, Users, Building2, Settings, Plus, Trash2, Trash, Edit, Pencil, Download, Eye, Calendar, Handshake, DollarSign, Home, MapPin, Calculator, Menu, X, Save, Clock, ChevronLeft, ChevronRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Utility function to format dates to dd/mm/yy
const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = String(date.getFullYear()).slice(-2);
  return `${day}/${month}/${year}`;
};

// Dashboard Component
// Home/Landing Page with Top-Tier Menu Groups
const HomePage = () => {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const userRole = user.role || 'admin';

  const topTierGroups = [
    {
      title: 'Home',
      description: 'Dashboard and overview',
      icon: <Home className="h-12 w-12" />,
      path: '/group/home',
      color: 'from-blue-500 to-blue-600',
      roles: ['admin']
    },
    {
      title: 'Client Management',
      description: 'Clients, contacts and relationships',
      icon: <Users className="h-12 w-12" />,
      path: '/group/clients',
      color: 'from-purple-500 to-purple-600',
      roles: ['admin']
    },
    {
      title: 'Properties',
      description: 'Property listings and management',
      icon: <MapPin className="h-12 w-12" />,
      path: '/group/properties',
      color: 'from-green-500 to-green-600',
      roles: ['admin']
    },
    {
      title: 'Financial',
      description: 'Invoices, commissions & cashflow',
      icon: <DollarSign className="h-12 w-12" />,
      path: '/group/financial',
      color: 'from-emerald-500 to-emerald-600',
      roles: ['admin', 'cashflow_only']
    },
    {
      title: 'Operations',
      description: 'Time tracking and reports',
      icon: <Clock className="h-12 w-12" />,
      path: '/group/operations',
      color: 'from-indigo-500 to-indigo-600',
      roles: ['admin']
    },
    {
      title: 'Settings',
      description: 'Application configuration',
      icon: <Settings className="h-12 w-12" />,
      path: '/group/settings',
      color: 'from-gray-500 to-gray-600',
      roles: ['admin']
    },
  ];

  const filteredGroups = topTierGroups.filter(group => group.roles.includes(userRole));

  return (
    <div className="space-y-8">
      <div className="text-center">
        <h1 className="text-4xl sm:text-5xl font-black text-black mb-3 tracking-tight">
          Welcome to Business Suite
        </h1>
        <p className="text-lg text-gray-700 font-medium">
          Select a category to explore
        </p>
      </div>

      {/* Top-Tier Group Cards - 50% smaller */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
        {filteredGroups.map((group, index) => (
          <Card
            key={index}
            className="cursor-pointer hover:shadow-xl transition-all duration-300 hover:scale-105 border-2 hover:border-blue-400"
            onClick={() => navigate(group.path)}
          >
            <CardContent className="p-4">
              <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${group.color} flex items-center justify-center text-white mb-3 shadow-md`}>
                {React.cloneElement(group.icon, { className: 'h-6 w-6' })}
              </div>
              <h3 className="text-base font-bold text-gray-900 mb-1">{group.title}</h3>
              <p className="text-xs text-gray-600 mb-2">{group.description}</p>
              <div className="flex items-center text-blue-600 font-semibold text-xs">
                <span>Explore</span>
                <ChevronRight className="h-3 w-3 ml-1" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Quick Access Section */}
      <Card className="bg-gradient-to-r from-blue-50 to-purple-50 border-2 border-blue-200">
        <CardContent className="p-6">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Quick Access</h2>
            <p className="text-gray-600 mb-4">Jump to your most used features</p>
            <div className="flex flex-wrap justify-center gap-3">
              {userRole === 'admin' && (
                <>
                  <Button onClick={() => navigate('/invoices')} className="bg-blue-600 hover:bg-blue-700">
                    <Plus className="h-4 w-4 mr-2" /> New Invoice
                  </Button>
                  <Button onClick={() => navigate('/time-tracking')} variant="outline">
                    <Clock className="h-4 w-4 mr-2" /> Start Timer
                  </Button>
                  <Button onClick={() => navigate('/customers')} variant="outline">
                    <Users className="h-4 w-4 mr-2" /> Add Client
                  </Button>
                  <Button onClick={() => navigate('/dashboard')} variant="outline">
                    <FileText className="h-4 w-4 mr-2" /> View Dashboard
                  </Button>
                </>
              )}
              <Button onClick={() => navigate('/cashflow-calculator')} variant="outline">
                <Calculator className="h-4 w-4 mr-2" /> Cashflow Calculator
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Group Pages - Second Level Navigation
const GroupPage = ({ groupKey }) => {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const userRole = user.role || 'admin';

  const groupsData = {
    home: {
      title: 'Home',
      description: 'Dashboard and Overview',
      items: [
        { title: 'Dashboard', description: 'Statistics and recent invoices', icon: <FileText className="h-10 w-10" />, path: '/dashboard', color: 'from-blue-500 to-blue-600', roles: ['admin'] },
      ]
    },
    clients: {
      title: 'Client Management',
      description: 'Manage Relationships',
      items: [
        { title: 'Clients', description: 'Main client CRM', icon: <Users className="h-10 w-10" />, path: '/customers', color: 'from-purple-500 to-purple-600', roles: ['admin'] },
        { title: 'Contacts', description: 'Contact management', icon: <Users className="h-10 w-10" />, path: '/contacts', color: 'from-pink-500 to-pink-600', roles: ['admin'] },
        { title: 'Customer', description: 'Customer database', icon: <Home className="h-10 w-10" />, path: '/purchasers', color: 'from-indigo-500 to-indigo-600', roles: ['admin'] },
        { title: 'Referral Partners', description: 'Partner relationships', icon: <Handshake className="h-10 w-10" />, path: '/referral-partners', color: 'from-teal-500 to-teal-600', roles: ['admin'] },
      ]
    },
    properties: {
      title: 'Properties',
      description: 'Property Management',
      items: [
        { title: 'Properties', description: 'Property listings', icon: <MapPin className="h-10 w-10" />, path: '/properties', color: 'from-green-500 to-green-600', roles: ['admin'] },
      ]
    },
    financial: {
      title: 'Financial',
      description: 'Financial Management',
      items: [
        { title: 'Invoices', description: 'Create and manage invoices', icon: <FileText className="h-10 w-10" />, path: '/invoices', color: 'from-amber-500 to-amber-600', roles: ['admin'] },
        { title: 'Client Invoices', description: 'Client billing', icon: <DollarSign className="h-10 w-10" />, path: '/client-invoices', color: 'from-yellow-500 to-yellow-600', roles: ['admin'] },
        { title: 'Referral Comm', description: 'Commission tracking', icon: <DollarSign className="h-10 w-10" />, path: '/commissions', color: 'from-orange-500 to-orange-600', roles: ['admin'] },
        { title: 'Cashflow Calc', description: 'Property cashflow calculator', icon: <Calculator className="h-10 w-10" />, path: '/cashflow-calculator', color: 'from-teal-500 to-teal-600', roles: ['admin', 'cashflow_only'] },
      ]
    },
    operations: {
      title: 'Operations',
      description: 'Business Operations',
      items: [
        { title: 'Time Tracking', description: 'Billable hours tracking', icon: <Clock className="h-10 w-10" />, path: '/time-tracking', color: 'from-indigo-500 to-indigo-600', roles: ['admin'] },
        { title: 'Reports', description: 'Business reports', icon: <FileText className="h-10 w-10" />, path: '/reports', color: 'from-pink-500 to-pink-600', roles: ['admin'] },
      ]
    },
    settings: {
      title: 'Settings',
      description: 'Configuration',
      items: [
        { title: 'Settings', description: 'Application settings', icon: <Settings className="h-10 w-10" />, path: '/settings', color: 'from-gray-500 to-gray-600', roles: ['admin'] },
      ]
    },
  };

  const groupData = groupsData[groupKey];
  if (!groupData) return null;

  const filteredItems = groupData.items.filter(item => item.roles.includes(userRole));

  return (
    <div className="space-y-8">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-gray-600">
        <button onClick={() => navigate('/')} className="hover:text-blue-600 font-medium">
          Home
        </button>
        <ChevronRight className="h-4 w-4" />
        <span className="text-gray-900 font-semibold">{groupData.title}</span>
      </div>

      {/* Header */}
      <div className="text-center">
        <h1 className="text-4xl font-black text-black mb-2 tracking-tight">
          {groupData.title}
        </h1>
        <p className="text-lg text-gray-700 font-medium">
          {groupData.description}
        </p>
      </div>

      {/* Items Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredItems.map((item, index) => (
          <Card
            key={index}
            className="cursor-pointer hover:shadow-2xl transition-all duration-300 hover:scale-105 border-2 hover:border-blue-400"
            onClick={() => navigate(item.path)}
          >
            <CardContent className="p-6">
              <div className={`w-16 h-16 rounded-xl bg-gradient-to-br ${item.color} flex items-center justify-center text-white mb-4 shadow-lg`}>
                {item.icon}
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">{item.title}</h3>
              <p className="text-gray-600 text-sm">{item.description}</p>
              <div className="mt-4 flex items-center text-blue-600 font-semibold text-sm">
                <span>Open</span>
                <ChevronRight className="h-4 w-4 ml-1" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Back Button */}
      <div className="flex justify-center">
        <Button variant="outline" onClick={() => navigate('/')} className="gap-2">
          <ChevronRight className="h-4 w-4 rotate-180" />
          Back to Home
        </Button>
      </div>
    </div>
  );
};

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchStats();
    fetchInvoices();
  }, []);

  useEffect(() => {
    fetchStats();
  }, [startDate, endDate]);

  const fetchStats = async () => {
    try {
      let url = `${API}/stats`;
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await axios.get(url);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
      toast.error('Failed to load statistics');
    }
  };

  const clearDateRange = () => {
    setStartDate('');
    setEndDate('');
  };

  const fetchInvoices = async () => {
    try {
      const response = await axios.get(`${API}/invoices`);
      setInvoices(response.data.slice(0, 5)); // Show only recent 5
    } catch (error) {
      console.error('Error fetching invoices:', error);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'bg-emerald-500';
      case 'sent': return 'bg-blue-500';
      case 'overdue': return 'bg-red-500';
      case 'draft': return 'bg-gray-500';
      default: return 'bg-gray-500';
    }
  };

  return (
    <div className="space-y-4 sm:space-y-8">
      <div>
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-black text-black mb-2 tracking-tight">Dashboard</h1>
        <p className="text-sm sm:text-base text-gray-700 font-medium">Overview of your invoice management</p>
      </div>

      {/* Date Range Filter */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg sm:text-xl">Date Range Filter</CardTitle>
          <CardDescription className="text-sm">Filter statistics by invoice date</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-4 items-end">
            <div className="flex-1 w-full">
              <Label htmlFor="start_date" className="text-sm">Start Date</Label>
              <Input
                id="start_date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                data-testid="start-date-input"
                className="w-full"
              />
            </div>
            <div className="flex-1 w-full">
              <Label htmlFor="end_date" className="text-sm">End Date</Label>
              <Input
                id="end_date"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                data-testid="end-date-input"
              />
            </div>
            <Button 
              variant="outline" 
              onClick={clearDateRange}
              data-testid="clear-date-range-btn"
            >
              Clear Filter
            </Button>
          </div>
          {(startDate || endDate) && (
            <p className="text-sm text-gray-500 mt-2">
              Showing data {startDate && `from ${startDate}`} {endDate && `to ${endDate}`}
            </p>
          )}
        </CardContent>
      </Card>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-6">
        <Card className="bg-gradient-to-br from-white to-blue-50 border-2 border-blue-200 hover:shadow-lg transition-all duration-300" data-testid="total-invoices-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-bold text-gray-800">Total Invoices</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-black text-blue-700">{stats?.total_invoices || 0}</div>
            <div className="text-sm text-gray-500 mt-1">${stats?.total_invoices_amount?.toFixed(2) || '0.00'}</div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-white to-green-50 border-2 border-green-200 hover:shadow-lg transition-all duration-300" data-testid="paid-invoices-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-bold text-gray-800">Paid Invoices</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-black text-green-700">{stats?.paid_invoices || 0}</div>
            <div className="text-sm text-gray-500 mt-1">${stats?.paid_invoices_amount?.toFixed(2) || '0.00'}</div>
          </CardContent>
        </Card>

        <Card 
          className="bg-gradient-to-br from-white to-orange-50 border-2 border-orange-200 hover:shadow-lg transition-all duration-300 cursor-pointer hover:scale-105" 
          data-testid="unreconciled-card"
          onClick={() => navigate('/invoices?filter=unreconciled')}
        >
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-bold text-gray-800">Unreconciled</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-black text-orange-700">{stats?.unreconciled_paid_count || 0}</div>
            <div className="text-sm text-gray-500 mt-1">${stats?.unreconciled_paid_amount?.toFixed(2) || '0.00'}</div>
            <div className="text-xs text-emerald-600 mt-1">Reconciled: {stats?.reconciled_paid_count || 0}</div>
            <div className="text-xs text-blue-600 mt-1 opacity-70">Click to view details →</div>
          </CardContent>
        </Card>

        <Card 
          className="bg-gradient-to-br from-white to-red-50 border-2 border-red-200 hover:shadow-lg transition-all duration-300 cursor-pointer hover:scale-105" 
          data-testid="unpaid-invoices-card"
          onClick={() => navigate('/invoices?filter=unpaid')}
        >
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-bold text-gray-800">Unpaid Invoices</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-black text-red-700">{stats?.unpaid_invoices || 0}</div>
            <div className="text-sm text-gray-500 mt-1">${stats?.unpaid_invoices_amount?.toFixed(2) || '0.00'}</div>
            <div className="text-xs text-blue-600 mt-1 opacity-70">Click to view details →</div>
          </CardContent>
        </Card>

        <Card className="stat-card" data-testid="total-revenue-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-600">Total Revenue</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-blue-600">${stats?.total_revenue?.toFixed(2) || '0.00'}</div>
            <div className="text-sm text-gray-500 mt-1">GST: ${stats?.total_gst_collected?.toFixed(2) || '0.00'}</div>
          </CardContent>
        </Card>

        <Card 
          className="bg-gradient-to-br from-white to-purple-50 border-2 border-purple-200 hover:shadow-lg transition-all duration-300 cursor-pointer hover:scale-105" 
          data-testid="unpaid-referrals-card"
          onClick={() => navigate('/commissions?filter=unpaid')}
        >
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-bold text-gray-800">Unpaid Referrals</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-black text-purple-700">${stats?.total_commissions_unpaid?.toFixed(2) || '0.00'}</div>
            <div className="text-xs text-gray-500 mt-1">Paid: ${stats?.total_commissions_paid?.toFixed(2) || '0.00'}</div>
            <div className="text-xs text-blue-600 mt-1 opacity-70">Click to view details →</div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Invoices */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle>Recent Invoices</CardTitle>
              <CardDescription>Your latest invoices</CardDescription>
            </div>
            <Button onClick={() => navigate('/invoices')} data-testid="view-all-invoices-btn">
              View All
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {invoices.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="whitespace-nowrap">Invoice #</TableHead>
                  <TableHead className="whitespace-nowrap">Client</TableHead>
                  <TableHead className="whitespace-nowrap">Date</TableHead>
                  <TableHead className="whitespace-nowrap">Amount</TableHead>
                  <TableHead className="whitespace-nowrap">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invoices.map((invoice) => (
                  <TableRow key={invoice.id}>
                    <TableCell className="font-medium whitespace-nowrap">{invoice.invoice_number}</TableCell>
                    <TableCell className="whitespace-nowrap">{invoice.customer_name}</TableCell>
                    <TableCell className="whitespace-nowrap">{formatDate(invoice.date)}</TableCell>
                    <TableCell className="whitespace-nowrap">${invoice.total.toFixed(2)}</TableCell>
                    <TableCell>
                      <Badge className={getStatusColor(invoice.status)}>
                        {invoice.status}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <FileText className="mx-auto h-12 w-12 mb-3 text-gray-400" />
              <p>No invoices yet. Create your first invoice!</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Invoices List Component
const InvoicesList = () => {
  const [invoices, setInvoices] = useState([]);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchInvoiceNumber, setSearchInvoiceNumber] = useState('');
  const [drillDownFilter, setDrillDownFilter] = useState('');
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    fetchInvoices();
    
    // Check for URL parameters to set drill-down filter
    const urlParams = new URLSearchParams(location.search);
    const filterParam = urlParams.get('filter');
    if (filterParam && (filterParam === 'unreconciled' || filterParam === 'unpaid')) {
      setDrillDownFilter(filterParam);
    }
  }, [location.search]);

  const fetchInvoices = async () => {
    try {
      const response = await axios.get(`${API}/invoices`);
      setInvoices(response.data);
    } catch (error) {
      console.error('Error fetching invoices:', error);
      toast.error('Failed to load invoices');
    }
  };

  const deleteInvoice = async (id) => {
    if (!window.confirm('Are you sure you want to delete this invoice? This action cannot be undone.')) return;
    
    try {
      await axios.delete(`${API}/invoices/${id}`);
      toast.success('Invoice deleted successfully');
      fetchInvoices();
    } catch (error) {
      console.error('Error deleting invoice:', error);
      toast.error('Failed to delete invoice');
    }
  };

  const downloadPDF = async (id, invoiceNumber) => {
    try {
      const response = await axios.get(`${API}/invoices/${id}/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice_${invoiceNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Invoice downloaded successfully');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Failed to download invoice');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'bg-emerald-500';
      case 'sent': return 'bg-blue-500';
      case 'overdue': return 'bg-red-500';
      case 'draft': return 'bg-gray-500';
      default: return 'bg-gray-500';
    }
  };

  const filteredInvoices = invoices.filter(inv => {
    // Status filter
    const matchesStatus = filterStatus === 'all' || inv.status === filterStatus;
    
    // Invoice number search
    const matchesInvoiceNumber = searchInvoiceNumber === '' || 
      inv.invoice_number.toLowerCase().includes(searchInvoiceNumber.toLowerCase());
    
    // Drill-down filter for dashboard navigation
    let matchesDrillDown = true;
    if (drillDownFilter === 'unreconciled') {
      matchesDrillDown = inv.status === 'paid' && !inv.bank_reconciliation_date;
    } else if (drillDownFilter === 'unpaid') {
      matchesDrillDown = inv.status !== 'paid';
    }
    
    return matchesStatus && matchesInvoiceNumber && matchesDrillDown;
  });

  return (
    <div className="space-y-6">
      <div className="sticky top-0 z-10 bg-yellow-50 pb-4 border-b border-yellow-200">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-4xl font-black text-black mb-2 tracking-tight">
              Invoices
              {drillDownFilter && (
                <span className="text-2xl font-medium text-blue-600 ml-3">
                  - {drillDownFilter === 'unreconciled' ? 'Unreconciled' : 'Unpaid'} Invoices
                </span>
              )}
            </h1>
            <p className="text-gray-700 font-medium">
              {drillDownFilter ? `Showing ${drillDownFilter === 'unreconciled' ? 'paid invoices that need reconciliation' : 'invoices that are not yet paid'}` : 'Manage your invoices'}
            </p>
            {drillDownFilter && (
              <div className="mt-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => {
                    setDrillDownFilter('');
                    navigate('/invoices');
                  }}
                  className="text-xs"
                >
                  ← Back to All Invoices
                </Button>
              </div>
            )}
          </div>
          <Button onClick={() => navigate('/invoices/create')} data-testid="create-invoice-btn">
            <Plus className="mr-2 h-4 w-4" /> Create Invoice
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <div className="flex justify-between items-center mb-4">
            <CardTitle>
              {drillDownFilter ? (
                <>
                  {drillDownFilter === 'unreconciled' ? 'Unreconciled' : 'Unpaid'} Invoices 
                  ({filteredInvoices.length} {filteredInvoices.length === 1 ? 'invoice' : 'invoices'})
                </>
              ) : (
                `Invoices (${filteredInvoices.length} of ${invoices.length})`
              )}
            </CardTitle>
          </div>
          <div className="flex gap-4 items-center flex-wrap">
            <div className="flex gap-2 items-center">
              <Label>Filter by Status:</Label>
              <Select 
                value={filterStatus} 
                onValueChange={setFilterStatus}
                disabled={!!drillDownFilter}
              >
                <SelectTrigger className="w-[180px]" data-testid="status-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="sent">Sent</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                  <SelectItem value="overdue">Overdue</SelectItem>
                </SelectContent>
              </Select>
              {drillDownFilter && (
                <Badge variant="secondary" className="ml-2">
                  Dashboard Filter: {drillDownFilter === 'unreconciled' ? 'Unreconciled' : 'Unpaid'}
                </Badge>
              )}
            </div>
            
            <div className="flex gap-2 items-center">
              <Label>Lookup by Invoice Number:</Label>
              <Input
                type="text"
                placeholder="Enter invoice number..."
                value={searchInvoiceNumber}
                onChange={(e) => setSearchInvoiceNumber(e.target.value)}
                className="w-[220px]"
                data-testid="invoice-number-search"
              />
              {searchInvoiceNumber && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setSearchInvoiceNumber('')}
                  className="px-2"
                  data-testid="clear-search"
                >
                  ✕
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {filteredInvoices.length > 0 ? (
            <div className="overflow-x-auto">
              <div className="border rounded-lg min-w-[980px]">
                {/* Fixed Header */}
                <div className="bg-gray-50 border-b px-4 py-3 text-xs font-semibold text-gray-700">
                  <div className="flex">
                    <div className="w-24 flex-shrink-0">Invoice #</div>
                    <div className="w-32 flex-shrink-0">Client</div>
                    <div className="w-40 flex-shrink-0">Property Address</div>
                    <div className="w-24 flex-shrink-0">Date</div>
                    <div className="w-24 flex-shrink-0">Due Date</div>
                    <div className="w-20 flex-shrink-0">Amount</div>
                    <div className="w-20 flex-shrink-0">Status</div>
                    <div className="w-20 flex-shrink-0">Reconciled</div>
                    <div className="w-36 flex-shrink-0 text-right">Actions</div>
                  </div>
                </div>
                
                {/* Scrollable Content */}
                <div className="max-h-[60vh] overflow-y-auto">
                  {filteredInvoices.map((invoice) => (
                  <div key={invoice.id} className="flex border-b border-gray-100 hover:bg-gray-50 px-4 py-3 text-sm">
                    <div className="w-24 flex-shrink-0 font-medium">{invoice.invoice_number}</div>
                    <div className="w-32 flex-shrink-0">{invoice.customer_name}</div>
                    <div className="w-40 flex-shrink-0">
                      {invoice.property_address ? (
                        <span className="text-blue-600 font-medium">
                          {invoice.property_address}
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </div>
                    <div className="w-24 flex-shrink-0">{formatDate(invoice.date)}</div>
                    <div className="w-24 flex-shrink-0">{formatDate(invoice.due_date)}</div>
                    <div className="w-20 flex-shrink-0 font-medium">${invoice.total.toFixed(2)}</div>
                    <div className="w-20 flex-shrink-0">
                      <Badge className={`${getStatusColor(invoice.status)} text-xs`}>
                        {invoice.status}
                      </Badge>
                    </div>
                    <div className="w-20 flex-shrink-0">
                      {invoice.status === 'paid' && invoice.bank_reconciliation_date ? (
                        <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-xs">
                          {formatDate(invoice.bank_reconciliation_date)}
                        </Badge>
                      ) : invoice.status === 'paid' ? (
                        <Badge variant="outline" className="bg-gray-50 text-gray-500 text-xs">
                          Pending
                        </Badge>
                      ) : (
                        <span className="text-gray-400 text-sm">-</span>
                      )}
                    </div>
                    <div className="w-36 flex-shrink-0 text-right">
                      <div className="flex gap-1 justify-end">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/invoices/${invoice.id}`)}
                          data-testid={`view-invoice-${invoice.id}`}
                          className="h-8 w-8 p-0"
                        >
                          <Eye className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/invoices/edit/${invoice.id}`)}
                          data-testid={`edit-invoice-${invoice.id}`}
                          className="h-8 w-8 p-0"
                        >
                          <Edit className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => downloadPDF(invoice.id, invoice.invoice_number)}
                          data-testid={`download-invoice-${invoice.id}`}
                          className="h-8 w-8 p-0"
                        >
                          <Download className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deleteInvoice(invoice.id)}
                          data-testid={`delete-invoice-${invoice.id}`}
                          className="h-8 w-8 p-0"
                        >
                          <Trash2 className="h-3 w-3 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            </div>
          ) : (
            <div className="text-center py-12 text-gray-500">
              <FileText className="mx-auto h-16 w-16 mb-4 text-gray-400" />
              <p className="text-lg mb-2">No invoices found</p>
              <p className="text-sm">Create your first invoice to get started</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Invoice Form Component
const InvoiceForm = ({ editMode = false }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [clients, setCustomers] = useState([]);
  const [referralPartners, setReferralPartners] = useState([]);
  const [properties, setProperties] = useState([]);
  const [invoiceType, setInvoiceType] = useState('standard'); // 'standard' or 'property'
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [propertyInvoiceStatus, setPropertyInvoiceStatus] = useState(null);
  const [selectedContractPart, setSelectedContractPart] = useState('');
  const [formData, setFormData] = useState({
    invoice_number: '',
    date: new Date().toISOString().split('T')[0],
    due_date: '',
    customer_id: '',
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    customer_address: '',
    customer_abn: '',
    items: [{ description: '', quantity: 1, total_amount: 0, unit_price: 0, tax_rate: 0, tax_amount: 0, amount: 0 }],
    subtotal: 0,
    tax_rate: 0,
    tax_amount: 0,
    total: 0,
    status: 'draft',
    notes: '',
    payment_terms: '',
    referral_partner_id: '',
    referral_partner_name: '',
    rebate_amount: 0,
    property_id: '',
    property_address: '',
    contract_part: ''
  });

  useEffect(() => {
    const loadData = async () => {
      await Promise.all([
        fetchCustomers(),
        fetchReferralPartners(),
        fetchProperties()
      ]);
      
      if (editMode && id) {
        fetchInvoice();
      } else {
        generateInvoiceNumber();
      }
    };
    
    loadData();
  }, [editMode, id]);

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/customers`);
      setCustomers(response.data);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const fetchReferralPartners = async () => {
    try {
      const response = await axios.get(`${API}/referral-partners`);
      setReferralPartners(response.data);
    } catch (error) {
      console.error('Error fetching referral partners:', error);
    }
  };

  const fetchProperties = async () => {
    try {
      const response = await axios.get(`${API}/properties`);
      setProperties(response.data);
    } catch (error) {
      console.error('Error fetching properties:', error);
    }
  };


  const fetchInvoice = async () => {
    try {
      const response = await axios.get(`${API}/invoices/${id}`);
      const invoiceData = response.data;
      
      // Map backend data to form structure
      const mappedData = {
        invoice_number: invoiceData.invoice_number || '',
        date: invoiceData.date ? new Date(invoiceData.date).toISOString().split('T')[0] : '',
        due_date: invoiceData.due_date ? new Date(invoiceData.due_date).toISOString().split('T')[0] : '',
        customer_id: invoiceData.customer_id || '',
        customer_name: invoiceData.customer_name || '',
        customer_email: invoiceData.customer_email || '',
        customer_phone: invoiceData.customer_phone || '',
        customer_address: invoiceData.customer_address || '',
        customer_abn: invoiceData.customer_abn || '',
        items: invoiceData.items && invoiceData.items.length > 0 
          ? invoiceData.items.map(item => {
              const unitPrice = parseFloat(item.unit_price) || 0;
              const taxRate = parseFloat(item.tax_rate) || 0;
              // Reconstruct per-unit GST-inclusive price from stored unit_price
              const perUnitGstInc = unitPrice * (1 + taxRate / 100);
              return {
                description: item.description || '',
                quantity: parseFloat(item.quantity) || 1,
                total_amount: Math.round(perUnitGstInc * 100) / 100,
                unit_price: unitPrice,
                tax_rate: taxRate,
                tax_amount: parseFloat(item.tax_amount) || 0,
                amount: parseFloat(item.amount) || 0
              };
            })
          : [{ description: '', quantity: 1, total_amount: 0, unit_price: 0, tax_rate: 0, tax_amount: 0, amount: 0 }],
        subtotal: parseFloat(invoiceData.subtotal) || 0,
        tax_rate: parseFloat(invoiceData.tax_rate) || 0,
        tax_amount: parseFloat(invoiceData.tax_amount) || 0,
        total: parseFloat(invoiceData.total) || 0,
        status: invoiceData.status || 'draft',
        notes: invoiceData.notes || '',
        payment_terms: invoiceData.payment_terms || '',
        referral_partner_id: invoiceData.referral_partner_id || '',
        referral_partner_name: invoiceData.referral_partner_name || '',
        rebate_amount: parseFloat(invoiceData.rebate_amount) || 0,
        property_id: invoiceData.property_id || '',
        property_address: invoiceData.property_address || '',
        contract_part: invoiceData.contract_part || ''
      };
      
      setFormData(mappedData);
      
      // If this is a property invoice, set the invoice type and fetch property data
      if (invoiceData.property_id) {
        setInvoiceType('property');
        // Find and set the selected property
        const property = properties.find(p => p.id === invoiceData.property_id);
        if (property) {
          setSelectedProperty(property);
        }
      }
      
      // Trigger recalculation of totals to ensure consistency
      setTimeout(() => {
        calculateTotals(mappedData.items);
      }, 100);
      
    } catch (error) {
      console.error('Error fetching invoice:', error);
      toast.error('Failed to load invoice');
    }
  };

  const generateInvoiceNumber = async () => {
    try {
      const response = await axios.get(`${API}/invoices/next-number`);
      setFormData(prev => ({
        ...prev,
        invoice_number: response.data.invoice_number
      }));
    } catch (error) {
      console.error('Error generating invoice number:', error);
      // Fallback to old method if new endpoint fails
      try {
        const response = await axios.get(`${API}/invoices`);
        const invoices = response.data;
        const nextNumber = invoices.length + 1;
        setFormData(prev => ({
          ...prev,
          invoice_number: `INV-${String(nextNumber).padStart(4, '0')}`
        }));
      } catch (fallbackError) {
        console.error('Error with fallback invoice number generation:', fallbackError);
      }
    }
  };

  const fetchPropertyInvoiceStatus = async (propertyId) => {
    if (!propertyId) {
      setPropertyInvoiceStatus(null);
      return;
    }
    
    try {
      const response = await axios.get(`${API}/properties/${propertyId}/invoice-status`);
      setPropertyInvoiceStatus(response.data);
      
      // Update form data with property info
      setFormData(prev => ({
        ...prev,
        property_id: propertyId,
        property_address: response.data.property_address
      }));
      
    } catch (error) {
      console.error('Error fetching property invoice status:', error);
      setPropertyInvoiceStatus(null);
      toast.error('Failed to load property invoice status');
    }
  };

  const updateInvoiceItemsForContractPart = (contractPart) => {
    if (!selectedProperty || selectedProperty.contract_type !== '2 part') return;
    
    const invoiceItems = [];
    
    if (contractPart === 'part1' && selectedProperty.part1_customer_invoice_amount > 0) {
      const gstInclusiveAmount = selectedProperty.part1_customer_invoice_amount;
      const baseAmount = gstInclusiveAmount / 1.10; // Remove GST to get base amount
      const gstAmount = gstInclusiveAmount - baseAmount; // Calculate GST component
      
      invoiceItems.push({
        description: `Services - Part 1 - ${selectedProperty.address}`,
        quantity: 1,
        total_amount: gstInclusiveAmount,
        unit_price: baseAmount,
        tax_rate: 10,
        tax_amount: gstAmount,
        amount: gstInclusiveAmount
      });
    } else if (contractPart === 'part2' && selectedProperty.part2_customer_invoice_amount > 0) {
      const gstInclusiveAmount = selectedProperty.part2_customer_invoice_amount;
      const baseAmount = gstInclusiveAmount / 1.10; // Remove GST to get base amount
      const gstAmount = gstInclusiveAmount - baseAmount; // Calculate GST component
      
      invoiceItems.push({
        description: `Services - Part 2 - ${selectedProperty.address}`,
        quantity: 1,
        total_amount: gstInclusiveAmount,
        unit_price: baseAmount,
        tax_rate: 10,
        tax_amount: gstAmount,
        amount: gstInclusiveAmount
      });
    } else if (contractPart === 'both') {
      // Add Part 1 if it has an amount
      if (selectedProperty.part1_customer_invoice_amount > 0) {
        const gstInclusiveAmount = selectedProperty.part1_customer_invoice_amount;
        const baseAmount = gstInclusiveAmount / 1.10;
        const gstAmount = gstInclusiveAmount - baseAmount;
        
        invoiceItems.push({
          description: `Services - Part 1 - ${selectedProperty.address}`,
          quantity: 1,
          total_amount: gstInclusiveAmount,
          unit_price: baseAmount,
          tax_rate: 10,
          tax_amount: gstAmount,
          amount: gstInclusiveAmount
        });
      }
      
      // Add Part 2 if it has an amount
      if (selectedProperty.part2_customer_invoice_amount > 0) {
        const gstInclusiveAmount = selectedProperty.part2_customer_invoice_amount;
        const baseAmount = gstInclusiveAmount / 1.10;
        const gstAmount = gstInclusiveAmount - baseAmount;
        
        invoiceItems.push({
          description: `Services - Part 2 - ${selectedProperty.address}`,
          quantity: 1,
          total_amount: gstInclusiveAmount,
          unit_price: baseAmount,
          tax_rate: 10,
          tax_amount: gstAmount,
          amount: gstInclusiveAmount
        });
      }
    }
    
    // If no items were created, add a default item
    if (invoiceItems.length === 0) {
      const partDescription = contractPart === 'part1' ? 'Part 1' : 
                             contractPart === 'part2' ? 'Part 2' : 
                             contractPart === 'both' ? 'Both Parts' : 'Services';
                             
      invoiceItems.push({
        description: `Services - ${partDescription} - ${selectedProperty.address}`,
        quantity: 1,
        total_amount: 0,
        unit_price: 0,
        tax_rate: 10,
        tax_amount: 0,
        amount: 0
      });
    }
    
    // Update form data with new items
    setFormData(prev => ({
      ...prev,
      items: invoiceItems
    }));
    
    // Calculate totals for the new items
    calculateTotals(invoiceItems);
  };

  const handleCustomerChange = (customerId) => {
    // Defer state update to prevent ResizeObserver errors with Select dropdown
    setTimeout(() => {
      const client = clients.find(c => c.id === customerId);
      if (client) {
        setFormData(prev => ({
          ...prev,
          customer_id: client.id,
          customer_name: client.company_name || client.name,
          customer_email: client.email || '',
          customer_phone: client.phone || '',
          customer_address: client.address || '',
          customer_abn: client.abn || ''
        }));
      }
    }, 0);
  };

  const handleReferralPartnerChange = (partnerId) => {
    // Defer state update to prevent ResizeObserver errors with Select dropdown
    setTimeout(() => {
      if (partnerId === 'none' || !partnerId) {
        // Clear referral partner
        setFormData(prev => ({
          ...prev,
          referral_partner_id: '',
          referral_partner_name: '',
          rebate_amount: 0
        }));
        return;
      }
      
      const partner = referralPartners.find(p => p.id === partnerId);
      if (partner) {
        // Don't auto-calculate - let user enter the rebate amount manually
        setFormData(prev => ({
          ...prev,
          referral_partner_id: partner.id,
          referral_partner_name: partner.name,
          rebate_amount: 0 // User will input this manually
        }));
      }
    }, 0);
  };

  const calculateDueDate = (invoiceDate, paymentTerms) => {
    if (!invoiceDate) return '';
    
    // Create a proper Date object from the invoice date string
    const date = new Date(invoiceDate);
    let daysToAdd = 0;
    
    switch (paymentTerms) {
      case 'Payment on receipt':
        daysToAdd = 0;
        break;
      case '7 day terms':
        daysToAdd = 7;
        break;
      case '14 day terms':
        daysToAdd = 14;
        break;
      case '30 day terms':
        daysToAdd = 30;
        break;
      default:
        return formData.due_date; // Keep existing due date if custom terms
    }
    
    date.setDate(date.getDate() + daysToAdd);
    return date.toISOString().split('T')[0];
  };

  const handlePaymentTermsChange = (terms) => {
    // Defer state update to prevent ResizeObserver errors with Select dropdown
    setTimeout(() => {
      const newDueDate = calculateDueDate(formData.date, terms);
      setFormData(prev => ({
        ...prev,
        payment_terms: terms,
        due_date: newDueDate
      }));
    }, 0);
  };

  const handleDateChange = (newDate) => {
    const newDueDate = calculateDueDate(newDate, formData.payment_terms);
    setFormData(prev => ({
      ...prev,
      date: newDate,
      due_date: newDueDate
    }));
  };

  const handlePropertySelection = (propertyId) => {
    const property = properties.find(p => p.id === propertyId);
    if (!property) return;

    // Defer ALL state updates to avoid ResizeObserver loop error with Select dropdown
    // Consolidate all state updates into a single batch to prevent cascading re-renders
    setTimeout(() => {
      setSelectedProperty(property);
      
      // Fetch invoice status for this property (async, will update state separately)
      fetchPropertyInvoiceStatus(propertyId);

      // Prepare all data before any state updates
      const invoiceItems = [];
      
      // Add property-related line items based on contract type
      if (property.contract_type === '1 part') {
        if (property.invoice_amount > 0) {
          const gstInclusiveAmount = property.invoice_amount;
          const baseAmount = gstInclusiveAmount / 1.10;
          const gstAmount = gstInclusiveAmount - baseAmount;
          
          invoiceItems.push({
            description: `Services - ${property.address}`,
            quantity: 1,
            total_amount: gstInclusiveAmount,
            unit_price: baseAmount,
            tax_rate: 10,
            tax_amount: gstAmount,
            amount: gstInclusiveAmount
          });
        }
      } else if (property.contract_type === '2 part') {
        invoiceItems.push({
          description: `Services related to ${property.address} - Select contract part first`,
          quantity: 1,
          total_amount: 0,
          unit_price: 0,
          tax_rate: 10,
          tax_amount: 0,
          amount: 0
        });
      }

      // Default item if none created
      if (invoiceItems.length === 0) {
        invoiceItems.push({
          description: `Services related to ${property.address}`,
          quantity: 1,
          unit_price: 0,
          tax_rate: 10,
          tax_amount: 0,
          amount: 0
        });
      }

      // Calculate due date
      let dueDate = '';
      if (property.contract_type === '1 part' && property.estimated_settlement_date) {
        dueDate = property.estimated_settlement_date;
      } else if (property.contract_type === '2 part' && property.part1_settlement_date) {
        dueDate = property.part1_settlement_date;
      }

      // Prepare customer data if assigned
      let customerData = {};
      if (property.customer_id) {
        const client = clients.find(c => c.id === property.customer_id);
        if (client) {
          customerData = {
            customer_id: client.id,
            customer_name: client.company_name || client.name,
            customer_email: client.email || '',
            customer_phone: client.phone || '',
            customer_address: client.address || '',
            customer_abn: client.abn || ''
          };
        }
      }

      // Prepare referral partner data if assigned
      let referralData = {};
      if (property.referral_partner_id) {
        const partner = referralPartners.find(p => p.id === property.referral_partner_id);
        if (partner) {
          referralData = {
            referral_partner_id: partner.id,
            referral_partner_name: partner.name,
            rebate_amount: property.referral_comm || 0
          };
        }
      }

      // SINGLE consolidated state update - prevents cascading re-renders
      setFormData(prev => ({
        ...prev,
        ...customerData,
        ...referralData,
        due_date: dueDate,
        items: invoiceItems,
        notes: `Property Address: ${property.address}\nCustomer: ${property.purchaser_name || 'Not assigned'}`,
        rebate_amount: referralData.rebate_amount || property.referral_comm || prev.rebate_amount || 0
      }));

      // Calculate totals after form data is set
      setTimeout(() => calculateTotals(invoiceItems), 10);
    }, 0);
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...formData.items];
    
    // For numeric fields, store the raw value but use parsed value for calculations
    if (field === 'quantity' || field === 'total_amount' || field === 'tax_rate') {
      // Store the input value as-is (allows partial typing like "10.")
      newItems[index][field] = value;
      
      // Use parsed values for calculations
      const quantity = parseFloat(newItems[index].quantity) || 0;
      const totalAmount = parseFloat(newItems[index].total_amount) || 0; // Per-unit GST-inclusive price
      const taxRate = parseFloat(newItems[index].tax_rate) || 0;
      
      // total_amount is the per-unit GST-inclusive price
      // unit_price = per-unit price excluding GST
      const unitPrice = taxRate > 0 ? totalAmount / (1 + taxRate / 100) : totalAmount;
      
      // Line subtotal (without GST) = quantity × unit price ex GST
      const itemSubtotal = quantity * unitPrice;
      
      // Line tax amount = subtotal × tax rate
      const taxAmount = (itemSubtotal * taxRate) / 100;
      
      // Round all calculated values to 2 decimal places
      newItems[index].unit_price = Math.round(unitPrice * 100) / 100;
      newItems[index].tax_amount = Math.round(taxAmount * 100) / 100;
      newItems[index].amount = Math.round(itemSubtotal * 100) / 100;
    } else {
      // For non-numeric fields, store as-is
      newItems[index][field] = value;
    }
    
    setFormData(prev => ({ ...prev, items: newItems }));
    calculateTotals(newItems);
  };

  const addItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, { description: '', quantity: 1, total_amount: 0, unit_price: 0, tax_rate: 0, tax_amount: 0, amount: 0 }]
    }));
  };

  const removeItem = (index) => {
    const newItems = formData.items.filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, items: newItems }));
    calculateTotals(newItems);
  };

  const calculateTotals = (items) => {
    // Subtotal = sum of (quantity × unit_price) excluding tax
    const subtotal = items.reduce((sum, item) => {
      const quantity = parseFloat(item.quantity) || 0;
      const unitPrice = parseFloat(item.unit_price) || 0;
      return sum + (quantity * unitPrice);
    }, 0);
    
    // Tax Amount = sum of all item-level taxes only (no invoice-level tax)
    const totalTax = items.reduce((sum, item) => sum + (parseFloat(item.tax_amount) || 0), 0);
    
    // Grand Total = Subtotal + Total Tax
    const total = subtotal + totalTax;
    
    // Round all totals to 2 decimal places
    const roundedSubtotal = Math.round(subtotal * 100) / 100;
    const roundedTotalTax = Math.round(totalTax * 100) / 100;
    const roundedTotal = Math.round(total * 100) / 100;
    
    setFormData(prev => ({
      ...prev,
      subtotal: roundedSubtotal,
      tax_amount: roundedTotalTax,
      total: roundedTotal
      // Note: rebate_amount is NOT recalculated - user enters it manually
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate contract part selection for property invoices with 2-part contracts
    if (invoiceType === 'property' && selectedProperty?.contract_type === '2 part') {
      if (!selectedContractPart) {
        toast.error('Please select which part of the 2-part contract to invoice');
        return;
      }
      
      // Update form data with selected contract part
      formData.contract_part = selectedContractPart;
    }
    
    // Clean and validate numeric data before submission
    const cleanedFormData = {
      ...formData,
      items: formData.items.map(item => ({
        ...item,
        quantity: parseFloat(item.quantity) || 0,
        unit_price: parseFloat(item.unit_price) || 0,
        tax_rate: parseFloat(item.tax_rate) || 0,
        tax_amount: parseFloat(item.tax_amount) || 0,
        amount: parseFloat(item.amount) || 0
      })),
      subtotal: parseFloat(formData.subtotal) || 0,
      tax_rate: parseFloat(formData.tax_rate) || 0,
      tax_amount: parseFloat(formData.tax_amount) || 0,
      total: parseFloat(formData.total) || 0,
      rebate_amount: parseFloat(formData.rebate_amount) || 0
    };
    
    try {
      if (editMode) {
        await axios.put(`${API}/invoices/${id}`, cleanedFormData);
        toast.success('Invoice updated successfully');
      } else {
        await axios.post(`${API}/invoices`, cleanedFormData);
        toast.success('Invoice created successfully');
      }
      navigate('/invoices');
    } catch (error) {
      console.error('Error saving invoice:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to save invoice';
      toast.error(errorMessage);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-4xl font-bold text-gray-900 mb-2">
          {editMode ? 'Edit Invoice' : 'Create Invoice'}
        </h1>
        <p className="text-gray-600">Fill in the details to {editMode ? 'update' : 'create'} an invoice</p>
      </div>

      <form onSubmit={handleSubmit}>
        <Card>
          <CardHeader>
            <CardTitle>Invoice Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Invoice Type Selection */}
            {!editMode && (
              <div className="border-b pb-6 mb-6">
                <div className="flex gap-4 items-center">
                  <Label className="text-base font-semibold">Invoice Type:</Label>
                  <div className="flex gap-4">
                    <Button
                      type="button"
                      variant={invoiceType === 'standard' ? 'default' : 'outline'}
                      onClick={() => setInvoiceType('standard')}
                      data-testid="standard-invoice-btn"
                    >
                      Standard Invoice
                    </Button>
                    <Button
                      type="button"
                      variant={invoiceType === 'property' ? 'default' : 'outline'}
                      onClick={() => setInvoiceType('property')}
                      data-testid="property-invoice-btn"
                    >
                      Property Related Invoice
                    </Button>
                  </div>
                </div>

                {/* Status Selection - Create Mode */}
                <div className="mt-4">
                  <Label htmlFor="status">Status</Label>
                  <Select value={formData.status} onValueChange={(val) => setFormData(prev => ({ ...prev, status: val }))}>
                    <SelectTrigger data-testid="invoice-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="draft">Draft</SelectItem>
                      <SelectItem value="sent">Sent</SelectItem>
                      <SelectItem value="paid">Paid</SelectItem>
                      <SelectItem value="overdue">Overdue</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Property Selection */}
                {invoiceType === 'property' && (
                  <div className="mt-4">
                    <Label htmlFor="property">Select Property</Label>
                    <Select value={selectedProperty?.id || ''} onValueChange={handlePropertySelection}>
                      <SelectTrigger data-testid="property-select">
                        <SelectValue placeholder="Select a property to populate invoice data" />
                      </SelectTrigger>
                      <SelectContent>
                        {properties.map((property) => (
                          <SelectItem key={property.id} value={property.id}>
                            {`${property.address || 'Unknown'} - ${property.purchaser_name || 'No Customer'}`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Property Invoice Status and Contract Part Selection */}
                {invoiceType === 'property' && selectedProperty && propertyInvoiceStatus && (
                  <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                    <h4 className="font-medium text-gray-800 mb-3">Property Invoice Status</h4>
                    
                    {propertyInvoiceStatus.contract_type === '1 part' ? (
                      <div>
                        {propertyInvoiceStatus.can_invoice ? (
                          <div className="flex items-center text-green-600">
                            <span className="text-sm">✅ Ready to invoice - No existing invoices</span>
                          </div>
                        ) : (
                          <div className="flex items-center text-red-600">
                            <span className="text-sm">❌ Cannot invoice - Property already has {propertyInvoiceStatus.existing_invoices} invoice(s)</span>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <div className="text-sm text-gray-600 mb-2">
                          2-Part Contract - Select what to invoice:
                        </div>
                        
                        <div className="space-y-2">
                          {/* Part 1 Only */}
                          <div className="flex items-center space-x-3">
                            <input
                              type="radio"
                              id="part1_only"
                              name="contract_part"
                              value="part1"
                              checked={selectedContractPart === 'part1'}
                              onChange={(e) => {
                                setSelectedContractPart(e.target.value);
                                updateInvoiceItemsForContractPart(e.target.value);
                              }}
                              disabled={propertyInvoiceStatus.part1_invoiced}
                              className="form-radio"
                            />
                            <label 
                              htmlFor="part1_only" 
                              className={`text-sm ${propertyInvoiceStatus.part1_invoiced ? 'text-gray-400 line-through' : 'text-gray-700'}`}
                            >
                              Part 1 Only
                              {propertyInvoiceStatus.part1_invoiced ? ' (Already Invoiced ❌)' : ' (Available ✅)'}
                            </label>
                          </div>
                          
                          {/* Part 2 Only - Only available if Part 1 is already invoiced */}
                          <div className="flex items-center space-x-3">
                            <input
                              type="radio"
                              id="part2_only"
                              name="contract_part"
                              value="part2"
                              checked={selectedContractPart === 'part2'}
                              onChange={(e) => {
                                setSelectedContractPart(e.target.value);
                                updateInvoiceItemsForContractPart(e.target.value);
                              }}
                              disabled={propertyInvoiceStatus.part2_invoiced || !propertyInvoiceStatus.part1_invoiced}
                              className="form-radio"
                            />
                            <label 
                              htmlFor="part2_only" 
                              className={`text-sm ${
                                propertyInvoiceStatus.part2_invoiced ? 'text-gray-400 line-through' : 
                                !propertyInvoiceStatus.part1_invoiced ? 'text-gray-400' : 'text-gray-700'
                              }`}
                            >
                              Part 2 Only
                              {propertyInvoiceStatus.part2_invoiced ? ' (Already Invoiced ❌)' : 
                               !propertyInvoiceStatus.part1_invoiced ? ' (Part 1 must be invoiced first)' : 
                               ' (Available ✅)'}
                            </label>
                          </div>
                          
                          {/* Both Parts - Only available if neither part is invoiced */}
                          <div className="flex items-center space-x-3">
                            <input
                              type="radio"
                              id="both_parts"
                              name="contract_part"
                              value="both"
                              checked={selectedContractPart === 'both'}
                              onChange={(e) => {
                                setSelectedContractPart(e.target.value);
                                updateInvoiceItemsForContractPart(e.target.value);
                              }}
                              disabled={propertyInvoiceStatus.part1_invoiced || propertyInvoiceStatus.part2_invoiced}
                              className="form-radio"
                            />
                            <label 
                              htmlFor="both_parts" 
                              className={`text-sm ${
                                (propertyInvoiceStatus.part1_invoiced || propertyInvoiceStatus.part2_invoiced) ? 'text-gray-400' : 'text-gray-700'
                              }`}
                            >
                              Both Parts Together
                              {(propertyInvoiceStatus.part1_invoiced || propertyInvoiceStatus.part2_invoiced) ? 
                               ' (One or both parts already invoiced)' : 
                               ' (Available ✅)'}
                            </label>
                          </div>
                        </div>
                        
                        {propertyInvoiceStatus.existing_invoices > 0 && (
                          <div className="text-xs text-gray-500 mt-2">
                            Existing invoices: {propertyInvoiceStatus.existing_invoices}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Status Selection - Edit Mode */}
            {editMode && (
              <div className="border-b pb-6 mb-6">
                <div className="mt-4">
                  <Label htmlFor="status">Status</Label>
                  {formData.status === 'paid' ? (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-md">
                        <Badge className="bg-emerald-500 text-white">Paid</Badge>
                        <span className="text-sm text-green-700 font-medium">
                          This invoice has been paid and cannot be changed to protect financial integrity.
                        </span>
                      </div>
                      <p className="text-xs text-gray-500">
                        Status is locked for paid invoices. Use the reconciliation features if needed.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Select 
                        value={formData.status} 
                        onValueChange={(val) => setFormData(prev => ({ ...prev, status: val }))}
                      >
                        <SelectTrigger data-testid="invoice-status-select-edit">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="draft">Draft</SelectItem>
                          <SelectItem value="sent">Sent</SelectItem>
                          <SelectItem value="paid">Paid</SelectItem>
                          <SelectItem value="overdue">Overdue</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-gray-500">
                        Note: Once marked as paid, status cannot be changed to protect financial records.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Basic Info */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="invoice_number">Invoice Number</Label>
                <Input
                  id="invoice_number"
                  value={formData.invoice_number}
                  onChange={(e) => setFormData(prev => ({ ...prev, invoice_number: e.target.value }))}
                  required
                  data-testid="invoice-number-input"
                />
              </div>
              <div>
                <Label htmlFor="date">Date</Label>
                <Input
                  id="date"
                  type="date"
                  value={formData.date}
                  onChange={(e) => handleDateChange(e.target.value)}
                  required
                  data-testid="invoice-date-input"
                />
              </div>
              <div>
                <Label htmlFor="due_date">Due Date</Label>
                <Input
                  id="due_date"
                  type="date"
                  value={formData.due_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, due_date: e.target.value }))}
                  required
                  data-testid="invoice-due-date-input"
                />
              </div>
            </div>

            {/* Client Selection */}
            <div>
              <Label htmlFor="client">Select Client</Label>
              <Select value={formData.customer_id} onValueChange={handleCustomerChange}>
                <SelectTrigger data-testid="client-select">
                  <SelectValue placeholder="Select a client" />
                </SelectTrigger>
                <SelectContent>
                  {clients.map((client) => (
                    <SelectItem key={client.id} value={client.id}>
                      {client.company_name ? `${client.company_name} (${client.name})` : client.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Client Details (Read-only) */}
            {formData.customer_name && (
              <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                <h3 className="font-semibold text-gray-900">Client Details</h3>
                <p className="text-sm text-gray-600"><strong>Name:</strong> {formData.customer_name}</p>
                {formData.customer_email && <p className="text-sm text-gray-600"><strong>Email:</strong> {formData.customer_email}</p>}
                {formData.customer_phone && <p className="text-sm text-gray-600"><strong>Phone:</strong> {formData.customer_phone}</p>}
                {formData.customer_address && <p className="text-sm text-gray-600"><strong>Address:</strong> {formData.customer_address}</p>}
                {formData.customer_abn && <p className="text-sm text-gray-600"><strong>ABN:</strong> {formData.customer_abn}</p>}
              </div>
            )}

            {/* Referral Partner Selection */}
            <div>
              <Label htmlFor="referral_partner">Referral Partner (Optional)</Label>
              <Select value={formData.referral_partner_id || 'none'} onValueChange={handleReferralPartnerChange}>
                <SelectTrigger data-testid="referral-partner-select">
                  <SelectValue placeholder="Select a referral partner" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {referralPartners.map((partner) => (
                    <SelectItem key={partner.id} value={partner.id}>
                      {partner.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {formData.referral_partner_name && (
                <div className="mt-2">
                  <Label htmlFor="rebate_amount">Referral Partners Comm Amount ($)</Label>
                  <Input
                    id="rebate_amount"
                    type="number"
                    value={formData.rebate_amount}
                    onChange={(e) => setFormData(prev => ({ ...prev, rebate_amount: parseFloat(e.target.value) || 0 }))}
                    min="0"
                    step="0.01"
                    placeholder="Enter commission amount"
                    data-testid="rebate-amount-input"
                  />
                </div>
              )}
            </div>


            {/* Items */}
            <div>
              <div className="flex justify-between items-center mb-3">
                <Label>Items</Label>
                <Button type="button" onClick={addItem} size="sm" data-testid="add-item-btn">
                  <Plus className="mr-2 h-4 w-4" /> Add Item
                </Button>
              </div>
              <div className="space-y-3">
                {formData.items.map((item, index) => (
                  <div key={index} className="grid grid-cols-12 gap-2 items-end border p-3 rounded-lg">
                    <div className="col-span-4">
                      <Label>Description</Label>
                      <Input
                        value={item.description}
                        onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                        placeholder="Item description"
                        required
                        data-testid={`item-description-${index}`}
                      />
                    </div>
                    <div className="col-span-1">
                      <Label>Qty</Label>
                      <Input
                        type="text"
                        value={item.quantity}
                        onChange={(e) => {
                          const value = e.target.value;
                          // Allow numbers and decimal point
                          if (/^[\d.]*$/.test(value)) {
                            handleItemChange(index, 'quantity', value);
                          }
                        }}
                        placeholder="1"
                        required
                        data-testid={`item-quantity-${index}`}
                      />
                    </div>
                    <div className="col-span-2">
                      <Label>Unit Price (GST Inc.)</Label>
                      <Input
                        type="text"
                        value={item.total_amount || ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          // Allow negative numbers, digits, and single decimal point (for part payments received)
                          if (/^-?[\d]*\.?[\d]*$/.test(value)) {
                            handleItemChange(index, 'total_amount', value);
                          }
                        }}
                        placeholder="0.00"
                        required
                        data-testid={`item-total-${index}`}
                      />
                    </div>
                    <div className="col-span-2">
                      <Label>Line Total (GST Inc.)</Label>
                      <div className="px-3 py-2 bg-gray-100 border border-gray-300 rounded-md text-sm font-mono">
                        ${((parseFloat(item.total_amount) || 0) * (parseFloat(item.quantity) || 0)).toFixed(2)}
                      </div>
                    </div>
                    <div className="col-span-2">
                      <Label>GST (%)</Label>
                      <Input
                        type="text"
                        value={item.tax_rate || 0}
                        onChange={(e) => {
                          const value = e.target.value;
                          // Allow numbers and decimal point, limit to reasonable percentage
                          if (/^[\d.]*$/.test(value) && (value === '' || parseFloat(value) <= 100)) {
                            handleItemChange(index, 'tax_rate', value);
                          }
                        }}
                        placeholder="10"
                        data-testid={`item-gst-${index}`}
                      />
                    </div>
                    <div className="col-span-2">
                      <Label>Amount</Label>
                      <Input
                        type="number"
                        value={item.amount.toFixed(2)}
                        disabled
                        className="bg-gray-50"
                      />
                    </div>
                    <div className="col-span-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeItem(index)}
                        disabled={formData.items.length === 1}
                        data-testid={`remove-item-${index}`}
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Totals */}
            <div className="bg-gray-50 p-4 rounded-lg space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* GST rate removed - now calculated per line item */}
                {/* Status moved to Invoice Type section */}
              </div>
              
              <div className="border-t pt-3 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Subtotal:</span>
                  <span className="font-medium">${formData.subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Total GST:</span>
                  <span className="font-medium">${formData.tax_amount.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-lg font-bold border-t pt-2">
                  <span>Total:</span>
                  <span>${formData.total.toFixed(2)}</span>
                </div>
              </div>
            </div>

            {/* Additional Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="payment_terms">Payment Terms</Label>
                <Select 
                  value={formData.payment_terms || ''} 
                  onValueChange={handlePaymentTermsChange}
                >
                  <SelectTrigger data-testid="payment-terms-select">
                    <SelectValue placeholder="Select payment terms" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Payment on receipt">Payment on receipt</SelectItem>
                    <SelectItem value="7 day terms">7 day terms</SelectItem>
                    <SelectItem value="14 day terms">14 day terms</SelectItem>
                    <SelectItem value="30 day terms">30 day terms</SelectItem>
                  </SelectContent>
                </Select>
                {formData.payment_terms && (
                  <p className="text-xs text-gray-500 mt-1">
                    Due date will be automatically calculated
                  </p>
                )}
              </div>
              <div>
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Additional notes or comments"
                  rows={3}
                  data-testid="invoice-notes-input"
                />
              </div>
            </div>
          </CardContent>
          <CardContent>
            <div className="flex gap-3 justify-end">
              <Button type="button" variant="outline" onClick={() => navigate('/invoices')} data-testid="cancel-invoice-btn">
                Cancel
              </Button>
              <Button type="submit" data-testid="save-invoice-btn">
                {editMode ? 'Update Invoice' : 'Create Invoice'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </form>
    </div>
  );
};

// Invoice View Component
const InvoiceView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState(null);
  const [settings, setSettings] = useState(null);
  const [isReconcileDialogOpen, setIsReconcileDialogOpen] = useState(false);
  const [reconciliationData, setReconciliationData] = useState({
    reconciliation_date: '',
    reconciliation_notes: ''
  });

  useEffect(() => {
    fetchInvoice();
    fetchSettings();
  }, [id]);

  const fetchInvoice = async () => {
    try {
      const response = await axios.get(`${API}/invoices/${id}`);
      setInvoice(response.data);
      if (response.data.bank_reconciliation_date) {
        setReconciliationData({
          reconciliation_date: response.data.bank_reconciliation_date,
          reconciliation_notes: response.data.bank_reconciliation_notes || ''
        });
      }
    } catch (error) {
      console.error('Error fetching invoice:', error);
      toast.error('Failed to load invoice');
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const downloadPDF = async () => {
    try {
      const response = await axios.get(`${API}/invoices/${id}/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice_${invoice.invoice_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Invoice downloaded successfully');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Failed to download invoice');
    }
  };

  const handleReconcile = async () => {
    try {
      await axios.post(`${API}/invoices/${id}/reconcile`, null, {
        params: {
          reconciliation_date: reconciliationData.reconciliation_date,
          reconciliation_notes: reconciliationData.reconciliation_notes
        }
      });
      toast.success('Invoice reconciled successfully');
      setIsReconcileDialogOpen(false);
      fetchInvoice(); // Refresh invoice data
    } catch (error) {
      console.error('Error reconciling invoice:', error);
      toast.error('Failed to reconcile invoice');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'bg-emerald-500';
      case 'sent': return 'bg-blue-500';
      case 'overdue': return 'bg-red-500';
      case 'draft': return 'bg-gray-500';
      default: return 'bg-gray-500';
    }
  };

  if (!invoice) return <div className="flex justify-center items-center h-96">Loading...</div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Invoice Preview</h1>
          <p className="text-gray-600">Invoice #{invoice.invoice_number}</p>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" onClick={() => navigate('/invoices')} data-testid="back-to-invoices-btn">
            Back to Invoices
          </Button>
          <Button onClick={downloadPDF} data-testid="download-pdf-btn">
            <Download className="mr-2 h-4 w-4" /> Download PDF
          </Button>
        </div>
      </div>

      <Card className="invoice-preview">
        <CardContent className="p-8">
          {/* Tax Invoice Title */}
          <div className="text-center mb-6">
            <h2 className="text-4xl font-bold text-blue-700">TAX INVOICE</h2>
          </div>
          
          {/* Header */}
          <div className="flex justify-between items-start mb-8">
            <div>
              {settings?.logo_url ? (
                <div className="mb-4">
                  <img 
                    src={settings.logo_url} 
                    alt="Company Logo" 
                    className="h-20 w-auto mb-2"
                  />
                  <p className="text-xl font-bold text-gray-900">{settings?.company_name || 'Your Company'}</p>
                </div>
              ) : (
                <h2 className="text-3xl font-bold text-blue-700 mb-4">INVOICE</h2>
              )}
              {!settings?.logo_url && <p className="text-lg font-semibold mb-2">{settings?.company_name || 'Your Company'}</p>}
              {settings?.company_address && <p className="text-sm text-gray-600">{settings.company_address}</p>}
              {settings?.company_phone && <p className="text-sm text-gray-600">{settings.company_phone}</p>}
              {settings?.company_email && <p className="text-sm text-gray-600">{settings.company_email}</p>}
              {settings?.company_abn && <p className="text-sm text-gray-600">ABN: {settings.company_abn}</p>}
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-600"><strong>Invoice #:</strong> {invoice.invoice_number}</p>
              <p className="text-sm text-gray-600"><strong>Date:</strong> {formatDate(invoice.date)}</p>
              <p className="text-sm text-gray-600"><strong>Due Date:</strong> {formatDate(invoice.due_date)}</p>
              <Badge className={`mt-2 ${getStatusColor(invoice.status)}`}>
                {invoice.status.toUpperCase()}
              </Badge>
            </div>
          </div>

          {/* Bill To */}
          <div className="mb-8">
            <h3 className="text-sm font-semibold text-gray-600 mb-2">BILL TO:</h3>
            <p className="font-semibold">{invoice.customer_name}</p>
            {invoice.customer_address && <p className="text-sm text-gray-600">{invoice.customer_address}</p>}
            {invoice.customer_email && <p className="text-sm text-gray-600">{invoice.customer_email}</p>}
            {invoice.customer_phone && <p className="text-sm text-gray-600">{invoice.customer_phone}</p>}
            {invoice.customer_abn && <p className="text-sm text-gray-600 mt-1">ABN: {invoice.customer_abn}</p>}
          </div>

          {/* Items Table */}
          <Table className="mb-6">
            <TableHeader>
              <TableRow className="bg-blue-50">
                <TableHead className="font-semibold">Description</TableHead>
                <TableHead className="text-right font-semibold">Quantity</TableHead>
                <TableHead className="text-right font-semibold">Unit Price</TableHead>
                <TableHead className="text-right font-semibold">GST</TableHead>
                <TableHead className="text-right font-semibold">Amount</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {invoice.items.map((item, index) => (
                <TableRow key={index}>
                  <TableCell>{item.description}</TableCell>
                  <TableCell className="text-right">{item.quantity}</TableCell>
                  <TableCell className="text-right">${(item.unit_price * (1 + (item.tax_rate || 0) / 100)).toFixed(2)}</TableCell>
                  <TableCell className="text-right">
                    {item.tax_rate ? `${item.tax_rate}% ($${item.tax_amount?.toFixed(2) || '0.00'})` : '-'}
                  </TableCell>
                  <TableCell className="text-right">${(item.unit_price * item.quantity).toFixed(2)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {/* Totals */}
          <div className="flex justify-end mb-8">
            <div className="w-64 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Subtotal:</span>
                <span>${invoice.subtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">GST:</span>
                <span>${invoice.tax_amount.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-lg font-bold border-t pt-2">
                <span>Total:</span>
                <span>${invoice.total.toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* Payment Terms and Notes */}
          {invoice.payment_terms && (
            <div className="mb-4">
              <h3 className="text-sm font-semibold text-gray-600 mb-1">PAYMENT TERMS:</h3>
              <p className="text-sm text-gray-700">{invoice.payment_terms}</p>
            </div>
          )}
          {invoice.notes && (
            <div className="mb-4">
              <h3 className="text-sm font-semibold text-gray-600 mb-1">NOTES:</h3>
              <p className="text-sm text-gray-700">{invoice.notes}</p>
            </div>
          )}
          
          {/* Banking Details for Payment */}
          {(settings?.account_name || settings?.bsb || settings?.account_number) && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <h3 className="text-sm font-semibold text-gray-600 mb-2">PAYMENT DETAILS:</h3>
              <div className="bg-gray-50 p-4 rounded-lg">
                {settings?.account_name && (
                  <p className="text-sm text-gray-700"><strong>Account Name:</strong> {settings.account_name}</p>
                )}
                {settings?.bsb && (
                  <p className="text-sm text-gray-700"><strong>BSB:</strong> {settings.bsb}</p>
                )}
                {settings?.account_number && (
                  <p className="text-sm text-gray-700"><strong>Account Number:</strong> {settings.account_number}</p>
                )}
              </div>
            </div>
          )}

          {/* Bank Reconciliation Section */}
          {invoice.status === 'paid' && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-sm font-semibold text-gray-600">BANK RECONCILIATION:</h3>
                <Dialog open={isReconcileDialogOpen} onOpenChange={setIsReconcileDialogOpen}>
                  <DialogTrigger asChild>
                    <Button variant="outline" size="sm">
                      {invoice.bank_reconciliation_date ? 'Update Reconciliation' : 'Reconcile Payment'}
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Bank Reconciliation</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="reconciliation_date">Bank Account Date</Label>
                        <Input
                          id="reconciliation_date"
                          type="date"
                          value={reconciliationData.reconciliation_date}
                          onChange={(e) => setReconciliationData({...reconciliationData, reconciliation_date: e.target.value})}
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="reconciliation_notes">Notes (Optional)</Label>
                        <Textarea
                          id="reconciliation_notes"
                          value={reconciliationData.reconciliation_notes}
                          onChange={(e) => setReconciliationData({...reconciliationData, reconciliation_notes: e.target.value})}
                          placeholder="Add any reconciliation notes..."
                        />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setIsReconcileDialogOpen(false)}>
                        Cancel
                      </Button>
                      <Button onClick={handleReconcile}>
                        Save Reconciliation
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
              {invoice.bank_reconciliation_date ? (
                <div className="bg-emerald-50 p-4 rounded-lg border border-emerald-200">
                  <p className="text-sm text-emerald-900">
                    <strong>Payment Received:</strong> {formatDate(invoice.bank_reconciliation_date)}
                  </p>
                  {invoice.bank_reconciliation_notes && (
                    <p className="text-sm text-emerald-800 mt-2">
                      <strong>Notes:</strong> {invoice.bank_reconciliation_notes}
                    </p>
                  )}
                </div>
              ) : (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-600">No bank reconciliation recorded yet.</p>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Clients Management Component
const ClientsManagement = () => {
  const [clients, setClients] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [showInvoicesDialog, setShowInvoicesDialog] = useState(false);
  const [selectedClientInvoices, setSelectedClientInvoices] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: ''
  });

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API}/customers`);
      setClients(response.data);
    } catch (error) {
      console.error('Error fetching clients:', error);
      toast.error('Failed to load clients');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingClient) {
        await axios.put(`${API}/customers/${editingClient.id}`, formData);
        toast.success('Client updated successfully');
      } else {
        await axios.post(`${API}/customers`, formData);
        toast.success('Client created successfully');
      }
      resetForm();
      fetchClients();
    } catch (error) {
      console.error('Error saving client:', error);
      toast.error('Failed to save client');
    }
  };

  const handleEdit = (client) => {
    setEditingClient(client);
    setFormData({
      name: client.name,
      email: client.email || '',
      phone: client.phone || '',
      address: client.address || '',
      company_name: client.company_name || '',
      abn: client.abn || ''
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this client? This action cannot be undone.')) return;
    
    try {
      await axios.delete(`${API}/customers/${id}`);
      toast.success('Client deleted successfully');
      fetchClients();
    } catch (error) {
      console.error('Error deleting client:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to delete client';
      toast.error(errorMessage);
    }
  };

  const viewClientInvoices = async (client) => {
    try {
      const response = await axios.get(`${API}/customers/${client.id}/invoices`);
      setSelectedClientInvoices(response.data);
      setShowInvoicesDialog(true);
    } catch (error) {
      console.error('Error fetching client invoices:', error);
      toast.error('Failed to load client invoices');
    }
  };

  const resetForm = () => {
    setFormData({ name: '', email: '', phone: '', address: '', company_name: '', abn: '' });
    setEditingClient(null);
    setIsDialogOpen(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'bg-emerald-500';
      case 'sent': return 'bg-blue-500';
      case 'overdue': return 'bg-red-500';
      case 'draft': return 'bg-gray-500';
      default: return 'bg-gray-500';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-black text-black mb-2 tracking-tight">Clients</h1>
          <p className="text-lg text-blue-700 font-semibold">People I consult to</p>
          <p className="text-sm text-gray-600 mt-1">Simple client management for consultations and invoicing</p>
        </div>
        <Button onClick={() => setIsDialogOpen(true)} data-testid="add-client-btn">
          <Plus className="mr-2 h-4 w-4" /> Add Client
        </Button>
      </div>

      <Card>
        <CardContent className="p-6">
          {clients.length > 0 ? (
            <div className="overflow-x-auto">
              <div className="border rounded-lg min-w-[800px]">
                {/* Fixed Header */}
                <div className="bg-gray-50 border-b px-4 py-3 text-xs font-semibold text-gray-700">
                  <div className="flex">
                    <div className="w-40 flex-shrink-0">Name</div>
                    <div className="w-40 flex-shrink-0">Company</div>
                    <div className="w-48 flex-shrink-0">Email</div>
                    <div className="w-32 flex-shrink-0">Phone</div>
                    <div className="w-28 flex-shrink-0 text-right">Actions</div>
                  </div>
                </div>
                
                {/* Scrollable Content */}
                <div className="max-h-[60vh] overflow-y-auto">
                  {clients.map((client) => (
                  <div key={client.id} className="flex border-b border-gray-100 hover:bg-gray-50 px-4 py-3 text-sm">
                    <div className="w-40 flex-shrink-0 font-medium">{client.name}</div>
                    <div className="w-40 flex-shrink-0">{client.company_name || '-'}</div>
                    <div className="w-48 flex-shrink-0">{client.email || '-'}</div>
                    <div className="w-32 flex-shrink-0">{client.phone || '-'}</div>
                    <div className="w-28 flex-shrink-0 text-right">
                      <div className="flex gap-2 justify-end">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => viewClientInvoices(client)}
                          data-testid={`view-invoices-${client.id}`}
                          className="h-8 w-8 p-0"
                        >
                          <DollarSign className="h-3 w-3 text-blue-600" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(client)}
                          className="h-8 w-8 p-0"
                          data-testid={`edit-client-${client.id}`}
                        >
                          <Edit className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(client.id)}
                          className="h-8 w-8 p-0"
                          data-testid={`delete-client-${client.id}`}
                        >
                          <Trash2 className="h-3 w-3 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            </div>
          ) : (
            <div className="text-center py-12 text-gray-500">
              <Users className="mx-auto h-16 w-16 mb-4 text-gray-400" />
              <p className="text-lg mb-2">No clients yet</p>
              <p className="text-sm">Add your first client to get started</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Client Form Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingClient ? 'Edit Client' : 'Add New Client'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name">Contact Name</Label>
              <Input
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
              />
            </div>
            <div>
              <Label htmlFor="company_name">Company Name</Label>
              <Input
                id="company_name"
                name="company_name"
                value={formData.company_name}
                onChange={handleInputChange}
                placeholder="e.g. Unikorn Group Pty Ltd"
              />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
              />
            </div>
            <div>
              <Label htmlFor="phone">Phone</Label>
              <Input
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
              />
            </div>
            <div>
              <Label htmlFor="address">Address</Label>
              <Input
                id="address"
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                placeholder="e.g. 81 The Comenarra Parkway, Turramura NSW 2074"
              />
            </div>
            <div>
              <Label htmlFor="abn">ABN</Label>
              <Input
                id="abn"
                name="abn"
                value={formData.abn}
                onChange={handleInputChange}
                placeholder="e.g. 74 697 692 07"
              />
            </div>
            <div className="flex gap-2 pt-4">
              <Button type="submit">
                {editingClient ? 'Update Client' : 'Add Client'}
              </Button>
              <Button type="button" variant="outline" onClick={resetForm}>
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Client Invoices Dialog */}
      <Dialog open={showInvoicesDialog} onOpenChange={setShowInvoicesDialog}>
        <DialogContent className="max-w-6xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Invoices for {selectedClientInvoices?.client_name}</DialogTitle>
          </DialogHeader>
          {selectedClientInvoices && (
            <div className="space-y-6">
              {/* Invoices Summary */}
              <div className="grid grid-cols-4 gap-4">
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm text-gray-600">Total Invoices</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-blue-600">
                      {selectedClientInvoices.total_invoices}
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm text-gray-600">Total Amount</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-gray-900">
                      ${selectedClientInvoices.total_amount?.toFixed(2) || '0.00'}
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm text-gray-600">Paid Amount</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-emerald-600">
                      ${selectedClientInvoices.paid_amount?.toFixed(2) || '0.00'}
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm text-gray-600">Pending Amount</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-amber-600">
                      ${selectedClientInvoices.pending_amount?.toFixed(2) || '0.00'}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Invoices Table */}
              <div>
                <h3 className="text-lg font-semibold mb-3">Client Invoices</h3>
                {selectedClientInvoices.invoices && selectedClientInvoices.invoices.length > 0 ? (
                  <div className="border rounded-lg">
                    {/* Fixed Header */}
                    <div className="bg-gray-50 border-b px-4 py-3 text-xs font-semibold text-gray-700">
                      <div className="flex">
                        <div className="w-32 flex-shrink-0">Invoice #</div>
                        <div className="w-32 flex-shrink-0">Date</div>
                        <div className="w-32 flex-shrink-0">Due Date</div>
                        <div className="w-40 flex-shrink-0">Property</div>
                        <div className="w-24 flex-shrink-0">Total</div>
                        <div className="w-24 flex-shrink-0">Status</div>
                        <div className="flex-1">Reconciled</div>
                      </div>
                    </div>
                    
                    {/* Scrollable Content */}
                    <div className="max-h-[40vh] overflow-auto">
                      {selectedClientInvoices.invoices.map((invoice) => (
                        <div key={invoice.id} className="flex border-b border-gray-100 hover:bg-gray-50 px-4 py-3 text-sm">
                          <div className="w-32 flex-shrink-0 font-medium">{invoice.invoice_number}</div>
                          <div className="w-32 flex-shrink-0">{formatDate(invoice.date)}</div>
                          <div className="w-32 flex-shrink-0">{formatDate(invoice.due_date)}</div>
                          <div className="w-40 flex-shrink-0">
                            {invoice.property_address ? (
                              <span className="text-blue-600 font-medium text-xs">
                                {invoice.property_address}
                              </span>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </div>
                          <div className="w-24 flex-shrink-0 font-medium">${invoice.total.toFixed(2)}</div>
                          <div className="w-24 flex-shrink-0">
                            <Badge className={`${getStatusColor(invoice.status)} text-xs`}>
                              {invoice.status}
                            </Badge>
                          </div>
                          <div className="flex-1">
                            {invoice.status === 'paid' && invoice.bank_reconciliation_date ? (
                              <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-xs">
                                {formatDate(invoice.bank_reconciliation_date)}
                              </Badge>
                            ) : invoice.status === 'paid' ? (
                              <Badge variant="outline" className="bg-gray-50 text-gray-500 text-xs">
                                Pending
                              </Badge>
                            ) : (
                              <span className="text-gray-400 text-xs">-</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <FileText className="mx-auto h-12 w-12 mb-3 text-gray-400" />
                    <p>No invoices found for this client</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Contacts Management Component
const ContactsManagement = () => {
  const [contacts, setContacts] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [categories, setCategories] = useState([]);
  const [isCategoryDialogOpen, setIsCategoryDialogOpen] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [openDropdown, setOpenDropdown] = useState(null);
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [isImporting, setIsImporting] = useState(false);
  const [duplicateErrors, setDuplicateErrors] = useState({});
  const [isValidating, setIsValidating] = useState(false);
  const [dropdownPositions, setDropdownPositions] = useState({});
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    company: '',
    position: '',
    website: '',
    notes: '',
    category: ''
  });

  useEffect(() => {
    fetchContacts();
    fetchCategories();
  }, []);

  // Handle dropdown toggle with position detection  
  const toggleDropdown = (contactId, event) => {
    if (openDropdown === contactId) {
      setOpenDropdown(null);
      return;
    }
    
    // Calculate if dropdown should appear above or below
    const buttonElement = event.currentTarget;
    const buttonRect = buttonElement.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const dropdownHeight = 200; // Approximate height of dropdown
    
    // Check if there's enough space below
    const spaceBelow = viewportHeight - buttonRect.bottom;
    const shouldPositionAbove = spaceBelow < dropdownHeight && buttonRect.top > dropdownHeight;
    
    setDropdownPositions(prev => ({
      ...prev,
      [contactId]: shouldPositionAbove ? 'above' : 'below'
    }));
    
    setOpenDropdown(contactId);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setOpenDropdown(null);
    };
    
    if (openDropdown) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [openDropdown]);

  const fetchContacts = async () => {
    try {
      const response = await axios.get(`${API}/contacts`);
      setContacts(response.data);
    } catch (error) {
      console.error('Error fetching contacts:', error);
      toast.error('Failed to load contacts');
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/contact-categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
      toast.error('Failed to load categories');
    }
  };

  const handleCreateCategory = async (e) => {
    e.preventDefault();
    if (!newCategoryName.trim()) return;
    
    try {
      await axios.post(`${API}/contact-categories`, { name: newCategoryName.trim() });
      toast.success('Category created successfully');
      setNewCategoryName('');
      setIsCategoryDialogOpen(false);
      fetchCategories();
    } catch (error) {
      console.error('Error creating category:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to create category';
      toast.error(errorMessage);
    }
  };

  const handleDeleteCategory = async (categoryId, categoryName) => {
    if (!window.confirm(`Are you sure you want to delete the "${categoryName}" category? This action cannot be undone.`)) return;
    
    try {
      await axios.delete(`${API}/contact-categories/${categoryId}`);
      toast.success('Category deleted successfully');
      fetchCategories();
    } catch (error) {
      console.error('Error deleting category:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to delete category';
      toast.error(errorMessage);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check for duplicate errors before submission
    if (Object.keys(duplicateErrors).length > 0) {
      toast.error('Please resolve duplicate contact issues before saving');
      return;
    }
    
    try {
      if (editingContact) {
        await axios.put(`${API}/contacts/${editingContact.id}`, formData);
        toast.success('Contact updated successfully');
      } else {
        await axios.post(`${API}/contacts`, formData);
        toast.success('Contact created successfully');
      }
      resetForm();
      fetchContacts();
    } catch (error) {
      console.error('Error saving contact:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to save contact';
      toast.error(errorMessage);
    }
  };

  const handleEdit = (contact) => {
    setEditingContact(contact);
    setFormData({
      name: contact.name || '',
      email: contact.email || '',
      phone: contact.phone || '',
      address: contact.address || '',
      company: contact.company || '',
      position: contact.position || '',
      website: contact.website || '',
      notes: contact.notes || '',
      category: contact.category || ''
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this contact? This action cannot be undone.')) return;
    
    try {
      await axios.delete(`${API}/contacts/${id}`);
      toast.success('Contact deleted successfully');
      fetchContacts();
    } catch (error) {
      console.error('Error deleting contact:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to delete contact';
      toast.error(errorMessage);
    }
  };

  const resetForm = () => {
    setFormData({ 
      name: '', 
      email: '', 
      phone: '', 
      address: '', 
      company: '', 
      position: '', 
      website: '', 
      notes: '', 
      category: '' 
    });
    setEditingContact(null);
    setIsDialogOpen(false);
    setDuplicateErrors({});
    if (validationTimeout) {
      clearTimeout(validationTimeout);
    }
  };

  const handleExport = async () => {
    try {
      const response = await axios.get(`${API}/export/contacts`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'contacts.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Contacts exported successfully!');
    } catch (error) {
      console.error('Error exporting contacts:', error);
      toast.error('Failed to export contacts');
    }
  };

  const handleImport = async () => {
    if (!importFile) {
      toast.error('Please select a CSV file to import');
      return;
    }

    setIsImporting(true);
    setImportResult(null);

    try {
      const formData = new FormData();
      formData.append('file', importFile);

      const response = await axios.post(`${API}/contacts/import`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setImportResult(response.data);
      
      if (response.data.imported_count > 0) {
        toast.success(`Successfully imported ${response.data.imported_count} contacts!`);
        fetchContacts(); // Refresh the contacts list
      }
      
      if (response.data.error_count > 0) {
        toast.warning(`${response.data.error_count} rows had errors. Check import details.`);
      }

    } catch (error) {
      console.error('Error importing contacts:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to import contacts';
      toast.error(errorMessage);
      setImportResult({ 
        message: errorMessage, 
        imported_count: 0, 
        error_count: 1, 
        errors: [errorMessage] 
      });
    } finally {
      setIsImporting(false);
    }
  };

  const resetImport = () => {
    setImportFile(null);
    setImportResult(null);
    setIsImportDialogOpen(false);
  };

  const validateContactData = async (contactData) => {
    if (!contactData.email && !contactData.phone && !contactData.name) {
      setDuplicateErrors({});
      return;
    }

    setIsValidating(true);
    try {
      const response = await axios.post(`${API}/contacts/validate`, {
        ...contactData,
        id: editingContact?.id // Pass current contact ID when editing
      });

      if (response.data.is_duplicate) {
        const errors = {};
        response.data.duplicates.forEach(duplicate => {
          errors[duplicate.field] = duplicate.message;
        });
        setDuplicateErrors(errors);
      } else {
        setDuplicateErrors({});
      }
    } catch (error) {
      console.error('Validation error:', error);
      setDuplicateErrors({});
    } finally {
      setIsValidating(false);
    }
  };

  // Debounced validation
  const [validationTimeout, setValidationTimeout] = useState(null);
  
  const debouncedValidate = (contactData) => {
    if (validationTimeout) {
      clearTimeout(validationTimeout);
    }
    
    const timeout = setTimeout(() => {
      validateContactData(contactData);
    }, 500); // Wait 500ms after user stops typing
    
    setValidationTimeout(timeout);
  };

  // Copy functions to other modules
  const copyToClient = async (contact) => {
    if (!window.confirm(`Copy "${contact.name}" to Clients module?`)) return;
    
    try {
      const clientData = {
        name: contact.name,
        email: contact.email,
        phone: contact.phone,
        address: contact.address
      };
      
      await axios.post(`${API}/customers`, clientData);
      toast.success(`${contact.name} copied to Clients successfully!`);
    } catch (error) {
      console.error('Error copying to clients:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to copy to clients';
      toast.error(errorMessage);
    }
  };

  const copyToReferralPartner = async (contact) => {
    if (!window.confirm(`Copy "${contact.name}" to Referral Partners module?`)) return;
    
    try {
      const partnerData = {
        name: contact.name,
        email: contact.email,
        phone: contact.phone,
        address: contact.address,
        commission_amount: 0 // Default value required for referral partners
      };
      
      await axios.post(`${API}/referral-partners`, partnerData);
      toast.success(`${contact.name} copied to Referral Partners successfully!`);
    } catch (error) {
      console.error('Error copying to referral partners:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to copy to referral partners';
      toast.error(errorMessage);
    }
  };

  const copyToPurchaser = async (contact) => {
    if (!window.confirm(`Copy "${contact.name}" to Customer CRM?`)) return;
    
    try {
      const customerData = {
        lead_name: contact.name,
        name: contact.name,
        full_address: contact.address,
        address: contact.address,
        email: contact.email,
        phone: contact.phone,
        mobile: contact.phone,
        company_workplace: contact.company
      };
      
      await axios.post(`${API}/customers-crm`, customerData);
      toast.success(`${contact.name} copied to Customer CRM successfully!`);
    } catch (error) {
      console.error('Error copying to customers:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to copy to Customer CRM';
      toast.error(errorMessage);
    }
  };

  // Filter contacts based on search term and category
  const filteredContacts = contacts.filter(contact => {
    const matchesSearch = contact.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         contact.company?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         contact.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !categoryFilter || contact.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-black tracking-tight text-black">Contacts</h1>
          <p className="text-sm sm:text-base text-gray-700 font-medium">Manage your contact information</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={handleExport} variant="outline" className="text-sm" size="sm">
            <Download className="h-4 w-4 mr-1 sm:mr-2" />
            <span className="hidden sm:inline">Export CSV</span>
            <span className="sm:hidden">Export</span>
          </Button>
          <Button onClick={() => setIsImportDialogOpen(true)} variant="outline" className="text-sm" size="sm">
            📁 <span className="hidden sm:inline ml-2">Import CSV</span>
            <span className="sm:hidden ml-1">Import</span>
          </Button>
          <Button onClick={() => setIsDialogOpen(true)} className="text-sm" size="sm">
            <Plus className="h-4 w-4 mr-1 sm:mr-2" />
            <span className="hidden sm:inline">Add Contact</span>
            <span className="sm:hidden">Add</span>
          </Button>
        </div>
      </div>

      {/* Search and Filter Controls */}
      <Card>
        <CardContent className="pt-4 sm:pt-6">
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-end">
            <div className="flex-1 w-full">
              <Label htmlFor="search" className="text-sm">Search Contacts</Label>
              <Input
                id="search"
                placeholder="Search by name, company, or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full"
              />
            </div>
            <div className="w-full sm:w-48">
              <Label htmlFor="categoryFilter" className="text-sm">Filter by Category</Label>
              <select
                id="categoryFilter"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
              >
                <option value="">All Categories</option>
                {categories.map(category => (
                  <option key={category.id} value={category.name}>{category.name}</option>
                ))}
              </select>
            </div>
            {(searchTerm || categoryFilter) && (
              <Button 
                variant="outline" 
                onClick={() => { setSearchTerm(''); setCategoryFilter(''); }}
                size="sm"
                className="w-full sm:w-auto"
              >
                Clear
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Add/Edit Contact Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingContact ? 'Edit Contact' : 'Add New Contact'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => {
                    const newFormData = { ...formData, name: e.target.value };
                    setFormData(newFormData);
                    debouncedValidate(newFormData);
                  }}
                  required
                  className={duplicateErrors.name_email ? 'border-red-500' : ''}
                />
                {duplicateErrors.name_email && (
                  <p className="text-red-500 text-xs mt-1">⚠️ {duplicateErrors.name_email}</p>
                )}
              </div>
              <div>
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => {
                    const newFormData = { ...formData, email: e.target.value };
                    setFormData(newFormData);
                    debouncedValidate(newFormData);
                  }}
                  required
                  className={duplicateErrors.email ? 'border-red-500' : ''}
                />
                {duplicateErrors.email && (
                  <p className="text-red-500 text-xs mt-1">⚠️ {duplicateErrors.email}</p>
                )}
              </div>
              <div>
                <Label htmlFor="phone">Phone *</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => {
                    const newFormData = { ...formData, phone: e.target.value };
                    setFormData(newFormData);
                    debouncedValidate(newFormData);
                  }}
                  required
                  className={duplicateErrors.phone ? 'border-red-500' : ''}
                />
                {duplicateErrors.phone && (
                  <p className="text-red-500 text-xs mt-1">⚠️ {duplicateErrors.phone}</p>
                )}
              </div>
              <div>
                <Label htmlFor="company">Company</Label>
                <Input
                  id="company"
                  value={formData.company}
                  onChange={(e) => setFormData(prev => ({ ...prev, company: e.target.value }))}
                />
              </div>
              <div>
                <Label htmlFor="position">Position</Label>
                <Input
                  id="position"
                  value={formData.position}
                  onChange={(e) => setFormData(prev => ({ ...prev, position: e.target.value }))}
                />
              </div>
              <div>
                <Label htmlFor="website">Website</Label>
                <Input
                  id="website"
                  value={formData.website}
                  onChange={(e) => setFormData(prev => ({ ...prev, website: e.target.value }))}
                  placeholder="https://example.com"
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label htmlFor="category">Category</Label>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setIsCategoryDialogOpen(true)}
                    className="text-xs"
                  >
                    + Manage Categories
                  </Button>
                </div>
                <select
                  id="category"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={formData.category}
                  onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                >
                  <option value="">Select Category</option>
                  {categories.map(category => (
                    <option key={category.id} value={category.name}>{category.name}</option>
                  ))}
                </select>
              </div>
            </div>
            
            <div>
              <Label htmlFor="address">Address</Label>
              <textarea
                id="address"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows="2"
                value={formData.address}
                onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
              />
            </div>
            
            <div>
              <Label htmlFor="notes">Notes</Label>
              <textarea
                id="notes"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows="3"
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Additional notes or comments..."
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={resetForm}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={Object.keys(duplicateErrors).length > 0 || isValidating}
              >
                {isValidating ? 'Validating...' : (editingContact ? 'Update Contact' : 'Create Contact')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Category Management Dialog */}
      <Dialog open={isCategoryDialogOpen} onOpenChange={setIsCategoryDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Manage Categories</DialogTitle>
          </DialogHeader>
          
          {/* Add New Category */}
          <form onSubmit={handleCreateCategory} className="space-y-4">
            <div>
              <Label htmlFor="newCategory">Add New Category</Label>
              <div className="flex gap-2">
                <Input
                  id="newCategory"
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  placeholder="Enter category name"
                  required
                />
                <Button type="submit" size="sm">Add</Button>
              </div>
            </div>
          </form>
          
          {/* Existing Categories */}
          <div className="space-y-2">
            <Label>Existing Categories</Label>
            {categories.length > 0 ? (
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {categories.map(category => (
                  <div key={category.id} className="flex items-center justify-between p-2 border rounded">
                    <span>{category.name}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteCategory(category.id, category.name)}
                      className="text-red-600 hover:text-red-700"
                    >
                      🗑️
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-sm">No categories created yet</p>
            )}
          </div>
          
          <DialogFooter>
            <Button type="button" onClick={() => setIsCategoryDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import CSV Dialog */}
      <Dialog open={isImportDialogOpen} onOpenChange={setIsImportDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Import Contacts from CSV</DialogTitle>
          </DialogHeader>
          
          {!importResult ? (
            <div className="space-y-4">
              <div>
                <Label htmlFor="csvFile">Select CSV File</Label>
                <div className="mt-2">
                  <input
                    id="csvFile"
                    type="file"
                    accept=".csv"
                    onChange={(e) => setImportFile(e.target.files[0])}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  CSV file should have columns: name*, email*, phone*, address, company, position, website, notes, category
                </p>
                <p className="text-xs text-gray-400">
                  * Required fields
                </p>
              </div>
              
              <div className="bg-blue-50 p-3 rounded-md">
                <h4 className="text-sm font-medium text-blue-800 mb-2">CSV Format Example:</h4>
                <pre className="text-xs text-blue-700 whitespace-pre-wrap">
name,email,phone,company,category
John Doe,john@example.com,+1-555-0123,Tech Corp,Business
Jane Smith,jane@company.com,+1-555-0456,Design LLC,Client
                </pre>
              </div>
              
              <DialogFooter>
                <Button type="button" variant="outline" onClick={resetImport}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleImport} 
                  disabled={!importFile || isImporting}
                >
                  {isImporting ? 'Importing...' : 'Import Contacts'}
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="text-center">
                <h3 className="text-lg font-medium text-green-600 mb-2">Import Complete!</h3>
                <div className="space-y-2">
                  <p className="text-sm">
                    <span className="font-medium text-green-600">✅ Imported:</span> {importResult.imported_count} contacts
                  </p>
                  {importResult.error_count > 0 && (
                    <p className="text-sm">
                      <span className="font-medium text-red-600">❌ Errors:</span> {importResult.error_count} rows
                    </p>
                  )}
                </div>
              </div>
              
              {importResult.errors && importResult.errors.length > 0 && (
                <div className="bg-red-50 p-3 rounded-md max-h-40 overflow-y-auto">
                  <h4 className="text-sm font-medium text-red-800 mb-2">Errors:</h4>
                  <ul className="text-xs text-red-700 space-y-1">
                    {importResult.errors.map((error, index) => (
                      <li key={index}>• {error}</li>
                    ))}
                  </ul>
                </div>
              )}
              
              <DialogFooter>
                <Button onClick={resetImport}>
                  Close
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Contacts Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg sm:text-xl">All Contacts ({filteredContacts.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {filteredContacts.length > 0 ? (
            <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
              <div className="border rounded-lg min-w-[950px]">
                {/* Fixed Header */}
                <div className="bg-gray-50 border-b px-4 py-3 text-xs font-semibold text-gray-700">
                  <div className="flex">
                    <div className="w-40 flex-shrink-0">Name</div>
                    <div className="w-32 flex-shrink-0">Company</div>
                    <div className="w-28 flex-shrink-0">Position</div>
                    <div className="w-48 flex-shrink-0">Email</div>
                    <div className="w-32 flex-shrink-0">Phone</div>
                    <div className="w-24 flex-shrink-0">Category</div>
                    <div className="w-28 flex-shrink-0 text-right">Actions</div>
                  </div>
                </div>
                
                {/* Scrollable Content */}
                <div className="max-h-[60vh] overflow-auto">
                  {filteredContacts.map((contact) => (
                    <div key={contact.id} className="flex border-b border-gray-100 hover:bg-gray-50 px-4 py-3 text-sm">
                      <div className="w-40 flex-shrink-0 font-medium">{contact.name}</div>
                      <div className="w-32 flex-shrink-0">{contact.company || '-'}</div>
                      <div className="w-28 flex-shrink-0">{contact.position || '-'}</div>
                      <div className="w-48 flex-shrink-0">{contact.email || '-'}</div>
                      <div className="w-32 flex-shrink-0">{contact.phone || '-'}</div>
                      <div className="w-24 flex-shrink-0">
                        {contact.category ? (
                          <Badge variant="outline" className="text-xs">{contact.category}</Badge>
                        ) : '-'}
                      </div>
                      <div className="w-28 flex-shrink-0 text-right relative">
                        <div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleDropdown(contact.id, e);
                            }}
                            className="px-3 text-xs"
                          >
                            ⋯ Actions
                          </Button>
                        
                        {openDropdown === contact.id && (
                          <div 
                            className={`absolute right-0 bg-white border border-gray-200 rounded-md shadow-lg z-50 min-w-48 ${
                              dropdownPositions[contact.id] === 'above' ? 'bottom-8' : 'top-8'
                            }`}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div className="py-1">
                              {/* Edit and Delete Actions - Top Priority */}
                              <button
                                onClick={() => {
                                  handleEdit(contact);
                                  setOpenDropdown(null);
                                }}
                                className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 flex items-center gap-2"
                              >
                                ✏️ Edit Contact
                              </button>
                              <button
                                onClick={() => {
                                  handleDelete(contact.id);
                                  setOpenDropdown(null);
                                }}
                                className="w-full text-left px-4 py-2 text-sm hover:bg-red-50 text-red-600 flex items-center gap-2"
                              >
                                🗑️ Delete Contact
                              </button>
                              
                              {/* Copy Actions */}
                              <button
                                onClick={() => {
                                  copyToClient(contact);
                                  setOpenDropdown(null);
                                }}
                                className="w-full text-left px-4 py-2 text-sm hover:bg-blue-50 text-blue-600 flex items-center gap-2"
                              >
                                👥 Copy to Clients
                              </button>
                              <button
                                onClick={() => {
                                  copyToReferralPartner(contact);
                                  setOpenDropdown(null);
                                }}
                                className="w-full text-left px-4 py-2 text-sm hover:bg-green-50 text-green-600 flex items-center gap-2"
                              >
                                🤝 Copy to Referral Partners
                              </button>
                              <button
                                onClick={() => {
                                  copyToPurchaser(contact);
                                  setOpenDropdown(null);
                                }}
                                className="w-full text-left px-4 py-2 text-sm hover:bg-purple-50 text-purple-600 flex items-center gap-2"
                              >
                                🏠 Copy to Customer
                              </button>
                            </div>
                          </div>
                        )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-12 text-gray-500">
              <Users className="mx-auto h-16 w-16 mb-4 text-gray-400" />
              <p className="text-lg mb-2">No contacts found</p>
              <p className="text-sm">
                {searchTerm || categoryFilter ? 
                  'Try adjusting your search or filter criteria' :
                  'Add your first contact to get started'
                }
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Purchasers Management Component
const PurchasersManagement = () => {
  const [purchasers, setPurchasers] = useState([]);
  const [filteredPurchasers, setFilteredPurchasers] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingPurchaser, setEditingPurchaser] = useState(null);
  const [showInteractionsDialog, setShowInteractionsDialog] = useState(false);
  const [selectedCustomerInteractions, setSelectedCustomerInteractions] = useState(null);
  const [interactions, setInteractions] = useState([]);
  const [isInteractionDialogOpen, setIsInteractionDialogOpen] = useState(false);
  const [editingInteraction, setEditingInteraction] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    contact: true,
    address: false,
    investment: false
  });
  const [filters, setFilters] = useState({
    buying_status: '',
    priority: '',
    lead_source: ''
  });
  const [interactionFormData, setInteractionFormData] = useState({
    interaction_date: '',
    interaction_time: '',
    interaction_type: 'Call',
    conversation_details: '',
    follow_up_reminder_date: '',
    follow_up_reminder_time: '',
    completed: false
  });
  const [formData, setFormData] = useState({
    // Legacy fields
    name: '',
    address: '',
    email: '',
    mobile: '',
    
    // Contact Information
    buying_status: '',
    lead_name: '',
    first_name: '',
    last_name: '',
    full_legal_name: '',
    email2: '',
    phone: '',
    company_workplace: '',
    priority: '',
    unsubscribed_from_campaigns: false,
    date_unsubscribed: '',
    
    // Address Information
    state: '',
    country: '',
    full_address: '',
    citizenship_status: '',
    personal_information: '',
    birthday: '',
    
    // Investment/Property Preferences
    do_they_have_partner: '',
    pph_client_owner: '',
    lead_source: '',
    referral_partner_id: '',
    referral_partner_name: '',
    purchasing_capacity: '',
    max_purchasing_capacity: '',
    settlement_timeframe: '',
    property_type_wanted: '',
    property_types_not_wanted: '',
    city_preferences: '',
    buyer_preference_notes: '',
    potential_properties: ''
  });

  useEffect(() => {
    fetchPurchasers();
  }, []);

  // Apply filters whenever purchasers or filters change
  useEffect(() => {
    let filtered = [...purchasers];
    
    if (filters.buying_status) {
      filtered = filtered.filter(p => p.buying_status === filters.buying_status);
    }
    
    if (filters.priority) {
      filtered = filtered.filter(p => p.priority === filters.priority);
    }
    
    if (filters.lead_source) {
      filtered = filtered.filter(p => 
        p.lead_source && p.lead_source.toLowerCase().includes(filters.lead_source.toLowerCase())
      );
    }
    
    setFilteredPurchasers(filtered);
  }, [purchasers, filters]);

  const fetchPurchasers = async () => {
    try {
      const response = await axios.get(`${API}/customers-crm`);
      const customersData = response.data;
      
      // Fetch interaction counts for each customer
      const customersWithInteractions = await Promise.all(
        customersData.map(async (customer) => {
          try {
            const interactionsResponse = await axios.get(`${API}/customers-crm/${customer.id}/interactions`);
            const interactions = interactionsResponse.data;
            return {
              ...customer,
              totalInteractions: interactions.length,
              incompleteInteractions: interactions.filter(i => !i.completed).length
            };
          } catch (error) {
            // If fetching interactions fails, just return customer with zero counts
            return {
              ...customer,
              totalInteractions: 0,
              incompleteInteractions: 0
            };
          }
        })
      );
      
      setPurchasers(customersWithInteractions);
    } catch (error) {
      console.error('Error fetching customers:', error);
      toast.error('Failed to load customers');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Auto-populate legacy fields from new fields for backward compatibility
      let submitData = {
        ...formData,
        name: formData.lead_name || formData.full_legal_name || formData.name,
        address: formData.full_address || formData.address,
        mobile: formData.phone || formData.mobile
      };
      
      // Convert empty strings to null for numeric fields to prevent validation errors
      const numericFields = ['purchasing_capacity', 'max_purchasing_capacity'];
      numericFields.forEach(field => {
        if (submitData[field] === '' || submitData[field] === null || submitData[field] === undefined) {
          submitData[field] = null;
        } else if (typeof submitData[field] === 'string') {
          const parsed = parseFloat(submitData[field]);
          submitData[field] = isNaN(parsed) ? null : parsed;
        }
      });
      
      // Remove fields that are empty strings (but not null or undefined)
      Object.keys(submitData).forEach(key => {
        if (submitData[key] === '') {
          submitData[key] = null;
        }
      });
      
      if (editingPurchaser) {
        await axios.put(`${API}/customers-crm/${editingPurchaser.id}`, submitData);
        toast.success('Customer updated successfully');
      } else {
        await axios.post(`${API}/customers-crm`, submitData);
        toast.success('Customer created successfully');
      }
      resetForm();
      fetchPurchasers();
    } catch (error) {
      console.error('Error saving customer:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to save customer';
      toast.error(typeof errorMessage === 'string' ? errorMessage : 'Failed to save customer');
    }
  };

  const handleEdit = (purchaser) => {
    setEditingPurchaser(purchaser);
    setFormData({
      // Legacy fields
      name: purchaser.name || '',
      address: purchaser.address || '',
      email: purchaser.email || '',
      mobile: purchaser.mobile || '',
      
      // Contact Information
      buying_status: purchaser.buying_status || '',
      lead_name: purchaser.lead_name || purchaser.name || '',
      first_name: purchaser.first_name || '',
      last_name: purchaser.last_name || '',
      full_legal_name: purchaser.full_legal_name || '',
      email2: purchaser.email2 || '',
      phone: purchaser.phone || purchaser.mobile || '',
      company_workplace: purchaser.company_workplace || '',
      priority: purchaser.priority || '',
      unsubscribed_from_campaigns: purchaser.unsubscribed_from_campaigns || false,
      date_unsubscribed: purchaser.date_unsubscribed || '',
      
      // Address Information
      state: purchaser.state || '',
      country: purchaser.country || '',
      full_address: purchaser.full_address || purchaser.address || '',
      citizenship_status: purchaser.citizenship_status || '',
      personal_information: purchaser.personal_information || '',
      birthday: purchaser.birthday || '',
      
      // Investment/Property Preferences
      do_they_have_partner: purchaser.do_they_have_partner || '',
      pph_client_owner: purchaser.pph_client_owner || '',
      lead_source: purchaser.lead_source || '',
      referral_partner_id: purchaser.referral_partner_id || '',
      referral_partner_name: purchaser.referral_partner_name || '',
      purchasing_capacity: purchaser.purchasing_capacity || '',
      max_purchasing_capacity: purchaser.max_purchasing_capacity || '',
      settlement_timeframe: purchaser.settlement_timeframe || '',
      property_type_wanted: purchaser.property_type_wanted || '',
      property_types_not_wanted: purchaser.property_types_not_wanted || '',
      city_preferences: purchaser.city_preferences || '',
      buyer_preference_notes: purchaser.buyer_preference_notes || '',
      potential_properties: purchaser.potential_properties || ''
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this customer? This action cannot be undone and will delete all their interactions.')) return;
    
    try {
      await axios.delete(`${API}/customers-crm/${id}`);
      toast.success('Customer deleted successfully');
      fetchPurchasers();
    } catch (error) {
      console.error('Error deleting customer:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to delete customer';
      toast.error(errorMessage);
    }
  };

  const resetForm = () => {
    setFormData({
      // Legacy fields
      name: '',
      address: '',
      email: '',
      mobile: '',
      
      // Contact Information
      buying_status: '',
      lead_name: '',
      first_name: '',
      last_name: '',
      full_legal_name: '',
      email2: '',
      phone: '',
      company_workplace: '',
      priority: '',
      unsubscribed_from_campaigns: false,
      date_unsubscribed: '',
      
      // Address Information
      state: '',
      country: '',
      full_address: '',
      citizenship_status: '',
      personal_information: '',
      birthday: '',
      
      // Investment/Property Preferences
      do_they_have_partner: '',
      pph_client_owner: '',
      lead_source: '',
      referral_partner_id: '',
      referral_partner_name: '',
      purchasing_capacity: '',
      max_purchasing_capacity: '',
      settlement_timeframe: '',
      property_type_wanted: '',
      property_types_not_wanted: '',
      city_preferences: '',
      buyer_preference_notes: '',
      potential_properties: ''
    });
    setEditingPurchaser(null);
    setIsDialogOpen(false);
    // Reset expanded sections to default (only contact open)
    setExpandedSections({
      contact: true,
      address: false,
      investment: false
    });
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Interaction Functions
  const viewCustomerInteractions = async (customer) => {
    try {
      const response = await axios.get(`${API}/customers-crm/${customer.id}/interactions`);
      setSelectedCustomerInteractions(customer);
      setInteractions(response.data);
      setShowInteractionsDialog(true);
    } catch (error) {
      console.error('Error fetching interactions:', error);
      toast.error('Failed to load customer interactions');
    }
  };

  const handleInteractionSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingInteraction) {
        await axios.put(`${API}/customer-interactions/${editingInteraction.id}`, interactionFormData);
        toast.success('Interaction updated successfully');
      } else {
        await axios.post(`${API}/customers-crm/${selectedCustomerInteractions.id}/interactions`, interactionFormData);
        toast.success('Interaction created successfully');
      }
      resetInteractionForm();
      viewCustomerInteractions(selectedCustomerInteractions);
    } catch (error) {
      console.error('Error saving interaction:', error);
      toast.error('Failed to save interaction');
    }
  };

  const handleEditInteraction = (interaction) => {
    setEditingInteraction(interaction);
    setInteractionFormData({
      interaction_date: interaction.interaction_date || '',
      interaction_time: interaction.interaction_time || '',
      interaction_type: interaction.interaction_type || 'Call',
      conversation_details: interaction.conversation_details || '',
      follow_up_reminder_date: interaction.follow_up_reminder_date || '',
      follow_up_reminder_time: interaction.follow_up_reminder_time || '',
      completed: interaction.completed || false
    });
    setIsInteractionDialogOpen(true);
  };

  const handleDeleteInteraction = async (id) => {
    if (!window.confirm('Are you sure you want to delete this interaction?')) return;
    
    try {
      await axios.delete(`${API}/customer-interactions/${id}`);
      toast.success('Interaction deleted successfully');
      viewCustomerInteractions(selectedCustomerInteractions);
    } catch (error) {
      console.error('Error deleting interaction:', error);
      toast.error('Failed to delete interaction');
    }
  };

  const resetInteractionForm = () => {
    // Get current date and time
    const now = new Date();
    const currentDate = now.toISOString().split('T')[0]; // YYYY-MM-DD format
    const currentTime = now.toTimeString().slice(0, 5); // HH:MM format
    
    setInteractionFormData({
      interaction_date: currentDate,
      interaction_time: currentTime,
      interaction_type: 'Call',
      conversation_details: '',
      follow_up_reminder_date: '',
      follow_up_reminder_time: '',
      completed: false
    });
    setEditingInteraction(null);
    setIsInteractionDialogOpen(false);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-black text-black mb-2 tracking-tight">Customers</h1>
          <p className="text-lg text-green-700 font-semibold">People who buy from me</p>
          <p className="text-sm text-gray-600 mt-1">Comprehensive CRM for buyer relationships and property preferences</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => resetForm()} data-testid="add-purchaser-btn">
              <Plus className="mr-2 h-4 w-4" /> Add Customer
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold">{editingPurchaser ? 'Edit Customer' : 'Add New Customer'}</DialogTitle>
              <p className="text-sm text-gray-500 mt-1">Complete customer information and preferences</p>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Compact Form Layout with Visual Sections */}
              <div className="space-y-4">
                {/* Contact Information */}
                <div className="bg-gray-50 rounded-lg border border-gray-200">
                  <div 
                    className="p-4 cursor-pointer hover:bg-gray-100 transition-colors rounded-t-lg flex items-center justify-between"
                    onClick={() => toggleSection('contact')}
                  >
                    <h3 className="text-sm font-bold text-gray-700 flex items-center gap-2">
                      <span className="w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs">1</span>
                      Contact Information
                    </h3>
                    <div className="text-gray-500">
                      {expandedSections.contact ? (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      )}
                    </div>
                  </div>
                  {expandedSections.contact && (
                    <div className="p-4 pt-0">
                      <div className="grid grid-cols-3 gap-3">
                    {/* Status, Priority, Lead Source, Birthday Row */}
                    <div>
                      <Label htmlFor="buying_status" className="text-xs">Buying Status</Label>
                      <select 
                        id="buying_status"
                        className="w-full rounded-md border border-gray-300 p-1.5 text-sm h-8"
                        value={formData.buying_status}
                        onChange={(e) => setFormData(prev => ({ ...prev, buying_status: e.target.value }))}
                      >
                        <option value="">Select</option>
                        <option value="Active">Active</option>
                        <option value="Not active">Not active</option>
                        <option value="On hold">On hold</option>
                      </select>
                    </div>
                    <div>
                      <Label htmlFor="priority" className="text-xs">Priority</Label>
                      <select 
                        id="priority"
                        className="w-full rounded-md border border-gray-300 p-1.5 text-sm h-8"
                        value={formData.priority}
                        onChange={(e) => setFormData(prev => ({ ...prev, priority: e.target.value }))}
                      >
                        <option value="">None</option>
                        <option value="High">High</option>
                        <option value="Medium">Medium</option>
                        <option value="Low">Low</option>
                      </select>
                    </div>
                    <div>
                      <Label htmlFor="lead_source" className="text-xs">Lead Source</Label>
                      <Input
                        id="lead_source"
                        className="h-8 text-sm"
                        value={formData.lead_source}
                        onChange={(e) => setFormData(prev => ({ ...prev, lead_source: e.target.value }))}
                        placeholder="e.g., Referral"
                      />
                    </div>
                    
                    {/* Name Fields Row */}
                    <div>
                      <Label htmlFor="lead_name" className="text-xs">Lead Name *</Label>
                      <Input
                        id="lead_name"
                        className="h-8 text-sm"
                        value={formData.lead_name}
                        onChange={(e) => setFormData(prev => ({ ...prev, lead_name: e.target.value, name: e.target.value }))}
                        required
                        placeholder="Primary contact name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="first_name" className="text-xs">First Name</Label>
                      <Input
                        id="first_name"
                        className="h-8 text-sm"
                        value={formData.first_name}
                        onChange={(e) => setFormData(prev => ({ ...prev, first_name: e.target.value }))}
                      />
                    </div>
                    <div>
                      <Label htmlFor="last_name" className="text-xs">Last Name</Label>
                      <Input
                        id="last_name"
                        className="h-8 text-sm"
                        value={formData.last_name}
                        onChange={(e) => setFormData(prev => ({ ...prev, last_name: e.target.value }))}
                      />
                    </div>
                    <div className="col-span-3">
                      <Label htmlFor="full_legal_name" className="text-xs">Full Legal Name</Label>
                      <Input
                        id="full_legal_name"
                        className="h-8 text-sm"
                        value={formData.full_legal_name}
                        onChange={(e) => setFormData(prev => ({ ...prev, full_legal_name: e.target.value }))}
                        placeholder="Complete legal name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email" className="text-xs">Email *</Label>
                      <Input
                        id="email"
                        type="email"
                        className="h-8 text-sm"
                        value={formData.email}
                        onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                        placeholder="primary@email.com"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email2" className="text-xs">Secondary Email</Label>
                      <Input
                        id="email2"
                        type="email"
                        className="h-8 text-sm"
                        value={formData.email2}
                        onChange={(e) => setFormData(prev => ({ ...prev, email2: e.target.value }))}
                        placeholder="secondary@email.com"
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone" className="text-xs">Phone *</Label>
                      <Input
                        id="phone"
                        className="h-8 text-sm"
                        value={formData.phone}
                        onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value, mobile: e.target.value }))}
                        placeholder="+1 234 567 8900"
                      />
                    </div>
                    <div className="col-span-2">
                      <Label htmlFor="company_workplace" className="text-xs">Company/Workplace</Label>
                      <Input
                        id="company_workplace"
                        className="h-8 text-sm"
                        value={formData.company_workplace}
                        onChange={(e) => setFormData(prev => ({ ...prev, company_workplace: e.target.value }))}
                        placeholder="Company name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="birthday" className="text-xs">Birthday</Label>
                      <Input
                        id="birthday"
                        type="date"
                        className="h-8 text-sm"
                        value={formData.birthday}
                        onChange={(e) => setFormData(prev => ({ ...prev, birthday: e.target.value }))}
                      />
                    </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Address Information */}
                <div className="bg-gray-50 rounded-lg border border-gray-200">
                  <div 
                    className="p-4 cursor-pointer hover:bg-gray-100 transition-colors rounded-t-lg flex items-center justify-between"
                    onClick={() => toggleSection('address')}
                  >
                    <h3 className="text-sm font-bold text-gray-700 flex items-center gap-2">
                      <span className="w-6 h-6 bg-green-500 text-white rounded-full flex items-center justify-center text-xs">2</span>
                      Address & Location
                    </h3>
                    <div className="text-gray-500">
                      {expandedSections.address ? (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      )}
                    </div>
                  </div>
                  {expandedSections.address && (
                    <div className="p-4 pt-0">
                      <div className="grid grid-cols-3 gap-3">
                    <div className="col-span-3">
                      <Label htmlFor="full_address" className="text-xs">Full Address</Label>
                      <Input
                        id="full_address"
                        className="h-8 text-sm"
                        value={formData.full_address}
                        onChange={(e) => setFormData(prev => ({ ...prev, full_address: e.target.value, address: e.target.value }))}
                        placeholder="Street address, City, State, ZIP"
                      />
                    </div>
                    <div>
                      <Label htmlFor="state" className="text-xs">State</Label>
                      <Input
                        id="state"
                        className="h-8 text-sm"
                        value={formData.state}
                        onChange={(e) => setFormData(prev => ({ ...prev, state: e.target.value }))}
                      />
                    </div>
                    <div>
                      <Label htmlFor="country" className="text-xs">Country</Label>
                      <Input
                        id="country"
                        className="h-8 text-sm"
                        value={formData.country}
                        onChange={(e) => setFormData(prev => ({ ...prev, country: e.target.value }))}
                      />
                    </div>
                    <div>
                      <Label htmlFor="citizenship_status" className="text-xs">Citizenship Status</Label>
                      <Input
                        id="citizenship_status"
                        className="h-8 text-sm"
                        value={formData.citizenship_status}
                        onChange={(e) => setFormData(prev => ({ ...prev, citizenship_status: e.target.value }))}
                      />
                    </div>
                    <div className="col-span-3">
                      <Label htmlFor="personal_information" className="text-xs">Personal Information / Additional Notes</Label>
                      <Textarea
                        id="personal_information"
                        className="text-sm"
                        value={formData.personal_information}
                        onChange={(e) => setFormData(prev => ({ ...prev, personal_information: e.target.value }))}
                        rows={2}
                        placeholder="Any additional personal information..."
                      />
                    </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Investment Preferences */}
                <div className="bg-gray-50 rounded-lg border border-gray-200">
                  <div 
                    className="p-4 cursor-pointer hover:bg-gray-100 transition-colors rounded-t-lg flex items-center justify-between"
                    onClick={() => toggleSection('investment')}
                  >
                    <h3 className="text-sm font-bold text-gray-700 flex items-center gap-2">
                      <span className="w-6 h-6 bg-purple-500 text-white rounded-full flex items-center justify-center text-xs">3</span>
                      Investment & Property Preferences
                    </h3>
                    <div className="text-gray-500">
                      {expandedSections.investment ? (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      )}
                    </div>
                  </div>
                  {expandedSections.investment && (
                    <div className="p-4 pt-0">
                      <div className="grid grid-cols-3 gap-3">
                    <div>
                      <Label htmlFor="do_they_have_partner" className="text-xs">Has Partner?</Label>
                      <select 
                        id="do_they_have_partner"
                        className="w-full rounded-md border border-gray-300 p-1.5 text-sm"
                        value={formData.do_they_have_partner}
                        onChange={(e) => setFormData(prev => ({ ...prev, do_they_have_partner: e.target.value }))}
                      >
                        <option value="">Select</option>
                        <option value="Yes">Yes</option>
                        <option value="No">No</option>
                      </select>
                    </div>
                    <div>
                      <Label htmlFor="purchasing_capacity" className="text-xs">Budget ($)</Label>
                      <Input
                        id="purchasing_capacity"
                        type="number"
                        className="h-8 text-sm"
                        value={formData.purchasing_capacity}
                        onChange={(e) => setFormData(prev => ({ ...prev, purchasing_capacity: e.target.value }))}
                        placeholder="500000"
                      />
                    </div>
                    <div>
                      <Label htmlFor="max_purchasing_capacity" className="text-xs">Max Budget ($)</Label>
                      <Input
                        id="max_purchasing_capacity"
                        type="number"
                        className="h-8 text-sm"
                        value={formData.max_purchasing_capacity}
                        onChange={(e) => setFormData(prev => ({ ...prev, max_purchasing_capacity: e.target.value }))}
                        placeholder="750000"
                      />
                    </div>
                    <div>
                      <Label htmlFor="settlement_timeframe" className="text-xs">Settlement Time</Label>
                      <Input
                        id="settlement_timeframe"
                        className="h-8 text-sm"
                        value={formData.settlement_timeframe}
                        onChange={(e) => setFormData(prev => ({ ...prev, settlement_timeframe: e.target.value }))}
                        placeholder="e.g., 60 days"
                      />
                    </div>
                    <div>
                      <Label htmlFor="property_type_wanted" className="text-xs">Desired Property</Label>
                      <Input
                        id="property_type_wanted"
                        className="h-8 text-sm"
                        value={formData.property_type_wanted}
                        onChange={(e) => setFormData(prev => ({ ...prev, property_type_wanted: e.target.value }))}
                        placeholder="House, Apartment"
                      />
                    </div>
                    <div>
                      <Label htmlFor="city_preferences" className="text-xs">City Preferences</Label>
                      <Input
                        id="city_preferences"
                        className="h-8 text-sm"
                        value={formData.city_preferences}
                        onChange={(e) => setFormData(prev => ({ ...prev, city_preferences: e.target.value }))}
                        placeholder="Melbourne, Sydney"
                      />
                    </div>
                    <div className="col-span-3">
                      <Label htmlFor="property_types_not_wanted" className="text-xs">Property Types NOT Wanted</Label>
                      <Input
                        id="property_types_not_wanted"
                        className="h-8 text-sm"
                        value={formData.property_types_not_wanted}
                        onChange={(e) => setFormData(prev => ({ ...prev, property_types_not_wanted: e.target.value }))}
                        placeholder="Studio, Basement"
                      />
                    </div>
                    <div className="col-span-3">
                      <Label htmlFor="buyer_preference_notes" className="text-xs">Buyer Preference Notes</Label>
                      <Textarea
                        id="buyer_preference_notes"
                        className="text-sm"
                        value={formData.buyer_preference_notes}
                        onChange={(e) => setFormData(prev => ({ ...prev, buyer_preference_notes: e.target.value }))}
                        rows={2}
                        placeholder="Additional preferences, requirements, or notes..."
                      />
                    </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={resetForm}>Cancel</Button>
                <Button type="submit" data-testid="save-purchaser-btn">
                  {editingPurchaser ? 'Update Customer' : 'Create Customer'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filter Section */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-700">Filters:</span>
            </div>
            <div className="flex-1 flex gap-3">
              <div className="w-48">
                <select
                  className="w-full rounded-md border border-gray-300 p-2 text-sm"
                  value={filters.buying_status}
                  onChange={(e) => setFilters(prev => ({ ...prev, buying_status: e.target.value }))}
                >
                  <option value="">All Buying Status</option>
                  <option value="Active">Active</option>
                  <option value="Not active">Not active</option>
                  <option value="On hold">On hold</option>
                </select>
              </div>
              <div className="w-48">
                <select
                  className="w-full rounded-md border border-gray-300 p-2 text-sm"
                  value={filters.priority}
                  onChange={(e) => setFilters(prev => ({ ...prev, priority: e.target.value }))}
                >
                  <option value="">All Priorities</option>
                  <option value="High">High</option>
                  <option value="Medium">Medium</option>
                  <option value="Low">Low</option>
                </select>
              </div>
              <div className="w-48">
                <Input
                  placeholder="Search Lead Source..."
                  className="text-sm"
                  value={filters.lead_source}
                  onChange={(e) => setFilters(prev => ({ ...prev, lead_source: e.target.value }))}
                />
              </div>
              {(filters.buying_status || filters.priority || filters.lead_source) && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setFilters({ buying_status: '', priority: '', lead_source: '' })}
                  className="text-sm"
                >
                  Clear Filters
                </Button>
              )}
            </div>
            <div className="text-sm text-gray-600">
              Showing <span className="font-semibold">{filteredPurchasers.length}</span> of <span className="font-semibold">{purchasers.length}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-6">
          {filteredPurchasers.length > 0 ? (
            <div className="overflow-x-auto">
              <div className="border rounded-lg min-w-[1120px]">
                {/* Fixed Header */}
                <div className="bg-gray-50 border-b px-4 py-3 text-xs font-semibold text-gray-700">
                  <div className="flex">
                    <div className="w-36 flex-shrink-0">Name</div>
                    <div className="w-40 flex-shrink-0">Address</div>
                    <div className="w-48 flex-shrink-0">Email</div>
                    <div className="w-32 flex-shrink-0">Phone</div>
                    <div className="w-28 flex-shrink-0">Status</div>
                    <div className="w-32 flex-shrink-0 text-center">Interactions</div>
                    <div className="w-40 flex-shrink-0 text-right">Actions</div>
                  </div>
                </div>
                
                {/* Scrollable Content */}
                <div className="max-h-[60vh] overflow-y-auto">
                  {filteredPurchasers.map((purchaser) => (
                    <div key={purchaser.id} className="flex border-b border-gray-100 hover:bg-gray-50 px-4 py-3 text-sm">
                      <div className="w-36 flex-shrink-0 font-medium truncate" title={purchaser.lead_name || purchaser.name || '-'}>{purchaser.lead_name || purchaser.name || '-'}</div>
                      <div className="w-40 flex-shrink-0 truncate" title={purchaser.full_address || purchaser.address || '-'}>{purchaser.full_address || purchaser.address || '-'}</div>
                      <div className="w-48 flex-shrink-0 truncate" title={purchaser.email || '-'}>{purchaser.email || '-'}</div>
                      <div className="w-32 flex-shrink-0 truncate" title={purchaser.phone || purchaser.mobile || '-'}>{purchaser.phone || purchaser.mobile || '-'}</div>
                      <div className="w-28 flex-shrink-0">
                        {purchaser.buying_status ? (
                          <Badge variant={purchaser.buying_status === 'Active' ? 'default' : 'secondary'}>
                            {purchaser.buying_status}
                          </Badge>
                        ) : '-'}
                      </div>
                      <div className="w-32 flex-shrink-0">
                        <div className="flex gap-1 justify-center items-center">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => viewCustomerInteractions(purchaser)}
                            className="h-8 w-8 p-0 hover:bg-purple-50"
                            title="View Interactions"
                          >
                            <FileText className="h-4 w-4 text-purple-600" />
                          </Button>
                          <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-300">
                            {purchaser.totalInteractions || 0}
                          </Badge>
                          {(purchaser.incompleteInteractions || 0) > 0 && (
                            <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-300">
                              {purchaser.incompleteInteractions} ⚠
                            </Badge>
                          )}
                        </div>
                      </div>
                      <div className="w-40 flex-shrink-0 text-right">
                        <div className="flex gap-1 justify-end">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(purchaser)}
                            data-testid={`edit-purchaser-${purchaser.id}`}
                            className="h-8 w-8 p-0"
                          >
                            <Edit className="h-3 w-3" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(purchaser.id)}
                            data-testid={`delete-purchaser-${purchaser.id}`}
                            className="h-8 w-8 p-0"
                          >
                            <Trash2 className="h-3 w-3 text-red-500" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-12 text-gray-500">
              <Home className="mx-auto h-16 w-16 mb-4 text-gray-400" />
              <p className="text-lg mb-2">No customers yet</p>
              <p className="text-sm">Add your first customer to get started</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Customer Interactions Dialog */}
      <Dialog open={showInteractionsDialog} onOpenChange={setShowInteractionsDialog}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl">
              Customer Interactions - {selectedCustomerInteractions?.lead_name || selectedCustomerInteractions?.name}
            </DialogTitle>
            <p className="text-sm text-gray-500 mt-1">
              {interactions.length} total interaction(s) • {interactions.filter(i => !i.completed).length} pending
            </p>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="flex justify-between items-center p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border-2 border-blue-200">
              <div>
                <h3 className="font-semibold text-gray-800">Track every customer touchpoint</h3>
                <p className="text-sm text-gray-600">Record calls, emails, meetings, and set follow-up reminders</p>
              </div>
              <Button 
                onClick={() => {
                  // Set current date and time when opening the dialog
                  const now = new Date();
                  const currentDate = now.toISOString().split('T')[0];
                  const currentTime = now.toTimeString().slice(0, 5);
                  setInteractionFormData({
                    interaction_date: currentDate,
                    interaction_time: currentTime,
                    interaction_type: 'Call',
                    conversation_details: '',
                    follow_up_reminder_date: '',
                    follow_up_reminder_time: '',
                    completed: false
                  });
                  setEditingInteraction(null);
                  setIsInteractionDialogOpen(true);
                }}
                size="lg"
                className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white shadow-lg"
              >
                <Plus className="mr-2 h-5 w-5" /> Add New Interaction
              </Button>
            </div>

            {interactions.length > 0 ? (
              <div className="space-y-3">
                {interactions.map((interaction) => (
                  <Card key={interaction.id} className={interaction.completed ? 'bg-gray-50' : ''}>
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex gap-4 mb-2">
                            <Badge>{interaction.interaction_type}</Badge>
                            <span className="text-sm text-gray-600">
                              {interaction.interaction_date} at {interaction.interaction_time}
                            </span>
                            {interaction.completed && (
                              <Badge variant="outline" className="bg-green-50">Completed</Badge>
                            )}
                          </div>
                          <p className="text-sm mb-2">{interaction.conversation_details || 'No details'}</p>
                          {interaction.follow_up_reminder_date && (
                            <div className="text-sm text-amber-600">
                              📅 Follow-up: {interaction.follow_up_reminder_date} at {interaction.follow_up_reminder_time || 'N/A'}
                            </div>
                          )}
                        </div>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditInteraction(interaction)}
                            className="h-8 w-8 p-0"
                          >
                            <Edit className="h-3 w-3" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteInteraction(interaction.id)}
                            className="h-8 w-8 p-0"
                          >
                            <Trash2 className="h-3 w-3 text-red-500" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <p>No interactions recorded yet</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Interaction Dialog */}
      <Dialog open={isInteractionDialogOpen} onOpenChange={setIsInteractionDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingInteraction ? 'Edit Interaction' : 'Add Interaction'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleInteractionSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="interaction_date">Date *</Label>
                <Input
                  id="interaction_date"
                  type="date"
                  value={interactionFormData.interaction_date}
                  onChange={(e) => setInteractionFormData(prev => ({ ...prev, interaction_date: e.target.value }))}
                  required
                />
              </div>
              <div>
                <Label htmlFor="interaction_time">Time *</Label>
                <Input
                  id="interaction_time"
                  type="time"
                  value={interactionFormData.interaction_time}
                  onChange={(e) => setInteractionFormData(prev => ({ ...prev, interaction_time: e.target.value }))}
                  required
                />
              </div>
            </div>
            <div>
              <Label htmlFor="interaction_type">Interaction Type *</Label>
              <select
                id="interaction_type"
                className="w-full rounded-md border border-gray-300 p-2"
                value={interactionFormData.interaction_type}
                onChange={(e) => setInteractionFormData(prev => ({ ...prev, interaction_type: e.target.value }))}
                required
              >
                <option value="Call">Call</option>
                <option value="Email">Email</option>
                <option value="Meeting">Meeting</option>
                <option value="Video Call">Video Call</option>
                <option value="SMS">SMS</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div>
              <Label htmlFor="conversation_details">Conversation Details</Label>
              <Textarea
                id="conversation_details"
                value={interactionFormData.conversation_details}
                onChange={(e) => setInteractionFormData(prev => ({ ...prev, conversation_details: e.target.value }))}
                rows={4}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="follow_up_reminder_date">Follow-up Reminder Date</Label>
                <Input
                  id="follow_up_reminder_date"
                  type="date"
                  value={interactionFormData.follow_up_reminder_date}
                  onChange={(e) => setInteractionFormData(prev => ({ ...prev, follow_up_reminder_date: e.target.value }))}
                />
              </div>
              <div>
                <Label htmlFor="follow_up_reminder_time">Follow-up Reminder Time</Label>
                <Input
                  id="follow_up_reminder_time"
                  type="time"
                  value={interactionFormData.follow_up_reminder_time}
                  onChange={(e) => setInteractionFormData(prev => ({ ...prev, follow_up_reminder_time: e.target.value }))}
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="completed"
                checked={interactionFormData.completed}
                onChange={(e) => setInteractionFormData(prev => ({ ...prev, completed: e.target.checked }))}
              />
              <Label htmlFor="completed">Mark as Completed</Label>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={resetInteractionForm}>Cancel</Button>
              <Button type="submit">
                {editingInteraction ? 'Update Interaction' : 'Create Interaction'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Settings Component

// Referral Partners Management Component
const ReferralPartnersManagement = () => {
  const [partners, setPartners] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingPartner, setEditingPartner] = useState(null);
  const [showRebatesDialog, setShowRebatesDialog] = useState(false);
  const [selectedPartnerRebates, setSelectedPartnerRebates] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    commission_amount: 0
  });

  useEffect(() => {
    fetchPartners();
  }, []);

  const fetchPartners = async () => {
    try {
      const response = await axios.get(`${API}/referral-partners`);
      setPartners(response.data);
    } catch (error) {
      console.error('Error fetching referral partners:', error);
      toast.error('Failed to load referral partners');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingPartner) {
        await axios.put(`${API}/referral-partners/${editingPartner.id}`, formData);
        toast.success('Referral partner updated successfully');
      } else {
        await axios.post(`${API}/referral-partners`, formData);
        toast.success('Referral partner created successfully');
      }
      resetForm();
      fetchPartners();
    } catch (error) {
      console.error('Error saving referral partner:', error);
      toast.error('Failed to save referral partner');
    }
  };

  const handleEdit = (partner) => {
    setEditingPartner(partner);
    setFormData({
      name: partner.name,
      email: partner.email || '',
      phone: partner.phone || '',
      address: partner.address || '',
      commission_amount: partner.commission_amount || 0
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this referral partner? This action cannot be undone.')) return;
    
    try {
      await axios.delete(`${API}/referral-partners/${id}`);
      toast.success('Referral partner deleted successfully');
      fetchPartners();
    } catch (error) {
      console.error('Error deleting referral partner:', error);
      toast.error('Failed to delete referral partner');
    }
  };

  const viewRebates = async (partner) => {
    try {
      const response = await axios.get(`${API}/referral-partners/${partner.id}/rebates`);
      setSelectedPartnerRebates(response.data);
      setShowRebatesDialog(true);
    } catch (error) {
      console.error('Error fetching rebates:', error);
      toast.error('Failed to load rebates');
    }
  };

  const resetForm = () => {
    setFormData({ name: '', email: '', phone: '', address: '', commission_amount: 0 });
    setEditingPartner(null);
    setIsDialogOpen(false);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'bg-emerald-500';
      case 'sent': return 'bg-blue-500';
      case 'overdue': return 'bg-red-500';
      case 'draft': return 'bg-gray-500';
      default: return 'bg-gray-500';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-black text-black mb-2 tracking-tight">Referral Partners</h1>
          <p className="text-gray-700 font-medium">Manage your referral partner information and commission amounts</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => resetForm()} data-testid="add-referral-partner-btn">
              <Plus className="mr-2 h-4 w-4" /> Add Referral Partner
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingPartner ? 'Edit Referral Partner' : 'Add Referral Partner'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  required
                  data-testid="partner-name-input"
                />
              </div>
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  data-testid="partner-email-input"
                />
              </div>
              <div>
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                  data-testid="partner-phone-input"
                />
              </div>
              <div>
                <Label htmlFor="address">Address</Label>
                <Textarea
                  id="address"
                  value={formData.address}
                  onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                  rows={3}
                  data-testid="partner-address-input"
                />
              </div>
              <div>
                <Label htmlFor="commission_amount">Referral Partners Comm Amount ($) *</Label>
                <Input
                  id="commission_amount"
                  type="number"
                  value={formData.commission_amount}
                  onChange={(e) => setFormData(prev => ({ ...prev, commission_amount: parseFloat(e.target.value) || 0 }))}
                  required
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  data-testid="commission-amount-input"
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={resetForm}>Cancel</Button>
                <Button type="submit" data-testid="save-partner-btn">
                  {editingPartner ? 'Update' : 'Create'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardContent className="p-6">
          {partners.length > 0 ? (
            <div className="overflow-x-auto">
              <div className="border rounded-lg min-w-[820px]">
                {/* Fixed Header */}
                <div className="bg-gray-50 border-b px-4 py-3 text-xs font-semibold text-gray-700">
                  <div className="flex">
                    <div className="w-40 flex-shrink-0">Name</div>
                    <div className="w-48 flex-shrink-0">Email</div>
                    <div className="w-32 flex-shrink-0">Phone</div>
                    <div className="w-32 flex-shrink-0">Comm Amount</div>
                    <div className="w-28 flex-shrink-0 text-right">Actions</div>
                  </div>
                </div>
                
                {/* Scrollable Content */}
                <div className="max-h-[60vh] overflow-y-auto">
                  {partners.map((partner) => (
                    <div key={partner.id} className="flex border-b border-gray-100 hover:bg-gray-50 px-4 py-3 text-sm">
                      <div className="w-40 flex-shrink-0 font-medium">{partner.name}</div>
                      <div className="w-48 flex-shrink-0">{partner.email || '-'}</div>
                      <div className="w-32 flex-shrink-0">{partner.phone || '-'}</div>
                      <div className="w-32 flex-shrink-0 font-medium">${partner.commission_amount?.toFixed(2) || '0.00'}</div>
                      <div className="w-28 flex-shrink-0 text-right">
                        <div className="flex gap-2 justify-end">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => viewRebates(partner)}
                            data-testid={`view-rebates-${partner.id}`}
                            className="h-8 w-8 p-0"
                          >
                            <DollarSign className="h-3 w-3 text-purple-600" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(partner)}
                            data-testid={`edit-partner-${partner.id}`}
                            className="h-8 w-8 p-0"
                          >
                            <Edit className="h-3 w-3" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(partner.id)}
                            data-testid={`delete-partner-${partner.id}`}
                            className="h-8 w-8 p-0"
                          >
                            <Trash2 className="h-3 w-3 text-red-500" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-12 text-gray-500">
              <Handshake className="mx-auto h-16 w-16 mb-4 text-gray-400" />
              <p className="text-lg mb-2">No referral partners yet</p>
              <p className="text-sm">Add your first referral partner to get started</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Rebates Dialog */}
      <Dialog open={showRebatesDialog} onOpenChange={setShowRebatesDialog}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Rebates for {selectedPartnerRebates?.partner_name}</DialogTitle>
          </DialogHeader>
          {selectedPartnerRebates && (
            <div className="space-y-6">
              {/* Rebates Summary */}
              <div className="grid grid-cols-3 gap-4">
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm text-gray-600">Total Rebates</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-purple-600">
                      ${selectedPartnerRebates.total_rebates?.toFixed(2) || '0.00'}
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm text-gray-600">Paid Rebates</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-emerald-600">
                      ${selectedPartnerRebates.paid_rebates?.toFixed(2) || '0.00'}
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm text-gray-600">Pending Rebates</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-amber-600">
                      ${selectedPartnerRebates.pending_rebates?.toFixed(2) || '0.00'}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Invoices Table */}
              <div>
                <h3 className="text-lg font-semibold mb-3">Associated Invoices</h3>
                {selectedPartnerRebates.invoices && selectedPartnerRebates.invoices.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Invoice #</TableHead>
                        <TableHead>Client</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Total</TableHead>
                        <TableHead>Rebate</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {selectedPartnerRebates.invoices.map((invoice) => (
                        <TableRow key={invoice.id}>
                          <TableCell className="font-medium">{invoice.invoice_number}</TableCell>
                          <TableCell>{invoice.customer_name}</TableCell>
                          <TableCell>{formatDate(invoice.date)}</TableCell>
                          <TableCell>${invoice.total.toFixed(2)}</TableCell>
                          <TableCell className="font-semibold text-purple-600">
                            ${invoice.rebate_amount?.toFixed(2) || '0.00'}
                          </TableCell>
                          <TableCell>
                            <Badge className={getStatusColor(invoice.status)}>
                              {invoice.status}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <p className="text-center text-gray-500 py-8">No invoices yet</p>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};


// User Management Table Component
const UserManagementTable = () => {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isResetDialogOpen, setIsResetDialogOpen] = useState(false);
  const [isAddUserDialogOpen, setIsAddUserDialogOpen] = useState(false);
  const [isEditRoleDialogOpen, setIsEditRoleDialogOpen] = useState(false);
  const [editingRole, setEditingRole] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newUserData, setNewUserData] = useState({
    username: '',
    email: '',
    password: '',
    role: 'admin'
  });
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/auth/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
    }
  };

  const handleAddUser = async () => {
    if (!newUserData.username || !newUserData.email || !newUserData.password) {
      toast.error('All fields are required');
      return;
    }

    if (newUserData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    try {
      const response = await axios.post(`${API}/auth/register`, newUserData);
      
      if (response.data.success) {
        toast.success('User created successfully');
        setIsAddUserDialogOpen(false);
        setNewUserData({ username: '', email: '', password: '', role: 'admin' });
        fetchUsers();
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error('Error adding user:', error);
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleDeleteUser = async (userId, username) => {
    if (userId === currentUser.id) {
      toast.error('You cannot delete your own account');
      return;
    }

    if (window.confirm(`Are you sure you want to delete user "${username}"? This action cannot be undone.`)) {
      try {
        await axios.delete(`${API}/auth/users/${userId}`);
        toast.success('User deleted successfully');
        fetchUsers();
      } catch (error) {
        console.error('Error deleting user:', error);
        toast.error('Failed to delete user');
      }
    }
  };

  const handleEditRole = (user) => {
    setSelectedUser(user);
    setEditingRole(user.role || 'admin');
    setIsEditRoleDialogOpen(true);
  };

  const handleUpdateRole = async () => {
    try {
      await axios.put(`${API}/auth/users/${selectedUser.id}/role`, {
        role: editingRole
      });
      toast.success(`Role updated successfully for ${selectedUser.username}`);
      setIsEditRoleDialogOpen(false);
      fetchUsers();
    } catch (error) {
      console.error('Error updating role:', error);
      toast.error('Failed to update user role');
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    try {
      await axios.post(`${API}/auth/users/${selectedUser.id}/reset-password`, null, {
        params: { new_password: newPassword }
      });
      toast.success(`Password reset successfully for ${selectedUser.username}`);
      setIsResetDialogOpen(false);
      setNewPassword('');
      setSelectedUser(null);
    } catch (error) {
      console.error('Error resetting password:', error);
      toast.error('Failed to reset password');
    }
  };

  const formatDateDisplay = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return formatDate(dateString);
    } catch {
      return 'N/A';
    }
  };

  return (
    <>
      <div className="flex justify-between items-center mb-4">
        <div>
          <p className="text-sm text-gray-600">
            {users.length} users registered
          </p>
        </div>
        <Button
          onClick={() => setIsAddUserDialogOpen(true)}
          className="bg-green-600 hover:bg-green-700"
        >
          <Plus className="mr-2 h-4 w-4" />
          Add User
        </Button>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Username</TableHead>
            <TableHead>Email</TableHead>
            <TableHead>Role / Access Level</TableHead>
            <TableHead>Registered Date</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {users.map((user) => (
            <TableRow key={user.id}>
              <TableCell className="font-medium">
                {user.username}
                {user.id === currentUser.id && (
                  <Badge variant="outline" className="ml-2 bg-blue-50 text-blue-700">You</Badge>
                )}
              </TableCell>
              <TableCell>{user.email}</TableCell>
              <TableCell>
                {user.role === 'admin' ? (
                  <Badge className="bg-green-600">
                    Admin - Full Access
                  </Badge>
                ) : user.role === 'cashflow_only' ? (
                  <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-300">
                    Cashflow Only
                  </Badge>
                ) : (
                  <Badge className="bg-gray-500">
                    Admin (Legacy)
                  </Badge>
                )}
              </TableCell>
              <TableCell>{formatDateDisplay(user.created_at)}</TableCell>
              <TableCell className="text-right">
                <div className="flex gap-2 justify-end">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setSelectedUser(user);
                      setIsResetDialogOpen(true);
                    }}
                  >
                    Reset Password
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleEditRole(user)}
                  >
                    <Edit className="h-4 w-4" />
                    Edit Role
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteUser(user.id, user.username)}
                    disabled={user.id === currentUser.id}
                    className="text-red-500 hover:text-red-700"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {users.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          No users found
        </div>
      )}

      {/* Add User Dialog */}
      <Dialog open={isAddUserDialogOpen} onOpenChange={setIsAddUserDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New User</DialogTitle>
            <CardDescription>Create a new user account</CardDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="new_username">Username</Label>
              <Input
                id="new_username"
                type="text"
                value={newUserData.username}
                onChange={(e) => setNewUserData({...newUserData, username: e.target.value})}
                placeholder="Enter username"
              />
            </div>
            <div>
              <Label htmlFor="new_email">Email</Label>
              <Input
                id="new_email"
                type="email"
                value={newUserData.email}
                onChange={(e) => setNewUserData({...newUserData, email: e.target.value})}
                placeholder="Enter email address"
              />
            </div>
            <div>
              <Label htmlFor="new_user_password">Password</Label>
              <Input
                id="new_user_password"
                type="password"
                value={newUserData.password}
                onChange={(e) => setNewUserData({...newUserData, password: e.target.value})}
                placeholder="Enter password (min 6 characters)"
              />
            </div>
            <div>
              <Label htmlFor="user_role">Access Level</Label>
              <select
                id="user_role"
                className="w-full rounded-md border border-gray-300 p-2"
                value={newUserData.role}
                onChange={(e) => setNewUserData({...newUserData, role: e.target.value})}
              >
                <option value="admin">Full Access (Admin) - Access to all modules</option>
                <option value="cashflow_only">Cashflow Calculator Only - Limited access</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                {newUserData.role === 'cashflow_only' 
                  ? '⚠️ User will ONLY see Cashflow Calculator (no other modules)' 
                  : '✅ User will have access to all features and modules'}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setIsAddUserDialogOpen(false);
              setNewUserData({ username: '', email: '', password: '', role: 'admin' });
            }}>
              Cancel
            </Button>
            <Button onClick={handleAddUser} className="bg-green-600 hover:bg-green-700">
              Create User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={isResetDialogOpen} onOpenChange={setIsResetDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset Password for {selectedUser?.username}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="new_user_password_reset">New Password</Label>
              <Input
                id="new_user_password_reset"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password (min 6 characters)"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setIsResetDialogOpen(false);
              setNewPassword('');
              setSelectedUser(null);
            }}>
              Cancel
            </Button>
            <Button onClick={handleResetPassword}>
              Reset Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Role Dialog */}
      <Dialog open={isEditRoleDialogOpen} onOpenChange={setIsEditRoleDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Role for {selectedUser?.username}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="user_role">Role</Label>
              <select
                id="edit_role"
                className="w-full rounded-md border border-gray-300 p-2"
                value={editingRole}
                onChange={(e) => setEditingRole(e.target.value)}
              >
                <option value="admin">Full Access (Admin) - Access to all modules</option>
                <option value="cashflow_only">Cashflow Calculator Only - Limited access</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                {editingRole === 'cashflow_only' 
                  ? '⚠️ User will ONLY see Cashflow Calculator (no other modules)' 
                  : '✅ User will have access to all features and modules'}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setIsEditRoleDialogOpen(false);
              setSelectedUser(null);
            }}>
              Cancel
            </Button>
            <Button onClick={handleUpdateRole}>
              Update Role
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

const SettingsPage = () => {
  const [formData, setFormData] = useState({
    company_name: '',
    company_email: '',
    company_phone: '',
    company_address: '',
    company_abn: '',
    account_name: '',
    bsb: '',
    account_number: '',
    logo_url: ''
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setFormData(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Failed to load settings');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/settings`, formData);
      toast.success('Settings updated successfully');
    } catch (error) {
      console.error('Error updating settings:', error);
      toast.error('Failed to update settings');
    }
  };

  const handleExport = async (exportType) => {
    try {
      const response = await fetch(`${API}/export/${exportType}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Export failed');
      }

      // Get the filename from the response headers or set a default
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `${exportType}_export.csv`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      // Handle ZIP file for 'all' export
      if (exportType === 'all') {
        filename = 'database_export.zip';
      }

      // Create blob and download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success(`${exportType === 'all' ? 'All data' : exportType} exported successfully!`);
    } catch (error) {
      console.error('Error exporting data:', error);
      toast.error('Failed to export data');
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-4xl font-black text-black mb-2 tracking-tight">Settings</h1>
        <p className="text-gray-700 font-medium">Configure your company information</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Company Information</CardTitle>
          <CardDescription>This information will appear on your invoices</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="company_name">Company Name *</Label>
              <Input
                id="company_name"
                value={formData.company_name}
                onChange={(e) => setFormData(prev => ({ ...prev, company_name: e.target.value }))}
                required
                data-testid="company-name-input"
              />
            </div>
            <div>
              <Label htmlFor="company_email">Company Email</Label>
              <Input
                id="company_email"
                type="email"
                value={formData.company_email}
                onChange={(e) => setFormData(prev => ({ ...prev, company_email: e.target.value }))}
                data-testid="company-email-input"
              />
            </div>
            <div>
              <Label htmlFor="company_phone">Company Phone</Label>
              <Input
                id="company_phone"
                value={formData.company_phone}
                onChange={(e) => setFormData(prev => ({ ...prev, company_phone: e.target.value }))}
                data-testid="company-phone-input"
              />
            </div>
            <div>
              <Label htmlFor="company_address">Company Address</Label>
              <Textarea
                id="company_address"
                value={formData.company_address}
                onChange={(e) => setFormData(prev => ({ ...prev, company_address: e.target.value }))}
                rows={3}
                data-testid="company-address-input"
              />
            </div>
            <div>
              <Label htmlFor="logo_url">Company Logo URL</Label>
              <Input
                id="logo_url"
                type="url"
                value={formData.logo_url}
                onChange={(e) => setFormData(prev => ({ ...prev, logo_url: e.target.value }))}
                placeholder="https://example.com/logo.png"
                data-testid="logo-url-input"
              />
              <p className="text-sm text-gray-500 mt-1">
                Enter a URL to an image (PNG, JPG, etc.) that will appear on your invoices
              </p>
            </div>
            <div>
              <Label htmlFor="company_abn">ABN (Australian Business Number)</Label>
              <Input
                id="company_abn"
                value={formData.company_abn}
                onChange={(e) => setFormData(prev => ({ ...prev, company_abn: e.target.value }))}
                placeholder="XX XXX XXX XXX"
                data-testid="company-abn-input"
              />
            </div>
            
            {/* Banking Details Section */}
            <div className="border-t pt-6 mt-6">
              <h3 className="text-lg font-semibold mb-4">Banking Details for Payment</h3>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="account_name">Account Name</Label>
                  <Input
                    id="account_name"
                    value={formData.account_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, account_name: e.target.value }))}
                    placeholder="Account holder name"
                    data-testid="account-name-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="bsb">BSB</Label>
                    <Input
                      id="bsb"
                      value={formData.bsb}
                      onChange={(e) => setFormData(prev => ({ ...prev, bsb: e.target.value }))}
                      placeholder="XXX-XXX"
                      data-testid="bsb-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="account_number">Account Number</Label>
                    <Input
                      id="account_number"
                      value={formData.account_number}
                      onChange={(e) => setFormData(prev => ({ ...prev, account_number: e.target.value }))}
                      placeholder="XXXXXXXXX"
                      data-testid="account-number-input"
                    />
                  </div>
                </div>
              </div>
            </div>
            
            <Button type="submit" data-testid="save-settings-btn">
              Save Settings
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Data Export Section */}
      <Card>
        <CardContent className="p-6">
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-black text-black mb-4 tracking-tight">Data Export</h2>
              <p className="text-gray-700 font-medium mb-6">Export your database collections in CSV format for backup, analysis, or migration purposes.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <Button 
                variant="outline" 
                onClick={() => handleExport('clients')}
                className="h-auto p-4 flex flex-col items-center space-y-2"
                data-testid="export-clients-btn"
              >
                <Users className="h-6 w-6" />
                <span className="font-medium">Export Clients</span>
                <span className="text-xs text-gray-500">Download clients.csv</span>
              </Button>

              <Button 
                variant="outline" 
                onClick={() => handleExport('contacts')}
                className="h-auto p-4 flex flex-col items-center space-y-2"
                data-testid="export-contacts-btn"
              >
                <Users className="h-6 w-6" />
                <span className="font-medium">Export Contacts</span>
                <span className="text-xs text-gray-500">Download contacts.csv</span>
              </Button>

              <Button 
                variant="outline" 
                onClick={() => handleExport('referral-partners')}
                className="h-auto p-4 flex flex-col items-center space-y-2"
                data-testid="export-referral-partners-btn"
              >
                <Handshake className="h-6 w-6" />
                <span className="font-medium">Export Referral Partners</span>
                <span className="text-xs text-gray-500">Download referral_partners.csv</span>
              </Button>

              <Button 
                variant="outline" 
                onClick={() => handleExport('customers')}
                className="h-auto p-4 flex flex-col items-center space-y-2"
                data-testid="export-purchasers-btn"
              >
                <Home className="h-6 w-6" />
                <span className="font-medium">Export Customers</span>
                <span className="text-xs text-gray-500">Download customers.csv</span>
              </Button>

              <Button 
                variant="outline" 
                onClick={() => handleExport('properties')}
                className="h-auto p-4 flex flex-col items-center space-y-2"
                data-testid="export-properties-btn"
              >
                <MapPin className="h-6 w-6" />
                <span className="font-medium">Export Properties</span>
                <span className="text-xs text-gray-500">Download properties.csv</span>
              </Button>

              <Button 
                variant="outline" 
                onClick={() => handleExport('invoices')}
                className="h-auto p-4 flex flex-col items-center space-y-2"
                data-testid="export-invoices-btn"
              >
                <FileText className="h-6 w-6" />
                <span className="font-medium">Export Invoices</span>
                <span className="text-xs text-gray-500">Download invoices.csv</span>
              </Button>
            </div>

            <div className="border-t pt-6">
              <Button 
                onClick={() => handleExport('all')}
                className="w-full md:w-auto bg-blue-600 hover:bg-blue-700 text-white px-8 py-3"
                data-testid="export-all-btn"
              >
                <Download className="mr-2 h-5 w-5" />
                Export All Data (ZIP)
              </Button>
              <p className="text-sm text-gray-500 mt-2">
                Downloads a ZIP file containing all database collections as separate CSV files.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Account Security & User Management Section */}
      <Card>
        <CardHeader>
          <CardTitle>Account Security & User Management</CardTitle>
          <CardDescription>Manage your password and system users</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* User Management Table */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Registered Users</h3>
            <UserManagementTable />
          </div>

          {/* Divider */}
          <div className="border-t pt-6">
            <h3 className="text-lg font-semibold mb-4">Change Your Password</h3>
            <form onSubmit={async (e) => {
              e.preventDefault();
              const user = JSON.parse(localStorage.getItem('user'));
              const oldPassword = e.target.old_password.value;
              const newPassword = e.target.new_password.value;
              const confirmPassword = e.target.confirm_password.value;

              if (newPassword !== confirmPassword) {
                toast.error('New passwords do not match');
                return;
              }

              if (newPassword.length < 6) {
                toast.error('Password must be at least 6 characters');
                return;
              }

              try {
                await axios.post(`${API}/auth/change-password`, {
                  user_id: user.id,
                  old_password: oldPassword,
                  new_password: newPassword
                });
                toast.success('Password changed successfully');
                e.target.reset();
              } catch (error) {
                console.error('Error changing password:', error);
                toast.error(error.response?.data?.detail || 'Failed to change password');
              }
            }} className="space-y-4">
              <div>
                <Label htmlFor="old_password">Current Password</Label>
                <Input
                  id="old_password"
                  name="old_password"
                  type="password"
                  required
                />
              </div>
              <div>
                <Label htmlFor="new_password">New Password</Label>
                <Input
                  id="new_password"
                  name="new_password"
                  type="password"
                  required
                />
              </div>
              <div>
                <Label htmlFor="confirm_password">Confirm New Password</Label>
                <Input
                  id="confirm_password"
                  name="confirm_password"
                  type="password"
                  required
                />
              </div>
              <Button type="submit">
                Change Password
              </Button>
            </form>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Properties Management Component
const PropertiesManagement = () => {
  const [properties, setProperties] = useState([]);
  const [clients, setCustomers] = useState([]);
  const [referralPartners, setReferralPartners] = useState([]);
  const [purchasers, setPurchasers] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingProperty, setEditingProperty] = useState(null);
  const [formData, setFormData] = useState({
    address: '',
    purchaser_id: '',
    purchaser_name: '',
    eoi_date: '',  // Expression of Interest Submission date
    contract_type: '1 part',
    estimated_settlement_date: '',      // For 1 part contracts
    part1_settlement_date: '',          // For 2 part contracts - Part 1
    part2_settlement_date: '',          // For 2 part contracts - Part 2
    customer_id: '',
    customer_name: '',
    referral_partner_id: '',
    referral_partner_name: '',
    contract_sale_value: '',            // For 1 part contracts - sale value
    invoice_amount: '',                 // For 1 part contracts
    part1_contract_amount: '',          // For 2 part contracts - Part 1 sales value
    part2_contract_amount: '',          // For 2 part contracts - Part 2 sales value
    part1_customer_invoice_amount: '',  // For 2 part contracts - Part 1 client invoice
    part2_customer_invoice_amount: '',  // For 2 part contracts - Part 2 client invoice
    referral_comm: '',
    status: 'draft',                    // Status synced with related invoice
    related_invoice_id: ''              // Track which invoice is related to this property
  });

  useEffect(() => {
    fetchProperties();
    fetchCustomers();
    fetchReferralPartners();
    fetchPurchasers();
  }, []);

  const fetchProperties = async () => {
    try {
      const response = await axios.get(`${API}/properties`);
      setProperties(response.data);
    } catch (error) {
      console.error('Error fetching properties:', error);
      toast.error('Failed to load properties');
    }
  };

  const fetchCustomers = async () => {
    try {
      // Fetch from customers-crm database (buyers) not simple_clients (consultations)
      const response = await axios.get(`${API}/customers-crm`);
      setCustomers(response.data);
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const fetchReferralPartners = async () => {
    try {
      const response = await axios.get(`${API}/referral-partners`);
      setReferralPartners(response.data);
    } catch (error) {
      console.error('Error fetching referral partners:', error);
    }
  };

  const fetchPurchasers = async () => {
    try {
      const response = await axios.get(`${API}/customers-crm`);
      setPurchasers(response.data);
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const validateForm = () => {
    const errors = [];
    
    if (!formData.address.trim()) {
      errors.push('Address is required');
    }
    
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time for comparison
    
    if (formData.contract_type === '1 part') {
      if (formData.estimated_settlement_date) {
        const settlementDate = new Date(formData.estimated_settlement_date);
        if (settlementDate < today) {
          errors.push('Settlement date cannot be in the past');
        }
      }
    } else if (formData.contract_type === '2 part') {
      if (formData.part1_settlement_date) {
        const part1Date = new Date(formData.part1_settlement_date);
        if (part1Date < today) {
          errors.push('Part 1 settlement date cannot be in the past');
        }
      }
      
      if (formData.part2_settlement_date) {
        const part2Date = new Date(formData.part2_settlement_date);
        if (part2Date < today) {
          errors.push('Part 2 settlement date cannot be in the past');
        }
        
        // Part 2 should be after Part 1
        if (formData.part1_settlement_date) {
          const part1Date = new Date(formData.part1_settlement_date);
          if (part2Date <= part1Date) {
            errors.push('Part 2 settlement date must be after Part 1 settlement date');
          }
        }
      }
    }
    
    // Validate contract amounts based on contract type
    if (formData.contract_type === '1 part') {
      if (formData.contract_sale_value && (isNaN(parseFloat(formData.contract_sale_value)) || parseFloat(formData.contract_sale_value) < 0)) {
        errors.push('Contract sale value must be a positive number');
      }
      
      if (formData.invoice_amount && (isNaN(parseFloat(formData.invoice_amount)) || parseFloat(formData.invoice_amount) < 0)) {
        errors.push('Invoice amount must be a positive number');
      }
    } else if (formData.contract_type === '2 part') {
      if (formData.part1_contract_amount && (isNaN(parseFloat(formData.part1_contract_amount)) || parseFloat(formData.part1_contract_amount) < 0)) {
        errors.push('Part 1 contract sales amount must be a positive number');
      }
      
      if (formData.part2_contract_amount && (isNaN(parseFloat(formData.part2_contract_amount)) || parseFloat(formData.part2_contract_amount) < 0)) {
        errors.push('Part 2 contract sales amount must be a positive number');
      }
      
      if (formData.part1_customer_invoice_amount && (isNaN(parseFloat(formData.part1_customer_invoice_amount)) || parseFloat(formData.part1_customer_invoice_amount) < 0)) {
        errors.push('Part 1 client invoice amount must be a positive number');
      }
      
      if (formData.part2_customer_invoice_amount && (isNaN(parseFloat(formData.part2_customer_invoice_amount)) || parseFloat(formData.part2_customer_invoice_amount) < 0)) {
        errors.push('Part 2 client invoice amount must be a positive number');
      }
    }
    
    if (formData.referral_comm && (isNaN(parseFloat(formData.referral_comm)) || parseFloat(formData.referral_comm) < 0)) {
      errors.push('Referral commission must be a positive number');
    }
    
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validationErrors = validateForm();
    if (validationErrors.length > 0) {
      validationErrors.forEach(error => toast.error(error));
      return;
    }

    try {
      // Prepare data for submission
      const submitData = {
        ...formData,
        contract_sale_value: formData.contract_sale_value ? parseFloat(formData.contract_sale_value) : 0,
        invoice_amount: formData.invoice_amount ? parseFloat(formData.invoice_amount) : 0,
        part1_contract_amount: formData.part1_contract_amount ? parseFloat(formData.part1_contract_amount) : 0,
        part2_contract_amount: formData.part2_contract_amount ? parseFloat(formData.part2_contract_amount) : 0,
        part1_customer_invoice_amount: formData.part1_customer_invoice_amount ? parseFloat(formData.part1_customer_invoice_amount) : 0,
        part2_customer_invoice_amount: formData.part2_customer_invoice_amount ? parseFloat(formData.part2_customer_invoice_amount) : 0,
        referral_comm: formData.referral_comm ? parseFloat(formData.referral_comm) : 0
      };

      if (editingProperty) {
        await axios.put(`${API}/properties/${editingProperty.id}`, submitData);
        toast.success('Property updated successfully');
      } else {
        await axios.post(`${API}/properties`, submitData);
        toast.success('Property created successfully');
      }
      resetForm();
      fetchProperties();
    } catch (error) {
      console.error('Error saving property:', error);
      toast.error('Failed to save property');
    }
  };

  const handleEdit = (property) => {
    setEditingProperty(property);
    setFormData({
      address: property.address,
      purchaser_id: property.purchaser_id || '',
      purchaser_name: property.purchaser_name || '',
      contract_type: property.contract_type,
      estimated_settlement_date: property.estimated_settlement_date || '',
      part1_settlement_date: property.part1_settlement_date || '',
      part2_settlement_date: property.part2_settlement_date || '',
      customer_id: property.customer_id || '',
      customer_name: property.customer_name || '',
      referral_partner_id: property.referral_partner_id || '',
      referral_partner_name: property.referral_partner_name || '',
      contract_sale_value: property.contract_sale_value?.toString() || '',
      invoice_amount: property.invoice_amount?.toString() || '',
      part1_contract_amount: property.part1_contract_amount?.toString() || '',
      part2_contract_amount: property.part2_contract_amount?.toString() || '',
      part1_customer_invoice_amount: property.part1_customer_invoice_amount?.toString() || '',
      part2_customer_invoice_amount: property.part2_customer_invoice_amount?.toString() || '',
      referral_comm: property.referral_comm?.toString() || '',
      status: property.status || 'draft',
      related_invoice_id: property.related_invoice_id || ''
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this property? This action cannot be undone.')) return;
    
    try {
      await axios.delete(`${API}/properties/${id}`);
      toast.success('Property deleted successfully');
      fetchProperties();
    } catch (error) {
      console.error('Error deleting property:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to delete property';
      toast.error(errorMessage);
    }
  };

  const resetForm = () => {
    setFormData({
      address: '',
      purchaser_id: '',
      purchaser_name: '',
      eoi_date: '',
      contract_type: '1 part',
      estimated_settlement_date: '',
      part1_settlement_date: '',
      part2_settlement_date: '',
      customer_id: '',
      customer_name: '',
      referral_partner_id: '',
      referral_partner_name: '',
      contract_sale_value: '',
      invoice_amount: '',
      part1_contract_amount: '',
      part2_contract_amount: '',
      part1_customer_invoice_amount: '',
      part2_customer_invoice_amount: '',
      referral_comm: '',
      status: 'draft',
      related_invoice_id: ''
    });
    setEditingProperty(null);
    setIsDialogOpen(false);
  };

  const handlePurchaserChange = (value) => {
    if (value === 'none') {
      setFormData(prev => ({
        ...prev,
        purchaser_id: '',
        purchaser_name: ''
      }));
      return;
    }
    const purchaser = purchasers.find(p => p.id === value);
    setFormData(prev => ({
      ...prev,
      purchaser_id: value,
      purchaser_name: purchaser ? purchaser.name : ''
    }));
  };

  const handleCustomerChange = (value) => {
    if (value === 'none') {
      setFormData(prev => ({
        ...prev,
        customer_id: '',
        customer_name: ''
      }));
      return;
    }
    const client = clients.find(c => c.id === value);
    setFormData(prev => ({
      ...prev,
      customer_id: value,
      customer_name: client ? client.name : ''
    }));
  };

  const handleReferralPartnerChange = (value) => {
    if (value === 'none') {
      setFormData(prev => ({
        ...prev,
        referral_partner_id: '',
        referral_partner_name: ''
      }));
      return;
    }
    const partner = referralPartners.find(p => p.id === value);
    setFormData(prev => ({
      ...prev,
      referral_partner_id: value,
      referral_partner_name: partner ? partner.name : ''
    }));
  };

  const formatDateSafe = (dateString) => {
    if (!dateString) return '-';
    return formatDate(dateString);
  };

  const formatCurrency = (amount) => {
    if (!amount) return '-';
    return `$${parseFloat(amount).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatSettlementDates = (property) => {
    if (property.contract_type === '1 part') {
      return formatDateSafe(property.estimated_settlement_date);
    } else if (property.contract_type === '2 part') {
      const part1 = formatDateSafe(property.part1_settlement_date);
      const part2 = formatDateSafe(property.part2_settlement_date);
      if (part1 === '-' && part2 === '-') return '-';
      return (
        <div className="text-sm">
          <div>Part 1: {part1}</div>
          <div>Part 2: {part2}</div>
        </div>
      );
    }
    return '-';
  };

  const formatContractAmounts = (property) => {
    if (property.contract_type === '1 part') {
      return formatCurrency(property.invoice_amount);
    } else if (property.contract_type === '2 part') {
      const part1 = formatCurrency(property.part1_contract_amount);
      const part2 = formatCurrency(property.part2_contract_amount);
      if (part1 === '-' && part2 === '-') return '-';
      return (
        <div className="text-sm">
          <div>Part 1: {part1}</div>
          <div>Part 2: {part2}</div>
        </div>
      );
    }
    return '-';
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-black text-black mb-2 tracking-tight">Properties</h1>
          <p className="text-gray-700 font-medium">Manage property sales information</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => resetForm()} data-testid="add-property-btn">
              <Plus className="mr-2 h-4 w-4" /> Add Property
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
            <DialogHeader>
              <DialogTitle>{editingProperty ? 'Edit Property' : 'Add Property'}</DialogTitle>
            </DialogHeader>
            <div className="flex-1 overflow-y-auto">
              <form id="property-form" onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="address">Property Address *</Label>
                <Textarea
                  id="address"
                  value={formData.address}
                  onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                  required
                  rows={2}
                  data-testid="property-address-input"
                />
              </div>
              
              <div>
                <Label htmlFor="purchaser">Customer</Label>
                <Select value={formData.purchaser_id || 'none'} onValueChange={handlePurchaserChange}>
                  <SelectTrigger data-testid="property-purchaser-select">
                    <SelectValue placeholder="Select customer" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Purchaser</SelectItem>
                    {purchasers.map((purchaser) => (
                      <SelectItem key={purchaser.id} value={purchaser.id}>
                        {purchaser.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="eoi_date">Expression of Interest (EOI) Submission Date</Label>
                <Input
                  id="eoi_date"
                  type="date"
                  value={formData.eoi_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, eoi_date: e.target.value }))}
                  data-testid="property-eoi-date-input"
                />
              </div>
              
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <Label htmlFor="contract_type">Contract Type</Label>
                  <Select value={formData.contract_type} onValueChange={(value) => setFormData(prev => ({ ...prev, contract_type: value }))}>
                    <SelectTrigger data-testid="property-contract-type-select">
                      <SelectValue placeholder="Select contract type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1 part">1 Part</SelectItem>
                      <SelectItem value="2 part">2 Part</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {formData.contract_type === '1 part' ? (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="estimated_settlement_date">Estimated Settlement Date</Label>
                      <Input
                        id="estimated_settlement_date"
                        type="date"
                        value={formData.estimated_settlement_date}
                        onChange={(e) => setFormData(prev => ({ ...prev, estimated_settlement_date: e.target.value }))}
                        min={new Date().toISOString().split('T')[0]}
                        data-testid="property-settlement-date-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="contract_sale_value">Contract Sale Value ($)</Label>
                      <Input
                        id="contract_sale_value"
                        type="number"
                        min="0"
                        step="0.01"
                        value={formData.contract_sale_value}
                        onChange={(e) => setFormData(prev => ({ ...prev, contract_sale_value: e.target.value }))}
                        placeholder="0.00"
                        data-testid="property-contract-sale-value-input"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="client">Client</Label>
                      <Select value={formData.customer_id || 'none'} onValueChange={handleCustomerChange}>
                        <SelectTrigger data-testid="property-client-select">
                          <SelectValue placeholder="Select client" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">No Client</SelectItem>
                          {clients.map((client) => (
                            <SelectItem key={client.id} value={client.id}>
                              {client.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="invoice_amount">Invoice Amount ($)</Label>
                      <Input
                        id="invoice_amount"
                        type="number"
                        min="0"
                        step="0.01"
                        value={formData.invoice_amount}
                        onChange={(e) => setFormData(prev => ({ ...prev, invoice_amount: e.target.value }))}
                        placeholder="0.00"
                        data-testid="property-invoice-amount-input"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 gap-4">
                    <div>
                      <Label htmlFor="referral_partner">Referral Partner</Label>
                      <Select value={formData.referral_partner_id || 'none'} onValueChange={handleReferralPartnerChange}>
                        <SelectTrigger data-testid="property-referral-partner-select">
                          <SelectValue placeholder="Select referral partner" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">No Referral Partner</SelectItem>
                          {referralPartners.map((partner) => (
                            <SelectItem key={partner.id} value={partner.id}>
                              {partner.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 gap-4">
                    <div>
                      <Label htmlFor="referral_comm">Referral Commission ($)</Label>
                      <Input
                        id="referral_comm"
                        type="number"
                        min="0"
                        step="0.01"
                        value={formData.referral_comm}
                        onChange={(e) => setFormData(prev => ({ ...prev, referral_comm: e.target.value }))}
                        placeholder="0.00"
                        data-testid="property-referral-comm-input"
                      />
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="part1_settlement_date">Part 1 Settlement Date</Label>
                      <Input
                        id="part1_settlement_date"
                        type="date"
                        value={formData.part1_settlement_date}
                        onChange={(e) => setFormData(prev => ({ ...prev, part1_settlement_date: e.target.value }))}
                        min={new Date().toISOString().split('T')[0]}
                        data-testid="property-part1-settlement-date-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="part1_contract_amount">Part 1 Contract Sales Amount ($)</Label>
                      <Input
                        id="part1_contract_amount"
                        type="number"
                        min="0"
                        step="0.01"
                        value={formData.part1_contract_amount}
                        onChange={(e) => setFormData(prev => ({ ...prev, part1_contract_amount: e.target.value }))}
                        placeholder="0.00"
                        data-testid="property-part1-contract-amount-input"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="part2_settlement_date">Part 2 Settlement Date</Label>
                      <Input
                        id="part2_settlement_date"
                        type="date"
                        value={formData.part2_settlement_date}
                        onChange={(e) => setFormData(prev => ({ ...prev, part2_settlement_date: e.target.value }))}
                        min={new Date().toISOString().split('T')[0]}
                        data-testid="property-part2-settlement-date-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="part2_contract_amount">Part 2 Contract Sales Amount ($)</Label>
                      <Input
                        id="part2_contract_amount"
                        type="number"
                        min="0"
                        step="0.01"
                        value={formData.part2_contract_amount}
                        onChange={(e) => setFormData(prev => ({ ...prev, part2_contract_amount: e.target.value }))}
                        placeholder="0.00"
                        data-testid="property-part2-contract-amount-input"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 gap-4">
                    <div>
                      <Label htmlFor="client">Client</Label>
                      <Select value={formData.customer_id || 'none'} onValueChange={handleCustomerChange}>
                        <SelectTrigger data-testid="property-client-select">
                          <SelectValue placeholder="Select client" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">No Client</SelectItem>
                          {clients.map((client) => (
                            <SelectItem key={client.id} value={client.id}>
                              {client.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="part1_customer_invoice_amount">Part 1 Client Invoice Amount ($)</Label>
                      <Input
                        id="part1_customer_invoice_amount"
                        type="number"
                        min="0"
                        step="0.01"
                        value={formData.part1_customer_invoice_amount}
                        onChange={(e) => setFormData(prev => ({ ...prev, part1_customer_invoice_amount: e.target.value }))}
                        placeholder="0.00"
                        data-testid="property-part1-client-invoice-amount-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="part2_customer_invoice_amount">Part 2 Client Invoice Amount ($)</Label>
                      <Input
                        id="part2_customer_invoice_amount"
                        type="number"
                        min="0"
                        step="0.01"
                        value={formData.part2_customer_invoice_amount}
                        onChange={(e) => setFormData(prev => ({ ...prev, part2_customer_invoice_amount: e.target.value }))}
                        placeholder="0.00"
                        data-testid="property-part2-client-invoice-amount-input"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 gap-4">
                    <div>
                      <Label htmlFor="referral_partner">Referral Partner</Label>
                      <Select value={formData.referral_partner_id || 'none'} onValueChange={handleReferralPartnerChange}>
                        <SelectTrigger data-testid="property-referral-partner-select">
                          <SelectValue placeholder="Select referral partner" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">No Referral Partner</SelectItem>
                          {referralPartners.map((partner) => (
                            <SelectItem key={partner.id} value={partner.id}>
                              {partner.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 gap-4">
                    <div>
                      <Label htmlFor="referral_comm">Referral Commission ($)</Label>
                      <Input
                        id="referral_comm"
                        type="number"
                        min="0"
                        step="0.01"
                        value={formData.referral_comm}
                        onChange={(e) => setFormData(prev => ({ ...prev, referral_comm: e.target.value }))}
                        placeholder="0.00"
                        data-testid="property-referral-comm-input"
                      />
                    </div>
                  </div>
                </>
              )}
              
              </form>
            </div>
            <div className="flex-shrink-0 pt-4 border-t mt-6">
              <DialogFooter>
                <Button type="button" variant="outline" onClick={resetForm}>Cancel</Button>
                <Button type="submit" form="property-form" data-testid="save-property-btn">
                  {editingProperty ? 'Update' : 'Create'}
                </Button>
              </DialogFooter>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardContent className="p-6">
          {properties.length > 0 ? (
            <div className="overflow-x-auto">
              <div className="border rounded-lg min-w-[1380px]">
                {/* Fixed Header */}
                <div className="bg-gray-50 border-b px-4 py-3 text-xs font-semibold text-gray-700">
                  <div className="flex">
                    <div className="w-48 flex-shrink-0">Address</div>
                    <div className="w-32 flex-shrink-0">Customer</div>
                    <div className="w-24 flex-shrink-0">EOI Date</div>
                    <div className="w-28 flex-shrink-0">Contract Type</div>
                    <div className="w-32 flex-shrink-0">Settlement Date(s)</div>
                    <div className="w-32 flex-shrink-0">Client</div>
                    <div className="w-32 flex-shrink-0">Referral Partner</div>
                    <div className="w-32 flex-shrink-0">Contract Amount(s)</div>
                    <div className="w-28 flex-shrink-0">Referral Comm</div>
                    <div className="w-20 flex-shrink-0">Status</div>
                    <div className="w-24 flex-shrink-0 text-right">Actions</div>
                  </div>
                </div>
                
                {/* Scrollable Content */}
                <div className="max-h-[60vh] overflow-y-auto">
                  {properties.map((property) => (
                    <div key={property.id} className="flex border-b border-gray-100 hover:bg-gray-50 px-4 py-3 text-xs">
                      <div className="w-48 flex-shrink-0 font-medium max-w-xs truncate">{property.address}</div>
                      <div className="w-32 flex-shrink-0">{property.purchaser_name || '-'}</div>
                      <div className="w-24 flex-shrink-0">{property.eoi_date ? formatDate(property.eoi_date) : '-'}</div>
                      <div className="w-28 flex-shrink-0">
                        <Badge variant={property.contract_type === '2 part' ? 'default' : 'secondary'} className="text-xs">
                          {property.contract_type}
                        </Badge>
                      </div>
                      <div className="w-32 flex-shrink-0">{formatSettlementDates(property)}</div>
                      <div className="w-32 flex-shrink-0">{property.customer_name || '-'}</div>
                      <div className="w-32 flex-shrink-0">{property.referral_partner_name || '-'}</div>
                      <div className="w-32 flex-shrink-0">{formatContractAmounts(property)}</div>
                      <div className="w-28 flex-shrink-0">{formatCurrency(property.referral_comm)}</div>
                      <div className="w-20 flex-shrink-0">
                        <Badge variant={
                          property.status === 'paid' ? 'default' :
                          property.status === 'sent' ? 'secondary' :
                          property.status === 'overdue' ? 'destructive' :
                          'outline'
                        } className="text-xs">
                          {property.status || 'draft'}
                        </Badge>
                      </div>
                      <div className="w-24 flex-shrink-0 text-right">
                        <div className="flex gap-1 justify-end">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(property)}
                            data-testid={`edit-property-${property.id}`}
                            className="h-7 w-7 p-0"
                          >
                            <Edit className="h-3 w-3" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(property.id)}
                            data-testid={`delete-property-${property.id}`}
                            className="h-7 w-7 p-0"
                          >
                            <Trash2 className="h-3 w-3 text-red-500" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-12 text-gray-500">
              <MapPin className="mx-auto h-16 w-16 mb-4 text-gray-400" />
              <p className="text-lg mb-2">No properties yet</p>
              <p className="text-sm">Add your first property to get started</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Layout Component


// Commissions Management Component
const CommissionsManagement = () => {
  const [commissionsData, setCommissionsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drillDownFilter, setDrillDownFilter] = useState('');
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    fetchCommissions();
    
    // Check for URL parameters to set drill-down filter
    const urlParams = new URLSearchParams(location.search);
    const filterParam = urlParams.get('filter');
    if (filterParam === 'unpaid') {
      setDrillDownFilter(filterParam);
    }
  }, [location.search]);

  const fetchCommissions = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/commissions`);
      setCommissionsData(response.data);
    } catch (error) {
      console.error('Error fetching commissions:', error);
      toast.error('Failed to load commissions');
    } finally {
      setLoading(false);
    }
  };

  const markCommissionPaid = async (invoiceId, paid) => {
    try {
      await axios.put(`${API}/invoices/${invoiceId}/mark-commission-paid?paid=${paid}`);
      toast.success(paid ? 'Referral Partners Comm marked as paid' : 'Referral Partners Comm marked as unpaid');
      fetchCommissions();
    } catch (error) {
      console.error('Error updating commission status:', error);
      toast.error('Failed to update Referral Partners Comm status');
    }
  };

  const markPartnerPaid = async (partnerId, partnerName) => {
    if (!window.confirm(`Mark all unpaid Referral Partners Comm for ${partnerName} as paid?`)) return;
    
    try {
      const response = await axios.put(`${API}/commissions/mark-partner-paid/${partnerId}`);
      toast.success(`Marked ${response.data.count} Referral Partners Comm as paid`);
      fetchCommissions();
    } catch (error) {
      console.error('Error marking partner commissions paid:', error);
      toast.error('Failed to update Referral Partners Comm');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'bg-emerald-500';
      case 'sent': return 'bg-blue-500';
      case 'overdue': return 'bg-red-500';
      case 'draft': return 'bg-gray-500';
      default: return 'bg-gray-500';
    }
  };

  if (loading) {
    return <div className="flex justify-center items-center h-96">Loading...</div>;
  }

  // Filter partners and commissions based on drill-down filter
  const filteredPartnersData = drillDownFilter === 'unpaid' && commissionsData?.partners 
    ? {
        ...commissionsData,
        partners: commissionsData.partners
          .map(partner => ({
            ...partner,
            invoices: partner.invoices.filter(invoice => !invoice.commission_paid)
          }))
          .filter(partner => partner.invoices.length > 0) // Only show partners with unpaid commissions
      }
    : commissionsData;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-4xl font-black text-black mb-2 tracking-tight">
          Referral Partners Comm Management
          {drillDownFilter && (
            <span className="text-2xl font-medium text-purple-600 ml-3">- Unpaid Commissions</span>
          )}
        </h1>
        <p className="text-gray-700 font-medium">
          {drillDownFilter ? 'Showing only unpaid referral partner commissions' : 'Track and manage referral partner commissions'}
        </p>
        {drillDownFilter && (
          <div className="mt-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => {
                setDrillDownFilter('');
                navigate('/commissions');
              }}
              className="text-xs"
            >
              ← Back to All Commissions
            </Button>
          </div>
        )}
      </div>

      {/* Summary Cards */}
      {!drillDownFilter && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm text-gray-600">Total Referral Partners Comm</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-purple-600">
                ${commissionsData?.summary?.total_commissions?.toFixed(2) || '0.00'}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm text-gray-600">Paid Referral Partners Comm</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-emerald-600">
                ${commissionsData?.summary?.total_paid?.toFixed(2) || '0.00'}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm text-gray-600">Unpaid Referral Partners Comm</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-orange-600">
                ${commissionsData?.summary?.total_unpaid?.toFixed(2) || '0.00'}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      
      {drillDownFilter && (
        <Card className="bg-orange-50 border-orange-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-orange-700">
              <Badge variant="secondary">Dashboard Filter Active</Badge>
              Showing Unpaid Commissions Only
            </CardTitle>
            <CardDescription>
              Displaying {filteredPartnersData?.partners?.length || 0} partners with unpaid commissions totaling ${filteredPartnersData?.summary?.total_unpaid?.toFixed(2) || commissionsData?.summary?.total_unpaid?.toFixed(2) || '0.00'}
            </CardDescription>
          </CardHeader>
        </Card>
      )}

      {/* Partners with Commissions */}
      {filteredPartnersData?.partners && filteredPartnersData.partners.length > 0 ? (
        <div className="space-y-6">
          {filteredPartnersData.partners.map((partner) => (
            <Card key={partner.partner_id}>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle>{partner.partner_name}</CardTitle>
                    <CardDescription>
                      Total: ${partner.total_commissions.toFixed(2)} | 
                      Paid: ${partner.paid_commissions.toFixed(2)} | 
                      Unpaid: ${partner.unpaid_commissions.toFixed(2)}
                    </CardDescription>
                  </div>
                  {partner.unpaid_commissions > 0 && (
                    <Button
                      onClick={() => markPartnerPaid(partner.partner_id, partner.partner_name)}
                      data-testid={`mark-all-paid-${partner.partner_id}`}
                    >
                      Mark All Paid
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table className="min-w-[950px]">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="whitespace-nowrap">Invoice #</TableHead>
                      <TableHead className="whitespace-nowrap">Date</TableHead>
                      <TableHead className="whitespace-nowrap">Client</TableHead>
                      <TableHead className="whitespace-nowrap">Invoice Total</TableHead>
                      <TableHead className="whitespace-nowrap">Referral Partners Comm</TableHead>
                      <TableHead className="whitespace-nowrap">Invoice Status</TableHead>
                      <TableHead className="whitespace-nowrap">Comm Status</TableHead>
                      <TableHead className="text-right whitespace-nowrap w-32">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {partner.invoices.map((invoice) => (
                      <TableRow key={invoice.id}>
                        <TableCell className="font-medium whitespace-nowrap">{invoice.invoice_number}</TableCell>
                        <TableCell className="whitespace-nowrap">{formatDate(invoice.date)}</TableCell>
                        <TableCell className="whitespace-nowrap">{invoice.customer_name}</TableCell>
                        <TableCell className="whitespace-nowrap">${invoice.total.toFixed(2)}</TableCell>
                        <TableCell className="font-semibold text-purple-600 whitespace-nowrap">
                          ${invoice.rebate_amount.toFixed(2)}
                        </TableCell>
                        <TableCell className="whitespace-nowrap">
                          <Badge className={getStatusColor(invoice.status)}>
                            {invoice.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="whitespace-nowrap">
                          {invoice.commission_paid ? (
                            <Badge className="bg-emerald-500">Paid</Badge>
                          ) : (
                            <Badge className="bg-orange-500">Unpaid</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right whitespace-nowrap">
                          {invoice.commission_paid ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => markCommissionPaid(invoice.id, false)}
                              data-testid={`mark-unpaid-${invoice.id}`}
                            >
                              Mark Unpaid
                            </Button>
                          ) : (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => markCommissionPaid(invoice.id, true)}
                              data-testid={`mark-paid-${invoice.id}`}
                            >
                              Mark Paid
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="text-center py-12">
            <DollarSign className="mx-auto h-16 w-16 mb-4 text-gray-400" />
            <p className="text-lg text-gray-500 mb-2">
              {drillDownFilter ? 'No unpaid commissions found' : 'No Referral Partners Comm yet'}
            </p>
            <p className="text-sm text-gray-400">
              {drillDownFilter 
                ? 'All referral partner commissions have been paid'
                : 'Create invoices with referral partners to track Referral Partners Comm'
              }
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};


// Reports Management Component
const ReportsManagement = () => {
  const [reportsData, setReportsData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    status: 'all',
    reconciled: 'all'
  });

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      
      if (filters.startDate) params.append('start_date', filters.startDate);
      if (filters.endDate) params.append('end_date', filters.endDate);
      if (filters.status !== 'all') params.append('status', filters.status);
      if (filters.reconciled !== 'all') params.append('reconciled', filters.reconciled);
      
      const response = await axios.get(`${API}/reports/invoices?${params}`);
      setReportsData(response.data);
    } catch (error) {
      console.error('Error fetching reports:', error);
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const applyFilters = () => {
    fetchReports();
  };

  const clearFilters = () => {
    setFilters({
      startDate: '',
      endDate: '',
      status: 'all',
      reconciled: 'all'
    });
    setTimeout(() => fetchReports(), 100);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'bg-emerald-500';
      case 'sent': return 'bg-blue-500';
      case 'overdue': return 'bg-red-500';
      case 'draft': return 'bg-gray-500';
      default: return 'bg-gray-500';
    }
  };

  const exportToCSV = () => {
    if (!reportsData?.invoices) return;
    
    const csvHeaders = ['Invoice #', 'Date', 'Due Date', 'Client', 'Property', 'Status', 'Subtotal', 'GST', 'Total', 'Reconciled'];
    const csvData = reportsData.invoices.map(inv => [
      inv.invoice_number,
      formatDate(inv.date),
      formatDate(inv.due_date),
      inv.customer_name || '',
      inv.property_address || '',
      inv.status,
      inv.subtotal?.toFixed(2) || '0.00',
      inv.tax_amount?.toFixed(2) || '0.00',
      inv.total?.toFixed(2) || '0.00',
      (inv.status === 'paid' && inv.bank_reconciliation_date) ? 'Yes' : 'No'
    ]);
    
    const csvContent = [csvHeaders, ...csvData]
      .map(row => row.join(','))
      .join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `invoice-reports-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (loading && !reportsData) {
    return <div className="flex justify-center items-center h-96">Loading reports...</div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-4xl font-black text-black mb-2 tracking-tight">Invoice Reports</h1>
        <p className="text-gray-700 font-medium">Comprehensive reporting and analytics for invoices</p>
      </div>

      {/* Filters Section */}
      <Card>
        <CardHeader>
          <CardTitle>Report Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
            <div>
              <Label htmlFor="startDate">Start Date</Label>
              <Input
                id="startDate"
                type="date"
                value={filters.startDate}
                onChange={(e) => handleFilterChange('startDate', e.target.value)}
              />
            </div>
            
            <div>
              <Label htmlFor="endDate">End Date</Label>
              <Input
                id="endDate"
                type="date"
                value={filters.endDate}
                onChange={(e) => handleFilterChange('endDate', e.target.value)}
              />
            </div>
            
            <div>
              <Label htmlFor="status">Status</Label>
              <Select value={filters.status} onValueChange={(value) => handleFilterChange('status', value)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="sent">Sent</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                  <SelectItem value="overdue">Overdue</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label htmlFor="reconciled">Reconciliation</Label>
              <Select value={filters.reconciled} onValueChange={(value) => handleFilterChange('reconciled', value)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="true">Reconciled Only</SelectItem>
                  <SelectItem value="false">Unreconciled Only</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="flex gap-2">
            <Button onClick={applyFilters} disabled={loading}>
              {loading ? 'Loading...' : 'Apply Filters'}
            </Button>
            <Button variant="outline" onClick={clearFilters}>
              Clear Filters
            </Button>
            <Button variant="outline" onClick={exportToCSV} disabled={!reportsData?.invoices?.length}>
              Export CSV
            </Button>
          </div>
        </CardContent>
      </Card>

      {reportsData && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm text-gray-600">Total Invoices</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-blue-600">
                  {reportsData.summary.total_invoices}
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm text-gray-600">Subtotal</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-600">
                  ${reportsData.summary.subtotal?.toFixed(2) || '0.00'}
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm text-gray-600">GST Amount</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-orange-600">
                  ${reportsData.summary.gst_amount?.toFixed(2) || '0.00'}
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm text-gray-600">Total Amount</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-purple-600">
                  ${reportsData.summary.total_amount?.toFixed(2) || '0.00'}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Status Breakdown */}
          <Card>
            <CardHeader>
              <CardTitle>Status Breakdown</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {Object.entries(reportsData.status_breakdown || {}).map(([status, data]) => (
                  <div key={status} className="p-4 border rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge className={getStatusColor(status)}>{status.toUpperCase()}</Badge>
                      <span className="font-semibold">{data.count}</span>
                    </div>
                    <div className="text-sm space-y-1">
                      <div>Subtotal: ${data.subtotal?.toFixed(2) || '0.00'}</div>
                      <div>GST: ${data.gst_amount?.toFixed(2) || '0.00'}</div>
                      <div className="font-bold">Total: ${data.total_amount?.toFixed(2) || '0.00'}</div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Reconciliation Breakdown */}
          <Card>
            <CardHeader>
              <CardTitle>Reconciliation Status (Paid Invoices Only)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="p-4 border rounded-lg bg-emerald-50">
                  <div className="flex items-center gap-2 mb-2">
                    <Badge className="bg-emerald-500">RECONCILED</Badge>
                    <span className="font-semibold">{reportsData.reconciliation_breakdown.reconciled.count}</span>
                  </div>
                  <div className="text-sm space-y-1">
                    <div>Subtotal: ${reportsData.reconciliation_breakdown.reconciled.subtotal?.toFixed(2) || '0.00'}</div>
                    <div>GST: ${reportsData.reconciliation_breakdown.reconciled.gst_amount?.toFixed(2) || '0.00'}</div>
                    <div className="font-bold">Total: ${reportsData.reconciliation_breakdown.reconciled.total_amount?.toFixed(2) || '0.00'}</div>
                  </div>
                </div>
                
                <div className="p-4 border rounded-lg bg-orange-50">
                  <div className="flex items-center gap-2 mb-2">
                    <Badge className="bg-orange-500">UNRECONCILED</Badge>
                    <span className="font-semibold">{reportsData.reconciliation_breakdown.unreconciled.count}</span>
                  </div>
                  <div className="text-sm space-y-1">
                    <div>Subtotal: ${reportsData.reconciliation_breakdown.unreconciled.subtotal?.toFixed(2) || '0.00'}</div>
                    <div>GST: ${reportsData.reconciliation_breakdown.unreconciled.gst_amount?.toFixed(2) || '0.00'}</div>
                    <div className="font-bold">Total: ${reportsData.reconciliation_breakdown.unreconciled.total_amount?.toFixed(2) || '0.00'}</div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Monthly Breakdown */}
          {Object.keys(reportsData.monthly_breakdown || {}).length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Monthly Breakdown</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {Object.entries(reportsData.monthly_breakdown).map(([month, data]) => (
                    <div key={month} className="flex items-center justify-between p-4 border rounded-lg">
                      <div>
                        <div className="font-semibold">{month}</div>
                        <div className="text-sm text-gray-600">{data.count} invoices</div>
                      </div>
                      <div className="text-right text-sm">
                        <div>Subtotal: ${data.subtotal?.toFixed(2) || '0.00'}</div>
                        <div>GST: ${data.gst_amount?.toFixed(2) || '0.00'}</div>
                        <div className="font-bold">Total: ${data.total_amount?.toFixed(2) || '0.00'}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Individual Invoices */}
          <Card>
            <CardHeader>
              <CardTitle>Individual Invoices ({reportsData.invoices?.length || 0})</CardTitle>
            </CardHeader>
            <CardContent>
              {reportsData.invoices && reportsData.invoices.length > 0 ? (
                <div className="overflow-x-auto">
                  <div className="border rounded-lg min-w-[1020px]">
                  {/* Fixed Header */}
                  <div className="bg-gray-50 border-b px-4 py-3 text-xs font-semibold text-gray-700">
                    <div className="flex">
                      <div className="w-32 flex-shrink-0">Invoice #</div>
                      <div className="w-24 flex-shrink-0">Date</div>
                      <div className="w-32 flex-shrink-0">Client</div>
                      <div className="w-40 flex-shrink-0">Property</div>
                      <div className="w-20 flex-shrink-0">Status</div>
                      <div className="w-24 flex-shrink-0">Subtotal</div>
                      <div className="w-24 flex-shrink-0">GST</div>
                      <div className="w-24 flex-shrink-0">Total</div>
                      <div className="w-24 flex-shrink-0">Reconciled</div>
                    </div>
                  </div>
                  
                  {/* Scrollable Content */}
                  <div className="max-h-[60vh] overflow-y-auto">
                    {reportsData.invoices.map((invoice) => (
                      <div key={invoice.id} className="flex border-b border-gray-100 hover:bg-gray-50 px-4 py-3 text-sm">
                        <div className="w-32 flex-shrink-0 font-medium">{invoice.invoice_number}</div>
                        <div className="w-24 flex-shrink-0">{formatDate(invoice.date)}</div>
                        <div className="w-32 flex-shrink-0">{invoice.customer_name || '-'}</div>
                        <div className="w-40 flex-shrink-0">
                          {invoice.property_address ? (
                            <span className="text-blue-600 font-medium text-xs">
                              {invoice.property_address}
                            </span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </div>
                        <div className="w-20 flex-shrink-0">
                          <Badge className={`${getStatusColor(invoice.status)} text-xs`}>
                            {invoice.status}
                          </Badge>
                        </div>
                        <div className="w-24 flex-shrink-0 font-medium">${invoice.subtotal?.toFixed(2) || '0.00'}</div>
                        <div className="w-24 flex-shrink-0">${invoice.tax_amount?.toFixed(2) || '0.00'}</div>
                        <div className="w-24 flex-shrink-0 font-bold">${invoice.total?.toFixed(2) || '0.00'}</div>
                        <div className="w-24 flex-shrink-0">
                          {invoice.status === 'paid' && invoice.bank_reconciliation_date ? (
                            <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-xs">
                              Yes
                            </Badge>
                          ) : invoice.status === 'paid' ? (
                            <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200 text-xs">
                              No
                            </Badge>
                          ) : (
                            <span className="text-gray-400 text-xs">N/A</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <FileText className="mx-auto h-16 w-16 mb-4 text-gray-400" />
                  <p className="text-lg mb-2">No invoices found</p>
                  <p className="text-sm">Try adjusting your filters or date range</p>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

// Client Invoices Management Component
const ClientInvoicesManagement = () => {
  const [clientInvoicesData, setClientInvoicesData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchClientInvoices();
  }, []);

  const fetchClientInvoices = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/client-invoices`);
      setClientInvoicesData(response.data);
    } catch (error) {
      console.error('Error fetching client invoices:', error);
      toast.error('Failed to load client invoices');
    } finally {
      setLoading(false);
    }
  };

  const updateInvoiceStatus = async (invoiceId, status) => {
    try {
      const response = await axios.put(`${API}/invoices/${invoiceId}/status`, null, {
        params: { status: status }
      });
      toast.success('Invoice status updated successfully');
      fetchClientInvoices();
    } catch (error) {
      console.error('Error updating invoice status:', error);
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Failed to update invoice status');
      }
    }
  };

  const updateInvoiceReconciliation = async (invoiceId, reconciled) => {
    try {
      const response = await axios.put(`${API}/invoices/${invoiceId}/reconciliation`, null, {
        params: { reconciled: reconciled }
      });
      toast.success('Invoice reconciliation updated successfully');
      fetchClientInvoices();
    } catch (error) {
      console.error('Error updating invoice reconciliation:', error);
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Failed to update invoice reconciliation');
      }
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'bg-emerald-500';
      case 'sent': return 'bg-blue-500';
      case 'overdue': return 'bg-red-500';
      case 'draft': return 'bg-gray-500';
      default: return 'bg-gray-500';
    }
  };

  if (loading) {
    return <div className="flex justify-center items-center h-96">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-4xl font-black text-black mb-2 tracking-tight">$ Client Invoice Management</h1>
        <p className="text-gray-700 font-medium">Track and manage invoices by client</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm text-gray-600">Total Clients</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">
              {clientInvoicesData?.summary?.total_clients || 0}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm text-gray-600">Total Invoices</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-purple-600">
              {clientInvoicesData?.summary?.total_invoices || 0}
            </div>
            <div className="text-sm text-gray-500 mt-1">
              ${clientInvoicesData?.summary?.total_amount?.toFixed(2) || '0.00'}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm text-gray-600">Paid Amount</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-emerald-600">
              ${clientInvoicesData?.summary?.paid_amount?.toFixed(2) || '0.00'}
            </div>
            <div className="text-sm text-gray-500 mt-1">
              Reconciled: ${clientInvoicesData?.summary?.reconciled_amount?.toFixed(2) || '0.00'}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm text-gray-600">Pending Amount</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">
              ${clientInvoicesData?.summary?.pending_amount?.toFixed(2) || '0.00'}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Clients with Invoices */}
      {clientInvoicesData?.clients && clientInvoicesData.clients.length > 0 ? (
        <div className="space-y-6">
          {clientInvoicesData.clients.map((client) => (
            <Card key={client.client_id}>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle>{client.client_name}</CardTitle>
                    <CardDescription>
                      Total: ${client.total_amount.toFixed(2)} | 
                      Paid: ${client.paid_amount.toFixed(2)} | 
                      Reconciled: ${client.reconciled_amount.toFixed(2)} | 
                      Pending: ${client.pending_amount.toFixed(2)}
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <div className="border rounded-lg min-w-[950px]">
                    {/* Fixed Header */}
                    <div className="bg-gray-50 border-b px-4 py-3 text-xs font-semibold text-gray-700">
                      <div className="flex">
                        <div className="w-32 flex-shrink-0">Invoice #</div>
                        <div className="w-24 flex-shrink-0">Date</div>
                        <div className="w-24 flex-shrink-0">Due Date</div>
                        <div className="w-40 flex-shrink-0">Property</div>
                        <div className="w-24 flex-shrink-0">Total</div>
                        <div className="w-24 flex-shrink-0">Status</div>
                        <div className="w-28 flex-shrink-0">Reconciled</div>
                        <div className="w-36 flex-shrink-0 text-right">Actions</div>
                      </div>
                    </div>
                    
                    {/* Scrollable Content */}
                    <div className="max-h-[40vh] overflow-y-auto">
                      {client.invoices.map((invoice) => (
                        <div key={invoice.id} className="flex border-b border-gray-100 hover:bg-gray-50 px-4 py-3 text-sm">
                          <div className="w-32 flex-shrink-0 font-medium">{invoice.invoice_number}</div>
                          <div className="w-24 flex-shrink-0">{formatDate(invoice.date)}</div>
                          <div className="w-24 flex-shrink-0">{formatDate(invoice.due_date)}</div>
                          <div className="w-40 flex-shrink-0">
                            {invoice.property_address ? (
                              <span className="text-blue-600 font-medium text-xs">
                                {invoice.property_address}
                              </span>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </div>
                          <div className="w-24 flex-shrink-0 font-medium">${invoice.total.toFixed(2)}</div>
                          <div className="w-24 flex-shrink-0">
                            <Select 
                              value={invoice.status} 
                              onValueChange={(status) => updateInvoiceStatus(invoice.id, status)}
                            >
                              <SelectTrigger className="h-7 text-xs">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="draft">Draft</SelectItem>
                                <SelectItem value="sent">Sent</SelectItem>
                                <SelectItem value="paid">Paid</SelectItem>
                                <SelectItem value="overdue">Overdue</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="w-28 flex-shrink-0">
                            {invoice.status === 'paid' ? (
                              <Select 
                                value={invoice.bank_reconciliation_date ? 'reconciled' : 'pending'}
                                onValueChange={(value) => updateInvoiceReconciliation(invoice.id, value === 'reconciled')}
                              >
                                <SelectTrigger className="h-7 text-xs">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="pending">Pending</SelectItem>
                                  <SelectItem value="reconciled">Reconciled</SelectItem>
                                </SelectContent>
                              </Select>
                            ) : (
                              <Badge variant="outline" className="text-xs">
                                N/A
                              </Badge>
                            )}
                          </div>
                          <div className="w-36 flex-shrink-0 text-right">
                            <div className="flex gap-1 justify-end">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => window.open(`/invoices/${invoice.id}`, '_blank')}
                              className="h-7 w-7 p-0"
                            >
                              <Eye className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="text-center py-12">
            <FileText className="mx-auto h-16 w-16 mb-4 text-gray-400" />
            <p className="text-lg text-gray-500 mb-2">No client invoices yet</p>
            <p className="text-sm text-gray-400">Create invoices for clients to track them here</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

const Layout = ({ children }) => {
  const navigate = useNavigate();
  const location = window.location.pathname;
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState(() => {
    // Load from localStorage or default to all collapsed
    const saved = localStorage.getItem('expandedMenuGroups');
    return saved ? JSON.parse(saved) : {
      home: false,
      clients: false,
      properties: false,
      financial: false,
      operations: false,
      settings: false
    };
  });

  const handleLogout = () => {
    localStorage.removeItem('user');
    toast.success('Logged out successfully');
    navigate('/login');
  };

  const toggleGroup = (groupKey) => {
    // Accordion behavior: close all other groups when opening one
    const isCurrentlyExpanded = expandedGroups[groupKey];
    const newExpandedGroups = {
      home: false,
      clients: false,
      properties: false,
      financial: false,
      operations: false,
      settings: false,
      [groupKey]: !isCurrentlyExpanded // Toggle the clicked group
    };
    setExpandedGroups(newExpandedGroups);
    localStorage.setItem('expandedMenuGroups', JSON.stringify(newExpandedGroups));
  };

  // Menu structure with groups
  const menuGroups = [
    {
      key: 'home',
      label: 'HOME',
      icon: <Home className="h-4 w-4" />,
      roles: ['admin', 'cashflow_only'],
      items: [
        { path: '/', label: 'Home', icon: <Home className="h-5 w-5" />, roles: ['admin', 'cashflow_only'] },
        { path: '/dashboard', label: 'Dashboard', icon: <FileText className="h-5 w-5" />, roles: ['admin'] },
      ]
    },
    {
      key: 'clients',
      label: 'CLIENT MANAGEMENT',
      icon: <Users className="h-4 w-4" />,
      roles: ['admin'],
      items: [
        { path: '/customers', label: 'Clients', icon: <Users className="h-5 w-5" />, roles: ['admin'] },
        { path: '/contacts', label: 'Contacts', icon: <Users className="h-5 w-5" />, roles: ['admin'] },
        { path: '/purchasers', label: 'Customer', icon: <Home className="h-5 w-5" />, roles: ['admin'] },
        { path: '/referral-partners', label: 'Referral Partners', icon: <Handshake className="h-5 w-5" />, roles: ['admin'] },
      ]
    },
    {
      key: 'properties',
      label: 'PROPERTIES',
      icon: <MapPin className="h-4 w-4" />,
      roles: ['admin'],
      items: [
        { path: '/properties', label: 'Properties', icon: <MapPin className="h-5 w-5" />, roles: ['admin'] },
      ]
    },
    {
      key: 'financial',
      label: 'FINANCIAL',
      icon: <DollarSign className="h-4 w-4" />,
      roles: ['admin', 'cashflow_only'],
      items: [
        { path: '/invoices', label: 'Invoices', icon: <FileText className="h-5 w-5" />, roles: ['admin'] },
        { path: '/client-invoices', label: 'Client Invoices', icon: <DollarSign className="h-5 w-5" />, roles: ['admin'] },
        { path: '/commissions', label: 'Referral Comm', icon: <DollarSign className="h-5 w-5" />, roles: ['admin'] },
        { path: '/cashflow-calculator', label: 'Cashflow Calc', icon: <Calculator className="h-5 w-5" />, roles: ['admin', 'cashflow_only'] },
      ]
    },
    {
      key: 'operations',
      label: 'OPERATIONS',
      icon: <Clock className="h-4 w-4" />,
      roles: ['admin'],
      items: [
        { path: '/time-tracking', label: 'Time Tracking', icon: <Clock className="h-5 w-5" />, roles: ['admin'] },
        { path: '/reports', label: 'Reports', icon: <FileText className="h-5 w-5" />, roles: ['admin'] },
      ]
    },
    {
      key: 'settings',
      label: 'SETTINGS',
      icon: <Settings className="h-4 w-4" />,
      roles: ['admin'],
      items: [
        { path: '/settings', label: 'Settings', icon: <Settings className="h-5 w-5" />, roles: ['admin'] },
      ]
    },
  ];

  const userRole = user.role || 'admin';
  
  // Filter groups and items based on user role
  const filteredMenuGroups = menuGroups.map(group => ({
    ...group,
    items: group.items.filter(item => item.roles.includes(userRole))
  })).filter(group => {
    // Include group if user has access to it and it has visible items
    return group.roles.includes(userRole) && group.items.length > 0;
  });

  const handleNavClick = (path) => {
    navigate(path);
    setMobileMenuOpen(false); // Close mobile menu after navigation
  };

  // Auto-expand group containing current page on mount
  useEffect(() => {
    // Find which group contains the current path
    const currentGroup = menuGroups.find(group => 
      group.items.some(item => item.path === location)
    );
    
    if (currentGroup && !expandedGroups[currentGroup.key]) {
      // Open the group containing the current page
      const newExpandedGroups = {
        home: false,
        clients: false,
        properties: false,
        financial: false,
        operations: false,
        settings: false,
        [currentGroup.key]: true
      };
      setExpandedGroups(newExpandedGroups);
      localStorage.setItem('expandedMenuGroups', JSON.stringify(newExpandedGroups));
    }
  }, [location]);

  return (
    <div className="min-h-screen bg-yellow-50 overflow-x-hidden">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-gradient-to-r from-yellow-200 to-amber-200 border-b-2 border-yellow-300 shadow-md z-50 flex items-center justify-between px-3">
        <div className="flex items-center gap-2 flex-1 min-w-0 mr-2">
          <img 
            src="/bedirect-logo.png" 
            alt="Bedirect Logo" 
            className="flex-shrink-0" 
            style={{ 
              height: '35px',
              width: '35px',
              filter: 'brightness(0) contrast(1.2)',
              imageRendering: '-webkit-optimize-contrast',
              WebkitFontSmoothing: 'antialiased'
            }} 
          />
          <h1 className="text-base sm:text-lg font-black text-black truncate">Business Suite</h1>
        </div>
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="p-2 rounded-lg bg-yellow-300 hover:bg-yellow-400 transition-colors shadow-md hover:shadow-lg flex-shrink-0"
          aria-label="Toggle menu"
        >
          {mobileMenuOpen ? <X className="h-6 w-6 text-black" /> : <Menu className="h-6 w-6 text-black" />}
        </button>
      </div>

      {/* Desktop Sidebar */}
      <div className="hidden lg:flex fixed left-0 top-0 h-full w-64 bg-gradient-to-b from-amber-100 to-yellow-100 border-r-2 border-yellow-200 shadow-xl flex-col">
        <div className="p-4 bg-gradient-to-r from-yellow-200 to-amber-200">
          <div className="flex items-center gap-3 mb-1">
            <img 
              src="/bedirect-logo.png" 
              alt="Bedirect Logo" 
              style={{ 
                height: '53px',
                width: '53px',
                filter: 'brightness(0) contrast(1.2)',
                imageRendering: '-webkit-optimize-contrast',
                WebkitFontSmoothing: 'antialiased'
              }} 
            />
            <h1 className="text-2xl font-black text-black tracking-tight">Business Suite</h1>
          </div>
          <p className="text-xs text-gray-700 font-medium ml-15">Professional Management Platform</p>
        </div>
        <div className="p-4 flex-1 overflow-y-auto">
          <nav className="space-y-3">
            {filteredMenuGroups.map((group) => (
              <div key={group.key} className="space-y-1">
                {/* Group Header */}
                <button
                  onClick={() => toggleGroup(group.key)}
                  className="w-full flex items-center justify-between px-3 py-2 text-xs font-bold text-gray-600 hover:text-gray-800 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    {group.icon}
                    <span>{group.label}</span>
                  </div>
                  <ChevronRight 
                    className={`h-4 w-4 transition-transform duration-200 ${
                      expandedGroups[group.key] ? 'rotate-90' : ''
                    }`}
                  />
                </button>
                
                {/* Group Items */}
                {expandedGroups[group.key] && (
                  <div className="space-y-1 ml-2">
                    {group.items.map((item) => (
                      <button
                        key={item.path}
                        onClick={() => handleNavClick(item.path)}
                        className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg transition-all duration-200 font-medium text-sm ${
                          location === item.path
                            ? 'bg-yellow-300 text-black shadow-md border-r-4 border-amber-600'
                            : 'text-gray-800 hover:bg-blue-100 hover:text-blue-700 hover:shadow-sm'
                        }`}
                        data-testid={`nav-${item.label.toLowerCase()}`}
                      >
                        {item.icon}
                        {item.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </nav>
        </div>
        {/* User info and logout */}
        <div className="p-4 border-t-2 border-yellow-200 bg-amber-50">
          <div className="text-sm text-gray-700 mb-2">
            <p className="font-semibold">Logged in as:</p>
            <p className="text-gray-600">{user.username}</p>
          </div>
          <Button 
            variant="outline" 
            onClick={handleLogout} 
            className="w-full bg-yellow-100 hover:bg-yellow-200 border-yellow-300"
          >
            Logout
          </Button>
        </div>
      </div>

      {/* Mobile Sidebar (Overlay) */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-40 bg-black bg-opacity-50" onClick={() => setMobileMenuOpen(false)}>
          <div 
            className="fixed left-0 top-16 bottom-0 w-64 bg-gradient-to-b from-amber-100 to-yellow-100 shadow-xl flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 flex-1 overflow-y-auto">
              <nav className="space-y-3">
                {filteredMenuGroups.map((group) => (
                  <div key={group.key} className="space-y-1">
                    {/* Group Header */}
                    <button
                      onClick={() => toggleGroup(group.key)}
                      className="w-full flex items-center justify-between px-3 py-2 text-xs font-bold text-gray-600 hover:text-gray-800 transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        {group.icon}
                        <span>{group.label}</span>
                      </div>
                      <ChevronRight 
                        className={`h-4 w-4 transition-transform duration-200 ${
                          expandedGroups[group.key] ? 'rotate-90' : ''
                        }`}
                      />
                    </button>
                    
                    {/* Group Items */}
                    {expandedGroups[group.key] && (
                      <div className="space-y-1 ml-2">
                        {group.items.map((item) => (
                          <button
                            key={item.path}
                            onClick={() => handleNavClick(item.path)}
                            className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg transition-all duration-200 font-medium text-sm ${
                              location === item.path
                                ? 'bg-yellow-300 text-black shadow-md border-r-4 border-amber-600'
                                : 'text-gray-800 hover:bg-blue-100 hover:text-blue-700 hover:shadow-sm'
                            }`}
                          >
                            {item.icon}
                            {item.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </nav>
            </div>
            {/* User info and logout */}
            <div className="p-4 border-t-2 border-yellow-200 bg-amber-50">
              <div className="text-sm text-gray-700 mb-2">
                <p className="font-semibold">Logged in as:</p>
                <p className="text-gray-600">{user.username}</p>
              </div>
              <Button 
                variant="outline" 
                onClick={handleLogout} 
                className="w-full bg-yellow-100 hover:bg-yellow-200 border-yellow-300"
              >
                Logout
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="lg:ml-64 pt-16 lg:pt-0 px-4 sm:px-6 lg:px-8 py-4 lg:py-8 bg-gradient-to-br from-yellow-50 to-amber-50 min-h-screen">
        <div className="max-w-7xl mx-auto">
          {children}
        </div>
      </div>
    </div>
  );
};

// Main App Component
// Forgot Password Component
const ForgotPassword = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [resetLink, setResetLink] = useState('');
  const [showLink, setShowLink] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/auth/forgot-password`, { email });
      
      if (response.data.success) {
        setResetLink(response.data.reset_link);
        setShowLink(true);
        toast.success(response.data.message);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error('Forgot password error:', error);
      toast.error('Failed to generate reset link');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-yellow-50">
      <Card className="w-full max-w-md">
        <CardHeader>
          <div className="text-center mb-4">
            <h1 className="text-3xl font-black text-black mb-2">Invoice Manager</h1>
            <p className="text-gray-600">Professional Invoice Management</p>
          </div>
          <CardTitle className="text-center">Forgot Password</CardTitle>
        </CardHeader>
        <CardContent>
          {!showLink ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your registered email"
                  required
                />
              </div>
              <Button type="submit" className="w-full">
                Generate Reset Link
              </Button>
            </form>
          ) : (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-sm text-green-800 mb-2">
                  Password reset link generated successfully!
                </p>
                <p className="text-xs text-green-700 mb-3">
                  Click the button below to reset your password:
                </p>
                <Button 
                  onClick={() => navigate(resetLink)} 
                  className="w-full bg-green-600 hover:bg-green-700"
                >
                  Reset Password Now
                </Button>
              </div>
            </div>
          )}
          <div className="mt-4 text-center">
            <button
              type="button"
              onClick={() => navigate('/login')}
              className="text-blue-600 hover:underline"
            >
              Back to Login
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Reset Password Component
const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useState(new URLSearchParams(window.location.search));
  const token = searchParams.get('token');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  useEffect(() => {
    if (!token) {
      toast.error('Invalid reset link');
      navigate('/login');
    }
  }, [token, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    try {
      const response = await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: password
      });
      
      if (response.data.success) {
        toast.success('Password reset successfully! Please login with your new password.');
        setTimeout(() => navigate('/login'), 2000);
      }
    } catch (error) {
      console.error('Reset password error:', error);
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-yellow-50">
      <Card className="w-full max-w-md">
        <CardHeader>
          <div className="text-center mb-4">
            <h1 className="text-3xl font-black text-black mb-2">Invoice Manager</h1>
            <p className="text-gray-600">Professional Invoice Management</p>
          </div>
          <CardTitle className="text-center">Reset Password</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="password">New Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter new password"
                required
              />
            </div>
            <div>
              <Label htmlFor="confirmPassword">Confirm New Password</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                required
              />
            </div>
            <Button type="submit" className="w-full">
              Reset Password
            </Button>
          </form>
          <div className="mt-4 text-center">
            <button
              type="button"
              onClick={() => navigate('/login')}
              className="text-blue-600 hover:underline"
            >
              Back to Login
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Login Component
const Login = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    // Check if user is already logged in
    const user = localStorage.getItem('user');
    if (user) {
      navigate('/');
    }
    
    // Fetch settings for logo
    fetchSettings();
  }, [navigate]);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/auth/login`, {
        email: formData.email,
        password: formData.password
      });
      
      if (response.data.success) {
        localStorage.setItem('user', JSON.stringify(response.data.user));
        toast.success(response.data.message);
        
        // Redirect based on user role
        const userRole = response.data.user.role || 'admin';
        const redirectPath = userRole === 'cashflow_only' ? '/cashflow-calculator' : '/';
        
        // Use setTimeout to ensure localStorage is set before navigation
        setTimeout(() => {
          navigate(redirectPath);
        }, 100);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error('Authentication error:', error);
      toast.error(error.response?.data?.detail || 'Invalid email or password');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-yellow-50">
      <Card className="w-full max-w-md">
        <CardHeader>
          <div className="text-center mb-4">
            {settings?.logo_url && (
              <div className="flex justify-center mb-4">
                <img 
                  src={settings.logo_url} 
                  alt="Company Logo" 
                  className="h-24 w-auto"
                />
              </div>
            )}
            <h1 className="text-3xl font-black text-black mb-2">
              {settings?.company_name || 'Invoice Manager'}
            </h1>
            <p className="text-gray-600">Professional Invoice Management</p>
          </div>
          <CardTitle className="text-center">Login</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                required
                placeholder="your.email@example.com"
              />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
                required
              />
            </div>
            <Button type="submit" className="w-full">
              Login
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const [user, setUser] = useState(localStorage.getItem('user'));
  
  useEffect(() => {
    // Listen for storage changes
    const handleStorageChange = () => {
      setUser(localStorage.getItem('user'));
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    // Also check periodically for localStorage changes (for same-tab updates)
    const interval = setInterval(() => {
      const currentUser = localStorage.getItem('user');
      if (currentUser !== user) {
        setUser(currentUser);
      }
    }, 100);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, [user]);
  
  if (!user) {
    return <Login />;
  }
  return children;
};

// Role-Based Route Component - Redirects cashflow_only users from admin pages
const RoleBasedRoute = ({ children, adminOnly = false }) => {
  const navigate = useNavigate();
  const userStr = localStorage.getItem('user');
  const user = userStr ? JSON.parse(userStr) : null;
  const userRole = user?.role || 'admin';

  useEffect(() => {
    // If this is an admin-only route and user is cashflow_only, redirect
    if (adminOnly && userRole === 'cashflow_only') {
      navigate('/cashflow-calculator');
    }
  }, [adminOnly, userRole, navigate]);

  // Don't render admin content for cashflow_only users
  if (adminOnly && userRole === 'cashflow_only') {
    return null;
  }

  return children;
};

// Cash Flow Calculator Component
// Time Tracking Component
const TimeTracking = () => {
  const [activeTab, setActiveTab] = useState('timer');
  const [clients, setClients] = useState([]);
  const [activityTypes, setActivityTypes] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [timeEntries, setTimeEntries] = useState([]);
  const [unclosedEntries, setUnclosedEntries] = useState([]);
  const [weeklySummary, setWeeklySummary] = useState([]);
  const [allEntries, setAllEntries] = useState([]);
  
  // Calendar view state
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [calendarData, setCalendarData] = useState({});
  const [selectedDate, setSelectedDate] = useState(null);
  const [dailyEntries, setDailyEntries] = useState([]);
  const [showDailyDetail, setShowDailyDetail] = useState(false);
  const [calendarClientFilter, setCalendarClientFilter] = useState('');
  
  // Filters for weekly report
  const [reportFilters, setReportFilters] = useState({
    client_id: '',
    activity_type_id: '',
    subcategory_id: '',
    start_date: '',
    end_date: ''
  });
  
  // Edit time entry dialog
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [editForm, setEditForm] = useState({
    start_time: '',
    end_time: '',
    notes: ''
  });
  
  // Timer form state
  const [timerForm, setTimerForm] = useState({
    client_id: '',
    client_name: '',
    activity_type_id: '',
    activity_type_name: '',
    subcategory_id: '',
    subcategory_name: '',
    notes: ''
  });

  // Dialog states
  const [isAddClientDialogOpen, setIsAddClientDialogOpen] = useState(false);
  const [isAddActivityDialogOpen, setIsAddActivityDialogOpen] = useState(false);
  const [isManageSubcategoriesOpen, setIsManageSubcategoriesOpen] = useState(false);
  const [isEditSubcategoryOpen, setIsEditSubcategoryOpen] = useState(false);
  const [editingSubcategory, setEditingSubcategory] = useState(null);
  
  // New client form
  const [newClientData, setNewClientData] = useState({
    name: '',
    email: '',
    phone: ''
  });

  // New activity form
  const [newActivityData, setNewActivityData] = useState({
    name: '',
    description: '',
    subcategory_id: ''
  });
  
  // Subcategory form
  const [subcategoryForm, setSubcategoryForm] = useState({
    name: '',
    description: ''
  });

  const user = JSON.parse(localStorage.getItem('user') || '{}');

  // Helper function to get current week dates
  const getCurrentWeekDates = () => {
    const now = new Date();
    const startOfWeek = new Date(now.setDate(now.getDate() - now.getDay()));
    startOfWeek.setHours(0, 0, 0, 0);
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);
    endOfWeek.setHours(23, 59, 59, 999);
    return { start: startOfWeek, end: endOfWeek };
  };

  useEffect(() => {
    // Set default date range to current week
    const { start, end } = getCurrentWeekDates();
    setReportFilters(prev => ({
      ...prev,
      start_date: start.toISOString().split('T')[0],
      end_date: end.toISOString().split('T')[0]
    }));

    fetchClients();
    fetchActivityTypes();
    fetchSubcategories();
    fetchUnclosedEntries();
    fetchAllEntries();
  }, []);

  // Fetch summary when filters change (including dates)
  useEffect(() => {
    if (reportFilters.start_date && reportFilters.end_date) {
      fetchWeeklySummary();
    }
  }, [reportFilters.start_date, reportFilters.end_date]);

  // Fetch calendar data when month or client filter changes
  useEffect(() => {
    if (activeTab === 'calendar') {
      fetchCalendarData(currentMonth);
    }
  }, [currentMonth, activeTab, calendarClientFilter]);

  const setDateRange = (rangeType) => {
    const now = new Date();
    let start, end;

    switch (rangeType) {
      case 'thisWeek':
        const weekStart = new Date(now.setDate(now.getDate() - now.getDay()));
        weekStart.setHours(0, 0, 0, 0);
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6);
        start = weekStart;
        end = weekEnd;
        break;
      
      case 'lastWeek':
        const lastWeekStart = new Date(now.setDate(now.getDate() - now.getDay() - 7));
        lastWeekStart.setHours(0, 0, 0, 0);
        const lastWeekEnd = new Date(lastWeekStart);
        lastWeekEnd.setDate(lastWeekStart.getDate() + 6);
        start = lastWeekStart;
        end = lastWeekEnd;
        break;
      
      case 'thisMonth':
        start = new Date(now.getFullYear(), now.getMonth(), 1);
        end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        break;
      
      case 'lastMonth':
        start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        end = new Date(now.getFullYear(), now.getMonth(), 0);
        break;
      
      default:
        return;
    }

    setReportFilters(prev => ({
      ...prev,
      start_date: start.toISOString().split('T')[0],
      end_date: end.toISOString().split('T')[0]
    }));
  };

  const fetchWeeklySummary = async () => {
    try {
      // Use date range from filters or default to current week
      let startDate, endDate;
      
      if (reportFilters.start_date && reportFilters.end_date) {
        // Convert date strings to ISO datetime
        startDate = new Date(reportFilters.start_date);
        startDate.setHours(0, 0, 0, 0);
        endDate = new Date(reportFilters.end_date);
        endDate.setHours(23, 59, 59, 999);
      } else {
        const { start, end } = getCurrentWeekDates();
        startDate = start;
        endDate = end;
      }

      const params = {
        user_email: user.email,
        start_date: startDate.toISOString(),
        end_date: endDate.toISOString()
      };
      
      // Add filters if set
      if (reportFilters.client_id) params.client_id = reportFilters.client_id;
      if (reportFilters.activity_type_id) params.activity_type_id = reportFilters.activity_type_id;
      if (reportFilters.subcategory_id) params.subcategory_id = reportFilters.subcategory_id;

      const response = await axios.get(`${API}/time-entries/reports/weekly-summary`, { params });
      setWeeklySummary(response.data);
    } catch (error) {
      console.error('Error fetching weekly summary:', error);
    }
  };

  const fetchAllEntries = async () => {
    try {
      const response = await axios.get(`${API}/time-entries`, {
        params: { user_email: user.email }
      });
      setAllEntries(response.data);
    } catch (error) {
      console.error('Error fetching time entries:', error);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API}/customers`);
      setClients(response.data);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const fetchActivityTypes = async () => {
    try {
      const response = await axios.get(`${API}/activity-types`);
      setActivityTypes(response.data);
    } catch (error) {
      console.error('Error fetching activity types:', error);
    }
  };

  const fetchSubcategories = async () => {
    try {
      const response = await axios.get(`${API}/subcategories`);
      console.log('Subcategories fetched:', response.data);
      setSubcategories(response.data);
    } catch (error) {
      console.error('Error fetching subcategories:', error);
    }
  };

  const fetchCalendarData = async (month) => {
    try {
      // Get first and last day of the month
      const firstDay = new Date(month.getFullYear(), month.getMonth(), 1);
      const lastDay = new Date(month.getFullYear(), month.getMonth() + 1, 0);
      
      firstDay.setHours(0, 0, 0, 0);
      lastDay.setHours(23, 59, 59, 999);

      const response = await axios.get(`${API}/time-entries`, {
        params: { user_email: user.email }
      });
      
      const entries = response.data;
      
      // Process entries to group by date and subcategory
      const dataByDate = {};
      
      entries.forEach(entry => {
        if (!entry.is_closed || !entry.end_time) return;
        
        // Apply client filter if set
        if (calendarClientFilter && entry.client_id !== calendarClientFilter) return;
        
        const entryDate = new Date(entry.start_time);
        const dateKey = entryDate.toISOString().split('T')[0];
        
        // Check if entry is in current month
        if (entryDate >= firstDay && entryDate <= lastDay) {
          if (!dataByDate[dateKey]) {
            dataByDate[dateKey] = {
              Billable: 0,
              Unbillable: 0,
              Personal: 0,
              Uncategorized: 0
            };
          }
          
          // Calculate duration
          const start = new Date(entry.start_time);
          const end = new Date(entry.end_time);
          const hours = (end - start) / (1000 * 60 * 60);
          
          const subcatName = entry.subcategory_name || 'Uncategorized';
          if (dataByDate[dateKey][subcatName] !== undefined) {
            dataByDate[dateKey][subcatName] += hours;
          } else {
            dataByDate[dateKey].Uncategorized += hours;
          }
        }
      });
      
      setCalendarData(dataByDate);
    } catch (error) {
      console.error('Error fetching calendar data:', error);
    }
  };

  const fetchDailyEntries = async (date) => {
    try {
      const startOfDay = new Date(date);
      startOfDay.setHours(0, 0, 0, 0);
      const endOfDay = new Date(date);
      endOfDay.setHours(23, 59, 59, 999);

      const response = await axios.get(`${API}/time-entries`, {
        params: { user_email: user.email }
      });
      
      const entries = response.data.filter(entry => {
        const entryDate = new Date(entry.start_time);
        const dateMatch = entryDate >= startOfDay && entryDate <= endOfDay;
        // Apply client filter if set
        const clientMatch = !calendarClientFilter || entry.client_id === calendarClientFilter;
        return dateMatch && clientMatch;
      });

      setDailyEntries(entries);
    } catch (error) {
      console.error('Error fetching daily entries:', error);
    }
  };

  const handleDateClick = (date) => {
    setSelectedDate(date);
    fetchDailyEntries(date);
    setShowDailyDetail(true);
  };

  const fetchUnclosedEntries = async () => {
    try {
      const response = await axios.get(`${API}/time-entries/reports/daily-unclosed`, {
        params: { user_email: user.email }
      });
      setUnclosedEntries(response.data);
    } catch (error) {
      console.error('Error fetching unclosed entries:', error);
    }
  };

  const handleStartTimer = async () => {
    if (!timerForm.client_id || !timerForm.activity_type_id) {
      toast.error('Please select both Client and Activity');
      return;
    }

    try {
      const now = new Date().toISOString();
      await axios.post(`${API}/time-entries`, {
        user_email: user.email,
        client_id: timerForm.client_id,
        client_name: timerForm.client_name,
        activity_type_id: timerForm.activity_type_id,
        activity_type_name: timerForm.activity_type_name,
        subcategory_id: timerForm.subcategory_id || null,
        subcategory_name: timerForm.subcategory_name || null,
        start_time: now,
        notes: timerForm.notes
      });
      
      toast.success('Timer started!');
      setTimerForm({ 
        client_id: '', 
        client_name: '', 
        activity_type_id: '', 
        activity_type_name: '', 
        subcategory_id: '',
        subcategory_name: '',
        notes: '' 
      });
      fetchUnclosedEntries();
    } catch (error) {
      console.error('Error starting timer:', error);
      toast.error('Failed to start timer');
    }
  };

  const handleStopTimer = async (entryId) => {
    try {
      await axios.put(`${API}/time-entries/${entryId}/close`);
      toast.success('Timer stopped!');
      fetchUnclosedEntries();
    } catch (error) {
      console.error('Error stopping timer:', error);
      toast.error('Failed to stop timer');
    }
  };

  const handleAddClient = async () => {
    if (!newClientData.name) {
      toast.error('Client name is required');
      return;
    }

    try {
      const response = await axios.post(`${API}/customers`, newClientData);
      toast.success('Client added successfully!');
      
      // Refresh clients list
      await fetchClients();
      
      // Auto-select the newly created client
      setTimerForm(prev => ({
        ...prev,
        client_id: response.data.id,
        client_name: response.data.name
      }));
      
      // Reset form and close dialog
      setNewClientData({ name: '', email: '', phone: '' });
      setIsAddClientDialogOpen(false);
    } catch (error) {
      console.error('Error adding client:', error);
      toast.error('Failed to add client');
    }
  };

  const handleAddActivity = async () => {
    if (!newActivityData.name) {
      toast.error('Activity name is required');
      return;
    }

    try {
      const response = await axios.post(`${API}/activity-types`, newActivityData);
      toast.success('Activity type added successfully!');
      
      // Refresh activity types list
      await fetchActivityTypes();
      
      // Auto-select the newly created activity and its subcategory
      setTimerForm(prev => ({
        ...prev,
        activity_type_id: response.data.id,
        activity_type_name: response.data.name,
        subcategory_id: response.data.subcategory_id || '',
        subcategory_name: response.data.subcategory_name || ''
      }));
      
      // Reset form and close dialog
      setNewActivityData({ name: '', description: '', subcategory_id: '' });
      setIsAddActivityDialogOpen(false);
    } catch (error) {
      console.error('Error adding activity type:', error);
      toast.error('Failed to add activity type');
    }
  };

  const handleAddSubcategory = async () => {
    if (!subcategoryForm.name) {
      toast.error('Subcategory name is required');
      return;
    }

    try {
      await axios.post(`${API}/subcategories`, subcategoryForm);
      toast.success('Subcategory added successfully!');
      await fetchSubcategories();
      setSubcategoryForm({ name: '', description: '' });
    } catch (error) {
      console.error('Error adding subcategory:', error);
      toast.error('Failed to add subcategory');
    }
  };

  const handleEditSubcategory = async () => {
    if (!subcategoryForm.name) {
      toast.error('Subcategory name is required');
      return;
    }

    try {
      await axios.put(`${API}/subcategories/${editingSubcategory.id}`, subcategoryForm);
      toast.success('Subcategory updated successfully!');
      await fetchSubcategories();
      await fetchActivityTypes(); // Refresh activities to get updated subcategory names
      setSubcategoryForm({ name: '', description: '' });
      setEditingSubcategory(null);
      setIsEditSubcategoryOpen(false);
    } catch (error) {
      console.error('Error updating subcategory:', error);
      toast.error('Failed to update subcategory');
    }
  };

  const handleDeleteSubcategory = async (id) => {
    if (!window.confirm('Are you sure you want to delete this subcategory? This will fail if any activities are using it.')) {
      return;
    }

    try {
      await axios.delete(`${API}/subcategories/${id}`);
      toast.success('Subcategory deleted successfully!');
      await fetchSubcategories();
    } catch (error) {
      console.error('Error deleting subcategory:', error);
      if (error.response && error.response.status === 400) {
        toast.error(error.response.data.detail || 'Cannot delete subcategory with existing activities');
      } else {
        toast.error('Failed to delete subcategory');
      }
    }
  };

  const openEditSubcategoryDialog = (subcategory) => {
    setEditingSubcategory(subcategory);
    setSubcategoryForm({
      name: subcategory.name,
      description: subcategory.description || ''
    });
    setIsEditSubcategoryOpen(true);
  };

  const handleEditEntry = (entry) => {
    setEditingEntry(entry);
    setEditForm({
      start_time: entry.start_time ? new Date(entry.start_time).toISOString().slice(0, 16) : '',
      end_time: entry.end_time ? new Date(entry.end_time).toISOString().slice(0, 16) : '',
      notes: entry.notes || ''
    });
    setIsEditDialogOpen(true);
  };

  const handleUpdateEntry = async () => {
    try {
      const updateData = {
        start_time: new Date(editForm.start_time).toISOString(),
        end_time: editForm.end_time ? new Date(editForm.end_time).toISOString() : null,
        notes: editForm.notes,
        is_closed: !!editForm.end_time
      };

      await axios.put(`${API}/time-entries/${editingEntry.id}`, updateData);
      toast.success('Time entry updated!');
      
      setIsEditDialogOpen(false);
      fetchUnclosedEntries();
      fetchAllEntries();
      fetchWeeklySummary();
    } catch (error) {
      console.error('Error updating entry:', error);
      toast.error('Failed to update entry');
    }
  };

  const handleDeleteEntry = async (entryId) => {
    if (!window.confirm('Are you sure you want to delete this time entry?')) return;
    
    try {
      await axios.delete(`${API}/time-entries/${entryId}`);
      toast.success('Time entry deleted!');
      
      fetchUnclosedEntries();
      fetchAllEntries();
      fetchWeeklySummary();
    } catch (error) {
      console.error('Error deleting entry:', error);
      toast.error('Failed to delete entry');
    }
  };

  const calculateDuration = (startTime, endTime) => {
    const start = new Date(startTime);
    const end = endTime ? new Date(endTime) : new Date();
    const durationMs = end - start;
    const hours = Math.floor(durationMs / 1000 / 60 / 60);
    const minutes = Math.floor((durationMs / 1000 / 60) % 60);
    return { hours, minutes, total: durationMs / 1000 / 60 / 60 };
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-4xl font-black text-black mb-2 tracking-tight">Time Tracking</h1>
        <p className="text-gray-700 font-medium">Track billable hours and manage activities</p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('timer')}
            className={`${
              activeTab === 'timer'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            Start Timer
          </button>
          <button
            onClick={() => setActiveTab('active')}
            className={`${
              activeTab === 'active'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            Active Timers ({unclosedEntries.length})
          </button>
          <button
            onClick={() => setActiveTab('report')}
            className={`${
              activeTab === 'report'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            Weekly Report
          </button>
          <button
            onClick={() => setActiveTab('calendar')}
            className={`${
              activeTab === 'calendar'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            Calendar
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`${
              activeTab === 'history'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            All Entries
          </button>
        </nav>
      </div>

      {/* Timer Tab */}
      {activeTab === 'timer' && (
        <Card>
          <CardHeader>
            <CardTitle>Start New Timer</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="flex justify-between items-center mb-2">
                  <Label>Client *</Label>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setIsAddClientDialogOpen(true)}
                    className="h-6 text-xs"
                  >
                    <Plus className="h-3 w-3 mr-1" /> Add New
                  </Button>
                </div>
                <select
                  className="w-full rounded-md border border-gray-300 p-2"
                  value={timerForm.client_id}
                  onChange={(e) => {
                    const selectedClient = clients.find(c => c.id === e.target.value);
                    setTimerForm(prev => ({
                      ...prev,
                      client_id: e.target.value,
                      client_name: selectedClient ? selectedClient.name : ''
                    }));
                  }}
                >
                  <option value="">Select Client</option>
                  {clients.map(client => (
                    <option key={client.id} value={client.id}>{client.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <div className="flex justify-between items-center mb-2">
                  <Label>Activity *</Label>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setIsAddActivityDialogOpen(true)}
                    className="h-6 text-xs"
                  >
                    <Plus className="h-3 w-3 mr-1" /> Add New
                  </Button>
                </div>
                <select
                  className="w-full rounded-md border border-gray-300 p-2"
                  value={timerForm.activity_type_id}
                  onChange={(e) => {
                    const selectedActivity = activityTypes.find(a => a.id === e.target.value);
                    setTimerForm(prev => ({
                      ...prev,
                      activity_type_id: e.target.value,
                      activity_type_name: selectedActivity ? selectedActivity.name : '',
                      subcategory_id: selectedActivity?.subcategory_id || '',
                      subcategory_name: selectedActivity?.subcategory_name || ''
                    }));
                  }}
                >
                  <option value="">Select Activity</option>
                  {activityTypes.map(activity => (
                    <option key={activity.id} value={activity.id}>
                      {activity.name} {activity.subcategory_name ? `(${activity.subcategory_name})` : ''}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <div className="flex justify-between items-center mb-2">
                <Label>Subcategory (optional)</Label>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsManageSubcategoriesOpen(true)}
                  className="h-6 text-xs"
                >
                  <Settings className="h-3 w-3 mr-1" /> Manage
                </Button>
              </div>
              <select
                className="w-full rounded-md border border-gray-300 p-2"
                value={timerForm.subcategory_id}
                onChange={(e) => {
                  const selectedSubcat = subcategories.find(s => s.id === e.target.value);
                  setTimerForm(prev => ({
                    ...prev,
                    subcategory_id: e.target.value,
                    subcategory_name: selectedSubcat ? selectedSubcat.name : ''
                  }));
                }}
              >
                <option value="">None</option>
                {subcategories.map(subcat => (
                  <option key={subcat.id} value={subcat.id}>{subcat.name}</option>
                ))}
              </select>
            </div>
            <div>
              <Label>Notes (optional)</Label>
              <Textarea
                value={timerForm.notes}
                onChange={(e) => setTimerForm(prev => ({ ...prev, notes: e.target.value }))}
                rows={3}
                placeholder="Add notes about this activity..."
              />
            </div>
            <Button onClick={handleStartTimer} className="w-full" size="lg">
              <Clock className="mr-2 h-5 w-5" /> Start Timer
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Active Timers Tab */}
      {activeTab === 'active' && (
        <Card>
          <CardHeader>
            <CardTitle>Active Timers</CardTitle>
          </CardHeader>
          <CardContent>
            {unclosedEntries.length > 0 ? (
              <div className="space-y-3">
                {unclosedEntries.map(entry => {
                  const startTime = new Date(entry.start_time);
                  const duration = Math.floor((new Date() - startTime) / 1000 / 60); // minutes
                  
                  return (
                    <div key={entry.id} className="border rounded-lg p-4 bg-gray-50">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <h3 className="font-semibold text-lg">{entry.client_name}</h3>
                          <p className="text-sm text-gray-600">{entry.activity_type_name}</p>
                          <p className="text-xs text-gray-500 mt-1">
                            Started: {startTime.toLocaleString()}
                          </p>
                          <p className="text-sm font-medium text-blue-600 mt-2">
                            Duration: {Math.floor(duration / 60)}h {duration % 60}m
                          </p>
                          {entry.notes && (
                            <p className="text-sm text-gray-600 mt-2">{entry.notes}</p>
                          )}
                        </div>
                        <Button
                          onClick={() => handleStopTimer(entry.id)}
                          variant="destructive"
                          size="sm"
                        >
                          Stop Timer
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <Clock className="mx-auto h-16 w-16 mb-4 text-gray-400" />
                <p className="text-lg mb-2">No active timers</p>
                <p className="text-sm">Start a timer from the "Start Timer" tab</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Weekly Report Tab */}
      {activeTab === 'report' && (
        <div className="space-y-4">
          {/* Filters */}
          <Card>
            <CardHeader>
              <CardTitle>Report Filters</CardTitle>
            </CardHeader>
            <CardContent>
              {/* Date Range */}
              <div className="mb-4">
                <Label className="mb-2 block font-semibold">Date Range</Label>
                <div className="grid grid-cols-2 gap-4 mb-3">
                  <div>
                    <Label className="text-xs text-gray-600">Start Date</Label>
                    <input
                      type="date"
                      className="w-full rounded-md border border-gray-300 p-2"
                      value={reportFilters.start_date}
                      onChange={(e) => setReportFilters(prev => ({ ...prev, start_date: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-600">End Date</Label>
                    <input
                      type="date"
                      className="w-full rounded-md border border-gray-300 p-2"
                      value={reportFilters.end_date}
                      onChange={(e) => setReportFilters(prev => ({ ...prev, end_date: e.target.value }))}
                    />
                  </div>
                </div>
                {/* Quick Date Range Buttons */}
                <div className="flex gap-2 flex-wrap">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setDateRange('thisWeek')}
                  >
                    This Week
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setDateRange('lastWeek')}
                  >
                    Last Week
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setDateRange('thisMonth')}
                  >
                    This Month
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setDateRange('lastMonth')}
                  >
                    Last Month
                  </Button>
                </div>
              </div>

              {/* Other Filters */}
              <div className="border-t pt-4">
                <Label className="mb-2 block font-semibold">Additional Filters</Label>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label className="text-xs text-gray-600">Filter by Client</Label>
                    <select
                      className="w-full rounded-md border border-gray-300 p-2"
                      value={reportFilters.client_id}
                      onChange={(e) => {
                        setReportFilters(prev => ({ ...prev, client_id: e.target.value }));
                      }}
                    >
                      <option value="">All Clients</option>
                      {clients.map(client => (
                        <option key={client.id} value={client.id}>{client.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label className="text-xs text-gray-600">Filter by Activity</Label>
                    <select
                      className="w-full rounded-md border border-gray-300 p-2"
                      value={reportFilters.activity_type_id}
                      onChange={(e) => {
                        setReportFilters(prev => ({ ...prev, activity_type_id: e.target.value }));
                      }}
                    >
                      <option value="">All Activities</option>
                      {activityTypes.map(activity => (
                        <option key={activity.id} value={activity.id}>{activity.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label className="text-xs text-gray-600">Filter by Subcategory</Label>
                    <select
                      className="w-full rounded-md border border-gray-300 p-2"
                      value={reportFilters.subcategory_id}
                      onChange={(e) => {
                        setReportFilters(prev => ({ ...prev, subcategory_id: e.target.value }));
                      }}
                    >
                      <option value="">All Subcategories</option>
                      {subcategories.map(subcat => (
                        <option key={subcat.id} value={subcat.id}>{subcat.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
              
              <div className="flex gap-2 mt-4">
                <Button onClick={fetchWeeklySummary}>Apply Filters</Button>
                <Button 
                  variant="outline" 
                  onClick={() => {
                    const { start, end } = getCurrentWeekDates();
                    setReportFilters({ 
                      client_id: '', 
                      activity_type_id: '', 
                      subcategory_id: '',
                      start_date: start.toISOString().split('T')[0],
                      end_date: end.toISOString().split('T')[0]
                    });
                  }}
                >
                  Clear Filters
                </Button>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-r from-blue-50 to-purple-50">
            <CardHeader>
              <CardTitle>
                Billable Hours Report
                {reportFilters.start_date && reportFilters.end_date && (
                  <span className="text-base font-normal text-gray-600 ml-2">
                    ({new Date(reportFilters.start_date).toLocaleDateString()} - {new Date(reportFilters.end_date).toLocaleDateString()})
                  </span>
                )}
              </CardTitle>
              <p className="text-sm text-gray-600">Summary by client and activity</p>
            </CardHeader>
            <CardContent>
              {weeklySummary.length > 0 ? (
                <div className="space-y-6">
                  {weeklySummary.map((clientData, idx) => {
                    const totalHours = clientData.total_hours.toFixed(2);
                    return (
                      <div key={idx} className="border rounded-lg p-4 bg-white">
                        {/* Client Header */}
                        <div className="flex justify-between items-center mb-4 pb-3 border-b-2 border-blue-200">
                          <h3 className="text-xl font-bold text-blue-600">{clientData.client_name}</h3>
                          <Badge className="bg-blue-600 text-white text-lg px-3 py-1">
                            {totalHours} hours
                          </Badge>
                        </div>
                        
                        {/* Subcategories */}
                        <div className="space-y-4">
                          {clientData.subcategories.map((subcatData, subcatIdx) => {
                            const subcatColors = {
                              'Billable': 'border-green-500 bg-green-50',
                              'Unbillable': 'border-orange-500 bg-orange-50',
                              'Personal': 'border-purple-500 bg-purple-50',
                              'Uncategorized': 'border-gray-400 bg-gray-50'
                            };
                            const subcatColor = subcatColors[subcatData.subcategory_name] || 'border-gray-400 bg-gray-50';
                            
                            return (
                              <div key={subcatIdx} className={`border-l-4 ${subcatColor} rounded-r-lg p-3`}>
                                {/* Subcategory Header */}
                                <div className="flex justify-between items-center mb-2">
                                  <h4 className="text-md font-bold text-gray-800">{subcatData.subcategory_name}</h4>
                                  <span className="text-sm font-semibold text-gray-600">
                                    {subcatData.total_hours.toFixed(2)}h
                                  </span>
                                </div>
                                
                                {/* Activities under this subcategory */}
                                <div className="space-y-1 ml-3">
                                  {subcatData.activities.map((activity, actIdx) => (
                                    <div key={actIdx} className="flex justify-between items-center py-1">
                                      <div>
                                        <span className="font-medium text-gray-700">{activity.activity_name}</span>
                                        <span className="text-xs text-gray-500 ml-2">({activity.count} entries)</span>
                                      </div>
                                      <span className="font-semibold text-gray-700">{activity.hours.toFixed(2)}h</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                  <div className="border-t-2 border-gray-300 pt-4">
                    <div className="flex justify-between items-center">
                      <h2 className="text-2xl font-black">Total Billable Hours</h2>
                      <h2 className="text-3xl font-black text-green-600">
                        {weeklySummary.reduce((sum, c) => sum + c.total_hours, 0).toFixed(2)} hours
                      </h2>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <FileText className="mx-auto h-16 w-16 mb-4 text-gray-400" />
                  <p className="text-lg mb-2">No billable hours this week</p>
                  <p className="text-sm">Start and complete timers to see your weekly summary</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Calendar View Tab */}
      {activeTab === 'calendar' && (
        <div className="space-y-4">
          {/* Month Navigation and Filters */}
          <Card>
            <CardContent className="py-4">
              <div className="flex justify-between items-center mb-4">
                <Button
                  variant="outline"
                  onClick={() => {
                    const newMonth = new Date(currentMonth);
                    newMonth.setMonth(newMonth.getMonth() - 1);
                    setCurrentMonth(newMonth);
                  }}
                >
                  &larr; Previous Month
                </Button>
                <h2 className="text-2xl font-bold">
                  {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                </h2>
                <Button
                  variant="outline"
                  onClick={() => {
                    const newMonth = new Date(currentMonth);
                    newMonth.setMonth(newMonth.getMonth() + 1);
                    setCurrentMonth(newMonth);
                  }}
                >
                  Next Month &rarr;
                </Button>
              </div>
              
              {/* Client Filter */}
              <div className="border-t pt-4">
                <div className="flex items-center gap-4">
                  <Label className="font-semibold">Filter by Client:</Label>
                  <select
                    className="flex-1 max-w-md rounded-md border border-gray-300 p-2"
                    value={calendarClientFilter}
                    onChange={(e) => setCalendarClientFilter(e.target.value)}
                  >
                    <option value="">All Clients</option>
                    {clients.map(client => (
                      <option key={client.id} value={client.id}>{client.name}</option>
                    ))}
                  </select>
                  {calendarClientFilter && (
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setCalendarClientFilter('')}
                    >
                      Clear Filter
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Calendar Grid */}
          <Card>
            <CardContent className="p-4">
              {/* Calendar */}
              <div className="grid grid-cols-7 gap-2">
                {/* Day Headers */}
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                  <div key={day} className="text-center font-bold text-sm text-gray-700 py-2">
                    {day}
                  </div>
                ))}
                
                {/* Calendar Days */}
                {(() => {
                  const firstDay = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
                  const lastDay = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);
                  const startingDayOfWeek = firstDay.getDay();
                  const daysInMonth = lastDay.getDate();
                  
                  const days = [];
                  
                  // Empty cells for days before month starts
                  for (let i = 0; i < startingDayOfWeek; i++) {
                    days.push(<div key={`empty-${i}`} className="h-24 border rounded-lg bg-gray-50"></div>);
                  }
                  
                  // Days of the month
                  for (let day = 1; day <= daysInMonth; day++) {
                    const dateKey = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day).toISOString().split('T')[0];
                    const dayData = calendarData[dateKey] || {};
                    const hasData = Object.values(dayData).some(val => val > 0);
                    
                    days.push(
                      <div
                        key={day}
                        onClick={() => hasData && handleDateClick(new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day))}
                        className={`h-24 border rounded-lg p-2 ${hasData ? 'cursor-pointer hover:bg-blue-50 hover:border-blue-300' : 'bg-white'}`}
                      >
                        <div className="font-semibold text-sm text-gray-700 mb-1">{day}</div>
                        {hasData && (
                          <div className="text-xs space-y-0.5">
                            {dayData.Billable > 0 && (
                              <div className="text-green-700 font-medium">B={dayData.Billable.toFixed(1)}</div>
                            )}
                            {dayData.Unbillable > 0 && (
                              <div className="text-orange-600 font-medium">U={dayData.Unbillable.toFixed(1)}</div>
                            )}
                            {dayData.Personal > 0 && (
                              <div className="text-purple-600 font-medium">P={dayData.Personal.toFixed(1)}</div>
                            )}
                            {dayData.Uncategorized > 0 && (
                              <div className="text-gray-500 font-medium">?={dayData.Uncategorized.toFixed(1)}</div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  }
                  
                  return days;
                })()}
              </div>
              
              {/* Legend */}
              <div className="mt-4 pt-4 border-t">
                <div className="flex gap-6 text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-green-700 rounded"></div>
                    <span>B = Billable</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-orange-600 rounded"></div>
                    <span>U = Unbillable</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-purple-600 rounded"></div>
                    <span>P = Personal</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-gray-500 rounded"></div>
                    <span>? = Uncategorized</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Daily Detail Dialog */}
      <Dialog open={showDailyDetail} onOpenChange={setShowDailyDetail}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Time Entries for {selectedDate && selectedDate.toLocaleDateString('en-US', { 
                weekday: 'long', 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
              {calendarClientFilter && (
                <span className="text-base font-normal text-gray-600 ml-2">
                  - {clients.find(c => c.id === calendarClientFilter)?.name || 'Filtered'}
                </span>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {dailyEntries.length > 0 ? (
              <>
                {/* Summary by Subcategory */}
                <div className="grid grid-cols-4 gap-4">
                  {(() => {
                    const summary = { Billable: 0, Unbillable: 0, Personal: 0, Uncategorized: 0 };
                    dailyEntries.forEach(entry => {
                      if (entry.is_closed && entry.end_time) {
                        const start = new Date(entry.start_time);
                        const end = new Date(entry.end_time);
                        const hours = (end - start) / (1000 * 60 * 60);
                        const subcat = entry.subcategory_name || 'Uncategorized';
                        if (summary[subcat] !== undefined) {
                          summary[subcat] += hours;
                        } else {
                          summary.Uncategorized += hours;
                        }
                      }
                    });
                    
                    return Object.entries(summary).map(([name, hours]) => {
                      if (hours === 0) return null;
                      const colors = {
                        Billable: 'bg-green-100 text-green-800',
                        Unbillable: 'bg-orange-100 text-orange-800',
                        Personal: 'bg-purple-100 text-purple-800',
                        Uncategorized: 'bg-gray-100 text-gray-800'
                      };
                      return (
                        <div key={name} className={`${colors[name]} rounded-lg p-4`}>
                          <div className="text-sm font-medium">{name}</div>
                          <div className="text-2xl font-bold">{hours.toFixed(2)}h</div>
                        </div>
                      );
                    });
                  })()}
                </div>

                {/* Detailed Entries */}
                <div className="space-y-3">
                  {dailyEntries.map(entry => {
                    const duration = calculateDuration(entry.start_time, entry.end_time);
                    return (
                      <div key={entry.id} className="border rounded-lg p-4 bg-white">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <h4 className="font-bold text-lg text-blue-600">{entry.client_name}</h4>
                            <p className="text-sm text-gray-600">{entry.activity_type_name}</p>
                          </div>
                          <div className="flex gap-2 items-center">
                            {entry.subcategory_name && (
                              <Badge variant="outline">{entry.subcategory_name}</Badge>
                            )}
                            {entry.is_closed ? (
                              <Badge className="bg-green-600">Closed</Badge>
                            ) : (
                              <Badge variant="outline" className="bg-amber-50 text-amber-700">Active</Badge>
                            )}
                          </div>
                        </div>
                        <div className="grid grid-cols-3 gap-4 text-sm">
                          <div>
                            <span className="text-gray-500">Start:</span> {new Date(entry.start_time).toLocaleTimeString()}
                          </div>
                          <div>
                            <span className="text-gray-500">End:</span> {entry.end_time ? new Date(entry.end_time).toLocaleTimeString() : '-'}
                          </div>
                          <div>
                            <span className="text-gray-500">Duration:</span> {duration.hours}h {duration.minutes}m
                          </div>
                        </div>
                        {entry.notes && (
                          <div className="mt-2 text-sm text-gray-600">
                            <span className="font-medium">Notes:</span> {entry.notes}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <Calendar className="mx-auto h-16 w-16 mb-4 text-gray-400" />
                <p className="text-lg">No entries for this day</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDailyDetail(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* All Entries Tab */}
      {activeTab === 'history' && (
        <Card>
          <CardHeader>
            <CardTitle>All Time Entries</CardTitle>
          </CardHeader>
          <CardContent>
            {allEntries.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700">Client</th>
                      <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700">Activity</th>
                      <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700">Subcategory</th>
                      <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700">Start</th>
                      <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700">End</th>
                      <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700">Duration</th>
                      <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700">Status</th>
                      <th className="px-4 py-2 text-right text-xs font-semibold text-gray-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allEntries.map(entry => {
                      const duration = calculateDuration(entry.start_time, entry.end_time);
                      return (
                        <tr key={entry.id} className="border-b hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm font-medium">{entry.client_name}</td>
                          <td className="px-4 py-3 text-sm">{entry.activity_type_name}</td>
                          <td className="px-4 py-3 text-sm">
                            {entry.subcategory_name ? (
                              <Badge variant="outline" className="text-xs">{entry.subcategory_name}</Badge>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm">{new Date(entry.start_time).toLocaleString()}</td>
                          <td className="px-4 py-3 text-sm">
                            {entry.end_time ? new Date(entry.end_time).toLocaleString() : '-'}
                          </td>
                          <td className="px-4 py-3 text-sm font-semibold">
                            {duration.hours}h {duration.minutes}m
                          </td>
                          <td className="px-4 py-3">
                            {entry.is_closed ? (
                              <Badge className="bg-green-600">Closed</Badge>
                            ) : (
                              <Badge variant="outline" className="bg-amber-50 text-amber-700">Active</Badge>
                            )}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <div className="flex gap-1 justify-end">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEditEntry(entry)}
                                className="h-8 w-8 p-0"
                              >
                                <Edit className="h-3 w-3" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteEntry(entry.id)}
                                className="h-8 w-8 p-0"
                              >
                                <Trash2 className="h-3 w-3 text-red-500" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <Clock className="mx-auto h-16 w-16 mb-4 text-gray-400" />
                <p className="text-lg mb-2">No time entries yet</p>
                <p className="text-sm">Start tracking your time to see entries here</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Edit Time Entry Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Time Entry</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="edit_start">Start Time *</Label>
              <Input
                id="edit_start"
                type="datetime-local"
                value={editForm.start_time}
                onChange={(e) => setEditForm(prev => ({ ...prev, start_time: e.target.value }))}
              />
            </div>
            <div>
              <Label htmlFor="edit_end">End Time</Label>
              <Input
                id="edit_end"
                type="datetime-local"
                value={editForm.end_time}
                onChange={(e) => setEditForm(prev => ({ ...prev, end_time: e.target.value }))}
              />
              <p className="text-xs text-gray-500 mt-1">Leave empty to keep timer active</p>
            </div>
            <div>
              <Label htmlFor="edit_notes">Notes</Label>
              <Textarea
                id="edit_notes"
                value={editForm.notes}
                onChange={(e) => setEditForm(prev => ({ ...prev, notes: e.target.value }))}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleUpdateEntry}>Update Entry</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add New Client Dialog */}
      <Dialog open={isAddClientDialogOpen} onOpenChange={setIsAddClientDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Client</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="new_client_name">Client Name *</Label>
              <Input
                id="new_client_name"
                value={newClientData.name}
                onChange={(e) => setNewClientData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Company or Person Name"
              />
            </div>
            <div>
              <Label htmlFor="new_client_email">Email</Label>
              <Input
                id="new_client_email"
                type="email"
                value={newClientData.email}
                onChange={(e) => setNewClientData(prev => ({ ...prev, email: e.target.value }))}
                placeholder="client@example.com"
              />
            </div>
            <div>
              <Label htmlFor="new_client_phone">Phone</Label>
              <Input
                id="new_client_phone"
                value={newClientData.phone}
                onChange={(e) => setNewClientData(prev => ({ ...prev, phone: e.target.value }))}
                placeholder="+1 234 567 8900"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setNewClientData({ name: '', email: '', phone: '' });
              setIsAddClientDialogOpen(false);
            }}>
              Cancel
            </Button>
            <Button onClick={handleAddClient}>Add Client</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add New Activity Dialog */}
      <Dialog open={isAddActivityDialogOpen} onOpenChange={setIsAddActivityDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Activity Type</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="new_activity_name">Activity Name *</Label>
              <Input
                id="new_activity_name"
                value={newActivityData.name}
                onChange={(e) => setNewActivityData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Meeting, Development, Consultation"
              />
            </div>
            <div>
              <Label htmlFor="new_activity_description">Description (optional)</Label>
              <Textarea
                id="new_activity_description"
                value={newActivityData.description}
                onChange={(e) => setNewActivityData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Brief description of this activity type"
                rows={3}
              />
            </div>
            <div>
              <Label htmlFor="new_activity_subcategory">Subcategory (optional)</Label>
              <select
                id="new_activity_subcategory"
                className="w-full rounded-md border border-gray-300 p-2"
                value={newActivityData.subcategory_id}
                onChange={(e) => setNewActivityData(prev => ({ ...prev, subcategory_id: e.target.value }))}
              >
                <option value="">None</option>
                {subcategories.map(subcat => (
                  <option key={subcat.id} value={subcat.id}>{subcat.name}</option>
                ))}
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setNewActivityData({ name: '', description: '', subcategory_id: '' });
              setIsAddActivityDialogOpen(false);
            }}>
              Cancel
            </Button>
            <Button onClick={handleAddActivity}>Add Activity</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manage Subcategories Dialog */}
      <Dialog open={isManageSubcategoriesOpen} onOpenChange={setIsManageSubcategoriesOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Manage Subcategories</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Add New Subcategory */}
            <div className="border rounded-lg p-4 bg-gray-50">
              <h3 className="font-semibold mb-3">Add New Subcategory</h3>
              <div className="space-y-3">
                <div>
                  <Label>Name *</Label>
                  <Input
                    value={subcategoryForm.name}
                    onChange={(e) => setSubcategoryForm(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g., Billable, Internal"
                  />
                </div>
                <div>
                  <Label>Description (optional)</Label>
                  <Input
                    value={subcategoryForm.description}
                    onChange={(e) => setSubcategoryForm(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Brief description"
                  />
                </div>
                <Button onClick={handleAddSubcategory} size="sm">
                  <Plus className="h-4 w-4 mr-1" /> Add Subcategory
                </Button>
              </div>
            </div>

            {/* Existing Subcategories */}
            <div>
              <h3 className="font-semibold mb-3">Existing Subcategories</h3>
              <div className="space-y-2">
                {subcategories.map(subcat => (
                  <div key={subcat.id} className="flex items-center justify-between border rounded p-3 bg-white">
                    <div>
                      <div className="font-medium">{subcat.name}</div>
                      {subcat.description && (
                        <div className="text-sm text-gray-500">{subcat.description}</div>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEditSubcategoryDialog(subcat)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteSubcategory(subcat.id)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsManageSubcategoriesOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Subcategory Dialog */}
      <Dialog open={isEditSubcategoryOpen} onOpenChange={setIsEditSubcategoryOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Subcategory</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Name *</Label>
              <Input
                value={subcategoryForm.name}
                onChange={(e) => setSubcategoryForm(prev => ({ ...prev, name: e.target.value }))}
              />
            </div>
            <div>
              <Label>Description (optional)</Label>
              <Input
                value={subcategoryForm.description}
                onChange={(e) => setSubcategoryForm(prev => ({ ...prev, description: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setIsEditSubcategoryOpen(false);
              setEditingSubcategory(null);
              setSubcategoryForm({ name: '', description: '' });
            }}>
              Cancel
            </Button>
            <Button onClick={handleEditSubcategory}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const CashFlowCalculator = () => {
  const [savedCalculations, setSavedCalculations] = useState([]);
  const [currentCalculationId, setCurrentCalculationId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState({
    // Basic Property Info
    location: 'VICTORIA',
    development: '',
    propertyType: 'House & Land',
    layout: '',
    propertyAddress: '',
    suburb: '',
    state: 'VIC',
    postcode: '',
    priceFrom: '',
    
    // Financial Parameters
    equityInterestRate: '',
    loanInterestRate: '',
    equityPercentage: '',
    taxRate: '',
    managementFees: '',
    rentPerWeek: '',
    
    // Initial Capital Outlay
    cashContributions: '',
    stampDuty: '',
    legalCosts: '',
    buildingInspection: '',
    otherCosts: '',
    
    // Ongoing Costs
    landlordsInsurance: '',
    ratesWater: '',
    bodyCorporate: '',
    otherMiscellaneous: '',
    
    // Tax Calculations
    depreciation: '',
  });

  const handleChange = (field, value) => {
    // Keep empty strings as empty, don't convert to 0
    setFormData(prev => ({ ...prev, [field]: value === '' ? '' : value }));
  };

  // Fetch saved calculations on mount
  useEffect(() => {
    fetchSavedCalculations();
  }, []);

  const fetchSavedCalculations = async () => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const response = await axios.get(`${API}/cashflow-calculations`, {
        params: {
          user_email: user.email,
          user_role: user.role
        }
      });
      setSavedCalculations(response.data);
    } catch (error) {
      console.error('Error fetching saved calculations:', error);
      toast.error('Failed to load saved calculations');
    }
  };

  const handleSaveCalculation = async () => {
    // Validation
    if (!formData.propertyAddress || !formData.suburb) {
      toast.error('Please enter Property Address and Suburb before saving');
      return;
    }

    setIsSaving(true);
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      
      const saveData = {
        user_email: user.email,
        property_address: formData.propertyAddress,
        suburb: formData.suburb,
        calculation_data: formData
      };

      if (currentCalculationId) {
        // Update existing
        await axios.put(`${API}/cashflow-calculations/${currentCalculationId}`, {
          property_address: formData.propertyAddress,
          suburb: formData.suburb,
          calculation_data: formData
        });
        toast.success('Calculation updated successfully');
      } else {
        // Create new
        await axios.post(`${API}/cashflow-calculations`, saveData);
        toast.success('Calculation saved successfully');
      }
      
      fetchSavedCalculations();
    } catch (error) {
      console.error('Error saving calculation:', error);
      toast.error('Failed to save calculation');
    } finally {
      setIsSaving(false);
    }
  };

  const handleLoadCalculation = async (calculationId) => {
    try {
      const response = await axios.get(`${API}/cashflow-calculations/${calculationId}`);
      const calculation = response.data;
      
      setFormData(calculation.calculation_data);
      setCurrentCalculationId(calculationId);
      toast.success(`Loaded: ${calculation.suburb} - ${calculation.property_address}`);
      
      // Scroll to top of calculator
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      console.error('Error loading calculation:', error);
      toast.error('Failed to load calculation');
    }
  };

  const handleDeleteCalculation = async (calculationId, address) => {
    if (!window.confirm(`Are you sure you want to delete calculation for ${address}?`)) return;
    
    try {
      await axios.delete(`${API}/cashflow-calculations/${calculationId}`);
      toast.success('Calculation deleted successfully');
      
      // If we're currently editing this calculation, clear it
      if (currentCalculationId === calculationId) {
        handleNewCalculation();
      }
      
      fetchSavedCalculations();
    } catch (error) {
      console.error('Error deleting calculation:', error);
      toast.error('Failed to delete calculation');
    }
  };

  const handleNewCalculation = () => {
    setFormData({
      // Basic Property Info
      location: 'VICTORIA',
      development: '',
      propertyType: 'House & Land',
      layout: '',
      propertyAddress: '',
      suburb: '',
      state: 'VIC',
      postcode: '',
      priceFrom: '',
      
      // Financial Parameters
      equityInterestRate: '',
      loanInterestRate: '',
      equityPercentage: '',
      taxRate: '',
      managementFees: '',
      rentPerWeek: '',
      
      // Initial Capital Outlay
      cashContributions: '',
      stampDuty: '',
      legalCosts: '',
      buildingInspection: '',
      otherCosts: '',
      
      // Ongoing Costs
      landlordsInsurance: '',
      ratesWater: '',
      bodyCorporate: '',
      otherMiscellaneous: '',
      
      // Tax Calculations
      depreciation: '',
    });
    setCurrentCalculationId(null);
    toast.success('New calculation started');
  };

  const generatePDFReport = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      
      // Prepare data for PDF
      const pdfData = {
        // Property info
        address1: formData.propertyAddress || '',
        address2: '',
        city: formData.suburb || '',
        state: formData.state || '',
        postcode: formData.postcode || '',
        
        // Financial parameters
        priceFrom: formData.priceFrom || 0,
        cashContributions: formData.cashContributions || 0,
        lvr: loanPercentage,
        equityInterestRate: formData.equityInterestRate || 0,
        loanInterestRate: formData.loanInterestRate || 0,
        
        // Initial capital outlay
        stampDuty: stampDuty,
        legalCosts: formData.legalCosts || 0,
        buildingInspection: formData.buildingInspection || 0,
        otherCosts: formData.otherCosts || 0,
        
        // Ongoing costs
        rentPerWeek: formData.rentPerWeek || 0,
        managementFees: formData.managementFees || 0,
        taxRate: formData.taxRate || 0,
        depreciation: formData.depreciation || 0,
        insurance: formData.insurance || 0,
        ratesWater: formData.ratesWater || 0,
        bodyCorporate: formData.bodyCorporate || 0,
        otherMiscellaneous: formData.otherMiscellaneous || 0,
      };
      
      const response = await fetch(`${backendUrl}/api/cashflow/pdf`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(pdfData),
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Cashflow_Report_${new Date().toISOString().split('T')[0]}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } else {
        console.error('Failed to generate PDF');
        alert('Failed to generate PDF report. Please try again.');
      }
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF report. Please try again.');
    }
  };


  // Calculations - handle empty strings
  const purchasePrice = parseFloat(formData.priceFrom) || 0;
  const cashContributions = parseFloat(formData.cashContributions) || 0;
  const propertyBalance = purchasePrice - cashContributions;
  const equityPercentage = parseFloat(formData.equityPercentage) || 0;
  const loanPercentage = 100 - equityPercentage;
  
  // Stamp Duty - now manual input
  const stampDuty = parseFloat(formData.stampDuty) || 0;
  
  // Equity includes: deposit percentage + cash contributions + stamp duty
  const equityDeposit = purchasePrice * (equityPercentage / 100);
  const equityContributed = equityDeposit + cashContributions + stampDuty;
  
  const legalCosts = parseFloat(formData.legalCosts) || 0;
  const buildingInspection = parseFloat(formData.buildingInspection) || 0;
  const otherCosts = parseFloat(formData.otherCosts) || 0;
  const initialCapitalOutlay = cashContributions + stampDuty + legalCosts + buildingInspection + otherCosts;
  
  // Ongoing Calculations
  const rentPerWeek = parseFloat(formData.rentPerWeek) || 0;
  const rentalIncomePA = rentPerWeek * 52;
  const debt = purchasePrice * (loanPercentage / 100);
  const equityInterestRate = parseFloat(formData.equityInterestRate) || 0;
  const loanInterestRate = parseFloat(formData.loanInterestRate) || 0;
  const managementFees = parseFloat(formData.managementFees) || 0;
  const landlordsInsurance = parseFloat(formData.landlordsInsurance) || 0;
  const ratesWater = parseFloat(formData.ratesWater) || 0;
  const bodyCorporate = parseFloat(formData.bodyCorporate) || 0;
  const otherMiscellaneous = parseFloat(formData.otherMiscellaneous) || 0;
  const depreciation = parseFloat(formData.depreciation) || 0;
  const taxRate = parseFloat(formData.taxRate) || 0;
  
  const interestOnEquity = equityContributed * (equityInterestRate / 100);
  const interestOnBankFinance = debt * (loanInterestRate / 100);
  const totalInterest = interestOnEquity + interestOnBankFinance;
  
  const agentsFees = rentalIncomePA * (managementFees / 100);
  const totalExpenses = totalInterest + landlordsInsurance + 
    ratesWater + bodyCorporate + agentsFees + otherMiscellaneous;
  
  const cashflowBeforeTax = rentalIncomePA - totalExpenses;
  const totalPaperLoss = cashflowBeforeTax - depreciation;
  const taxRefund = Math.abs(totalPaperLoss) * (taxRate / 100);
  
  const grossYield = (rentalIncomePA / purchasePrice) * 100;
  const netCashflowPA = cashflowBeforeTax + taxRefund;
  const netCashflowPerWeek = netCashflowPA / 52;
  
  // Summary breakdown
  const weeklyExpenses = totalExpenses / 52;
  const tenantPays = rentPerWeek;  // Use parsed value, not formData
  const taxmanPays = taxRefund / 52;
  const youPay = weeklyExpenses - tenantPays - taxmanPays;
  
  // For graph: if positive cashflow, base 100% on income (tenant+taxman), else on expenses
  const totalWeeklyForGraph = youPay > 0 ? weeklyExpenses : (tenantPays + taxmanPays);
  const totalWeekly = weeklyExpenses;

  // Check if mobile device
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Show desktop-only message on mobile
  if (isMobile) {
    return (
      <div className="space-y-4">
        <div>
          <h1 className="text-2xl font-black text-black mb-1 tracking-tight">Cash Flow Calculator</h1>
          <p className="text-xs text-gray-700">Property Investment Cashflow Analysis</p>
        </div>
        
        <Card className="border-2 border-yellow-300 bg-gradient-to-br from-yellow-50 to-amber-50">
          <CardHeader className="bg-gradient-to-r from-yellow-200 to-amber-200">
            <CardTitle className="text-lg font-bold flex items-center gap-2">
              <Calculator className="h-6 w-6" />
              Desktop Only Feature
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="text-center space-y-4">
              <div className="text-6xl">💻</div>
              <h3 className="text-xl font-bold text-gray-800">
                Cash Flow Calculator Requires Desktop
              </h3>
              <p className="text-gray-600">
                The Cash Flow Calculator has a complex 3-column layout that works best on desktop screens.
              </p>
              <p className="text-gray-600">
                Please access this feature from a desktop computer or tablet in landscape mode for the best experience.
              </p>
              <div className="pt-4 border-t border-yellow-200">
                <p className="text-sm text-gray-500">
                  Minimum recommended screen width: 1024px
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-black text-black mb-1 tracking-tight">Cash Flow Calculator</h1>
          <p className="text-xs text-gray-700">Property Investment Cashflow Analysis</p>
          {currentCalculationId && (
            <Badge className="mt-2 bg-blue-500">Editing: {formData.suburb} - {formData.propertyAddress}</Badge>
          )}
        </div>
        <div className="flex gap-2">
          <Button
            onClick={handleNewCalculation}
            variant="outline"
            size="sm"
            className="bg-white"
          >
            <Plus className="h-4 w-4 mr-1" /> New
          </Button>
          <Button
            onClick={handleSaveCalculation}
            disabled={isSaving}
            size="sm"
            className="bg-green-600 hover:bg-green-700"
          >
            <Save className="h-4 w-4 mr-1" /> {currentCalculationId ? 'Update' : 'Save'}
          </Button>
        </div>
      </div>

      <Card className="border-2 border-yellow-300 bg-gradient-to-br from-yellow-50 to-amber-50">
        <CardHeader className="bg-gradient-to-r from-yellow-200 to-amber-200 py-2">
          <CardTitle className="text-lg font-black">Estimated Cashflow Analysis</CardTitle>
        </CardHeader>
        <CardContent className="p-3">
          <div className="grid grid-cols-3 gap-3">
            {/* LEFT COLUMN */}
            <div className="space-y-3">
              {/* Property Info */}
              <div>
                <h3 className="text-sm font-bold text-black mb-2 border-b border-yellow-300 pb-1">Property Information</h3>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="col-span-2">
                    <Label htmlFor="propertyAddress" className="text-xs">Property Address</Label>
                    <Input
                      id="propertyAddress"
                      value={formData.propertyAddress}
                      onChange={(e) => setFormData(prev => ({ ...prev, propertyAddress: e.target.value }))}
                      className="bg-red-100 h-8 text-xs"
                      placeholder="Street address"
                    />
                  </div>
                  <div>
                    <Label htmlFor="suburb" className="text-xs">Suburb</Label>
                    <Input
                      id="suburb"
                      value={formData.suburb}
                      onChange={(e) => setFormData(prev => ({ ...prev, suburb: e.target.value }))}
                      className="bg-red-100 h-8 text-xs"
                    />
                  </div>
                  <div>
                    <Label htmlFor="state" className="text-xs">State</Label>
                    <Input
                      id="state"
                      value={formData.state}
                      onChange={(e) => setFormData(prev => ({ ...prev, state: e.target.value }))}
                      className="bg-red-100 h-8 text-xs"
                    />
                  </div>
                  <div>
                    <Label htmlFor="postcode" className="text-xs">Postcode</Label>
                    <Input
                      id="postcode"
                      value={formData.postcode}
                      onChange={(e) => setFormData(prev => ({ ...prev, postcode: e.target.value }))}
                      className="bg-red-100 h-8 text-xs"
                    />
                  </div>
                  <div>
                    <Label htmlFor="location" className="text-xs">Location</Label>
                    <Input
                      id="location"
                      value={formData.location}
                      onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
                      className="bg-red-100 h-8 text-xs"
                    />
                  </div>
                  <div>
                    <Label htmlFor="development" className="text-xs">Development</Label>
                    <Input
                      id="development"
                      value={formData.development}
                      onChange={(e) => setFormData(prev => ({ ...prev, development: e.target.value }))}
                      className="bg-red-100 h-8 text-xs"
                    />
                  </div>
                  <div>
                    <Label htmlFor="propertyType" className="text-xs">Property Type</Label>
                    <Input
                      id="propertyType"
                      value={formData.propertyType}
                      onChange={(e) => setFormData(prev => ({ ...prev, propertyType: e.target.value }))}
                      className="bg-red-100 h-8 text-xs"
                    />
                  </div>
                  <div>
                    <Label htmlFor="layout" className="text-xs">Layout</Label>
                    <Input
                      id="layout"
                      value={formData.layout}
                      onChange={(e) => setFormData(prev => ({ ...prev, layout: e.target.value }))}
                      className="bg-red-100 h-8 text-xs"
                    />
                  </div>
                </div>
              </div>

              {/* Financial Parameters */}
              <div>
                <h3 className="text-sm font-bold text-black mb-2 border-b border-yellow-300 pb-1">Financial Parameters</h3>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <Label htmlFor="priceFrom" className="text-xs">Price ($)</Label>
                    <Input id="priceFrom" type="number" value={formData.priceFrom} onChange={(e) => handleChange('priceFrom', e.target.value)} className="bg-red-100 h-8 text-xs" min="0" />
                  </div>
                  <div>
                    <Label htmlFor="equityPercentage" className="text-xs">Equity %</Label>
                    <Input id="equityPercentage" type="number" step="0.1" value={formData.equityPercentage} onChange={(e) => handleChange('equityPercentage', e.target.value)} className="bg-red-100 h-8 text-xs" min="0" max="100" />
                  </div>
                  <div>
                    <Label htmlFor="equityInterestRate" className="text-xs">Equity Rate %</Label>
                    <Input id="equityInterestRate" type="number" step="0.01" value={formData.equityInterestRate} onChange={(e) => handleChange('equityInterestRate', e.target.value)} className="bg-red-100 h-8 text-xs" min="0" max="20" />
                  </div>
                  <div>
                    <Label htmlFor="loanInterestRate" className="text-xs">Loan Rate %</Label>
                    <Input id="loanInterestRate" type="number" step="0.01" value={formData.loanInterestRate} onChange={(e) => handleChange('loanInterestRate', e.target.value)} className="bg-red-100 h-8 text-xs" min="0" max="20" />
                  </div>
                  <div>
                    <Label htmlFor="taxRate" className="text-xs">Tax Rate %</Label>
                    <Input id="taxRate" type="number" step="0.01" value={formData.taxRate} onChange={(e) => handleChange('taxRate', e.target.value)} className="bg-red-100 h-8 text-xs" min="0" max="100" />
                  </div>
                  <div>
                    <Label htmlFor="managementFees" className="text-xs">Mgmt Fees %</Label>
                    <Input id="managementFees" type="number" step="0.1" value={formData.managementFees} onChange={(e) => handleChange('managementFees', e.target.value)} className="bg-red-100 h-8 text-xs" min="0" max="100" />
                  </div>
                  <div>
                    <Label htmlFor="rentPerWeek" className="text-xs">Rent/Week ($)</Label>
                    <Input id="rentPerWeek" type="number" value={formData.rentPerWeek} onChange={(e) => handleChange('rentPerWeek', e.target.value)} className="bg-red-100 h-8 text-xs" min="0" />
                  </div>
                </div>
              </div>

              {/* LVR Note */}
              <div className="bg-yellow-200 p-2 rounded border border-yellow-400">
                <p className="font-bold text-center text-xs">LVR: {formData.equityPercentage}% Equity + {loanPercentage}% Loan</p>
              </div>

              {/* Generate PDF Report Button */}
              <Button 
                onClick={generatePDFReport} 
                className="w-full bg-amber-600 hover:bg-amber-700 text-white font-bold"
              >
                <FileText className="h-4 w-4 mr-2" />
                Generate PDF Report
              </Button>
            </div>

            {/* COLUMN 2 */}
            <div className="space-y-3">
              {/* Initial Capital Outlay */}
              <div>
                <h3 className="text-sm font-bold text-black mb-2 border-b border-yellow-300 pb-1">Initial Capital Outlay</h3>
                <div className="space-y-0 text-xs">
                  <div className="grid grid-cols-2 gap-2">
                    <Label className="self-center text-xs">Cash Contrib</Label>
                    <div className="relative">
                      <span className="absolute left-1 top-1 text-xs text-gray-600">$</span>
                      <Input type="number" value={formData.cashContributions} onChange={(e) => handleChange('cashContributions', e.target.value)} className="bg-red-100 h-7 text-xs text-right pl-4 pr-1 rounded [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" min="0" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Label className="self-center text-xs">Purchase Price</Label>
                    <div className="bg-white p-1 rounded text-right font-semibold">${purchasePrice.toLocaleString('en-AU', { maximumFractionDigits: 0 })}</div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Label className="self-center text-xs">Property Balance</Label>
                    <div className="bg-white p-1 rounded text-right font-semibold">${propertyBalance.toLocaleString('en-AU', { maximumFractionDigits: 0 })}</div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Label className="self-center text-xs">Equity Deposit ({formData.equityPercentage}%)</Label>
                    <div className="bg-white p-1 rounded text-right font-semibold">${equityDeposit.toLocaleString('en-AU', { maximumFractionDigits: 0 })}</div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Label className="self-center text-xs">Stamp Duty</Label>
                    <div className="relative">
                      <span className="absolute left-1 top-1 text-xs text-gray-600">$</span>
                      <Input type="number" value={formData.stampDuty} onChange={(e) => handleChange('stampDuty', e.target.value)} className="bg-red-100 h-7 text-xs text-right pl-4 pr-1 rounded [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" min="0" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Label className="self-center text-xs font-bold">Total Equity</Label>
                    <div className="bg-white p-1 rounded text-right font-bold">${equityContributed.toLocaleString('en-AU', { maximumFractionDigits: 0 })}</div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Label className="self-center text-xs">Legal Costs</Label>
                    <div className="relative">
                      <span className="absolute left-1 top-1 text-xs text-gray-600">$</span>
                      <Input type="number" value={formData.legalCosts} onChange={(e) => handleChange('legalCosts', e.target.value)} className="bg-red-100 h-7 text-xs text-right pl-4 pr-1 rounded [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" min="0" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Label className="self-center text-xs">Building Insp</Label>
                    <div className="relative">
                      <span className="absolute left-1 top-1 text-xs text-gray-600">$</span>
                      <Input type="number" value={formData.buildingInspection} onChange={(e) => handleChange('buildingInspection', e.target.value)} className="bg-red-100 h-7 text-xs text-right pl-4 pr-1 rounded [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" min="0" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Label className="self-center text-xs">Other</Label>
                    <div className="relative">
                      <span className="absolute left-1 top-1 text-xs text-gray-600">$</span>
                      <Input type="number" value={formData.otherCosts} onChange={(e) => handleChange('otherCosts', e.target.value)} className="bg-red-100 h-7 text-xs text-right pl-4 pr-1 rounded [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" min="0" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 border-t border-black pt-1 mt-1">
                    <Label className="self-center font-bold text-xs">Total Outlay</Label>
                    <div className="bg-white p-1 rounded text-right font-bold">${initialCapitalOutlay.toLocaleString('en-AU', { maximumFractionDigits: 0 })}</div>
                  </div>
                </div>
              </div>

              {/* Ongoing Costs */}
              <div>
                <h3 className="text-sm font-bold text-black mb-2 border-b border-yellow-300 pb-1">Ongoing (per annum)</h3>
                <div className="space-y-0 text-xs">
                  <div className="grid grid-cols-2 gap-2">
                    <Label className="self-center text-xs">Rental Income</Label>
                    <div className="bg-white p-1 rounded text-right font-semibold">${rentalIncomePA.toLocaleString('en-AU', { maximumFractionDigits: 0 })}</div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Label className="self-center text-xs">Debt ({loanPercentage}%)</Label>
                    <div className="bg-white p-1 rounded text-right font-semibold">${debt.toLocaleString('en-AU', { maximumFractionDigits: 0 })}</div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Label className="self-center text-xs">Int Equity @ {formData.equityInterestRate}%</Label>
                    <div className="bg-white p-1 rounded text-right font-semibold">${interestOnEquity.toLocaleString('en-AU', { maximumFractionDigits: 0 })}</div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Label className="self-center text-xs">Int Loan @ {formData.loanInterestRate}%</Label>
                    <div className="bg-white p-1 rounded text-right font-semibold">${interestOnBankFinance.toLocaleString('en-AU', { maximumFractionDigits: 0 })}</div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Label className="self-center text-xs">Total Interest</Label>
                    <div className="bg-white p-1 rounded text-right font-semibold">${totalInterest.toLocaleString('en-AU', { maximumFractionDigits: 0 })}</div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Label className="self-center text-xs">Insurance</Label>
                    <div className="relative">
                      <span className="absolute left-1 top-1 text-xs text-gray-600">$</span>
                      <Input type="number" value={formData.landlordsInsurance} onChange={(e) => handleChange('landlordsInsurance', e.target.value)} className="bg-red-100 h-7 text-xs text-right pl-4 pr-1 rounded [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" min="0" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Label className="self-center text-xs">Rates & Water</Label>
                    <div className="relative">
                      <span className="absolute left-1 top-1 text-xs text-gray-600">$</span>
                      <Input type="number" value={formData.ratesWater} onChange={(e) => handleChange('ratesWater', e.target.value)} className="bg-red-100 h-7 text-xs text-right pl-4 pr-1 rounded [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" min="0" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Label className="self-center text-xs">Body Corp p.a.</Label>
                    <div className="relative">
                      <span className="absolute left-1 top-1 text-xs text-gray-600">$</span>
                      <Input type="number" value={formData.bodyCorporate} onChange={(e) => handleChange('bodyCorporate', e.target.value)} className="bg-red-100 h-7 text-xs text-right pl-4 pr-1 rounded [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" min="0" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Label className="self-center text-xs">Agents @ {formData.managementFees}%</Label>
                    <div className="bg-white p-1 rounded text-right font-semibold">${agentsFees.toLocaleString('en-AU', { maximumFractionDigits: 0 })}</div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Label className="self-center text-xs">Other Miscellaneous</Label>
                    <div className="relative">
                      <span className="absolute left-1 top-1 text-xs text-gray-600">$</span>
                      <Input type="number" value={formData.otherMiscellaneous} onChange={(e) => handleChange('otherMiscellaneous', e.target.value)} className="bg-red-100 h-7 text-xs text-right pl-4 pr-1 rounded [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" min="0" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 border-t border-black pt-1 mt-1">
                    <Label className="self-center font-bold text-xs">Total Expenses</Label>
                    <div className="bg-white p-1 rounded text-right font-bold">${totalExpenses.toLocaleString('en-AU', { maximumFractionDigits: 0 })}</div>
                  </div>
                </div>
              </div>

              {/* Weekly Summary */}
              <div>
                <h3 className="text-sm font-bold text-black mb-2 border-b border-yellow-300 pb-1">Weekly Summary</h3>
                <div className="space-y-0 text-xs">
                  <div className="grid grid-cols-2 gap-2">
                    <Label className="self-center text-xs">Weekly Rent</Label>
                    <div className="bg-white p-1 rounded text-right font-semibold">${formData.rentPerWeek.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Label className="self-center text-xs">Weekly Expenses (excl Int)</Label>
                    <div className="bg-white p-1 rounded text-right font-semibold">${((totalExpenses - totalInterest) / 52).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Label className="self-center text-xs">Weekly Interest</Label>
                    <div className="bg-white p-1 rounded text-right font-semibold">${(totalInterest / 52).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 border-t border-black pt-1 mt-1">
                    <Label className="self-center font-bold text-xs">Weekly Total Expenses</Label>
                    <div className="bg-white p-1 rounded text-right font-bold">${(totalExpenses / 52).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Label className="self-center font-bold text-xs">Weekly Net (Before Tax)</Label>
                    <div className={`p-1 rounded text-right font-bold ${(formData.rentPerWeek - (totalExpenses / 52)) < 0 ? 'bg-red-100' : 'bg-green-100'}`}>${(formData.rentPerWeek - (totalExpenses / 52)).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* COLUMN 3 */}
            <div className="space-y-3">
              {/* Cashflow Analysis */}
              <div>
                <h3 className="text-sm font-bold text-black mb-2 border-b border-yellow-300 pb-1">Cashflow Analysis</h3>
                <div className="space-y-1 text-xs">
                  <div className="grid grid-cols-2 gap-2">
                    <Label className="self-center text-xs">Before Tax</Label>
                    <div className="bg-white p-1 rounded text-right font-semibold">${cashflowBeforeTax.toLocaleString('en-AU', { maximumFractionDigits: 0 })}</div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Label className="self-center text-xs">Depreciation</Label>
                    <div className="relative">
                      <span className="absolute left-1 top-1 text-xs text-gray-600">$</span>
                      <Input type="number" value={formData.depreciation} onChange={(e) => handleChange('depreciation', e.target.value)} className="bg-red-100 h-7 text-xs text-right pl-4 pr-1 rounded [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" min="0" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Label className="self-center text-xs">Paper Loss</Label>
                    <div className="bg-white p-1 rounded text-right font-semibold">${totalPaperLoss.toLocaleString('en-AU', { maximumFractionDigits: 0 })}</div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Label className="self-center text-xs">Tax Refund</Label>
                    <div className="bg-white p-1 rounded text-right font-semibold">${taxRefund.toLocaleString('en-AU', { maximumFractionDigits: 0 })}</div>
                  </div>
                </div>
              </div>

              {/* Results */}
              <div className="space-y-2">
                <div className="bg-green-50 p-2 rounded border border-green-300">
                  <div className="grid grid-cols-2 gap-2">
                    <Label className="self-center font-bold text-xs">Gross Yield %</Label>
                    <div className="text-right font-bold text-sm">{grossYield.toFixed(2)}%</div>
                  </div>
                </div>
                <div className="bg-blue-50 p-2 rounded border border-blue-300">
                  <div className="grid grid-cols-2 gap-2">
                    <Label className="self-center font-bold text-xs">Net Cashflow p.a.</Label>
                    <div className="text-right font-bold text-sm">${netCashflowPA.toLocaleString('en-AU', { maximumFractionDigits: 0 })}</div>
                  </div>
                </div>
                <div className="bg-purple-50 p-2 rounded border border-purple-300">
                  <div className="grid grid-cols-2 gap-2">
                    <Label className="self-center font-bold text-xs">Net Cashflow/Week</Label>
                    <div className="text-right font-bold text-sm">${netCashflowPerWeek.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                  </div>
                </div>
              </div>

              {/* Weekly Breakdown */}
              <div>
                <h3 className="text-sm font-bold text-black mb-2 border-b border-yellow-300 pb-1">Weekly Breakdown</h3>
                <div className="border rounded overflow-hidden text-xs">
                  <table className="w-full">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="p-1 text-left font-bold">Contributor</th>
                        <th className="p-1 text-right font-bold">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-t">
                        <td className="p-1">{youPay > 0 ? 'You contribute' : 'You receive'}</td>
                        <td className={`p-1 text-right font-semibold ${youPay > 0 ? 'text-red-600' : 'text-green-600'}`}>${Math.abs(youPay).toFixed(2)}</td>
                      </tr>
                      <tr className="border-t bg-gray-50">
                        <td className="p-1">Tenant contributes</td>
                        <td className="p-1 text-right font-semibold text-green-600">${tenantPays.toFixed(2)}</td>
                      </tr>
                      <tr className="border-t">
                        <td className="p-1">Taxman contributes</td>
                        <td className="p-1 text-right font-semibold text-blue-600">${taxmanPays.toFixed(2)}</td>
                      </tr>
                      <tr className="border-t bg-yellow-100">
                        <td className="p-1 font-bold">Total Weekly Expenses</td>
                        <td className="p-1 text-right font-bold">${totalWeekly.toFixed(2)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              
              {/* Assumptions */}
              <div className="bg-yellow-200 p-2 rounded border border-yellow-400">
                <p className="text-xs font-bold text-center">Figures are estimates - Refund based on marginal tax rate</p>
              </div>

              {/* Contributor Column Graph */}
              <div>
                <h3 className="text-sm font-bold text-black mb-2 border-b border-yellow-300 pb-1">Contributor Breakdown</h3>
                <div className="text-xs">
                  <div className="flex items-end justify-around gap-2 h-40 mb-2">
                    {/* You Column */}
                    {youPay > 0 && (
                      <div className="flex flex-col items-center flex-1">
                        <div className="text-center mb-2 font-bold">${youPay.toFixed(2)}</div>
                        <div className="w-full flex flex-col justify-end items-center relative" style={{ height: '120px' }}>
                          <div 
                            className="absolute text-center font-bold text-black w-full"
                            style={{ 
                              bottom: `${Math.abs((youPay / totalWeeklyForGraph * 100))}%`,
                              transform: 'translateY(-4px)'
                            }}
                          >
                            {Math.abs((youPay / totalWeeklyForGraph) * 100).toFixed(0)}%
                          </div>
                          <div 
                            className="w-full rounded-t bg-red-500"
                            style={{ height: `${Math.abs((youPay / totalWeeklyForGraph * 100))}%` }}
                          >
                          </div>
                        </div>
                        <div className="text-center mt-1 font-semibold">You</div>
                      </div>
                    )}
                    
                    {youPay < 0 && (
                      <div className="flex flex-col items-center flex-1">
                        <div className="text-center mb-2 font-bold">${Math.abs(youPay).toFixed(2)}</div>
                        <div className="w-full flex flex-col justify-end items-center relative" style={{ height: '120px' }}>
                          <div className="absolute text-center font-bold text-black w-full" style={{ bottom: '0%', transform: 'translateY(-4px)' }}>
                            0%
                          </div>
                          <div className="w-full h-full flex items-center justify-center">
                            <span className="text-green-600 font-bold text-xs">Surplus</span>
                          </div>
                        </div>
                        <div className="text-center mt-1 font-semibold">You receive</div>
                      </div>
                    )}
                    
                    {/* Tenant Column */}
                    <div className="flex flex-col items-center flex-1">
                      <div className="text-center mb-2 font-bold">${tenantPays.toFixed(2)}</div>
                      <div className="w-full flex flex-col justify-end items-center relative" style={{ height: '120px' }}>
                        <div 
                          className="absolute text-center font-bold text-black w-full"
                          style={{ 
                            bottom: `${(tenantPays / totalWeeklyForGraph * 100)}%`,
                            transform: 'translateY(-4px)'
                          }}
                        >
                          {((tenantPays / totalWeeklyForGraph) * 100).toFixed(0)}%
                        </div>
                        <div 
                          className="bg-green-500 w-full rounded-t"
                          style={{ height: `${(tenantPays / totalWeeklyForGraph * 100)}%` }}
                        >
                        </div>
                      </div>
                      <div className="text-center mt-1 font-semibold">Tenant</div>
                    </div>
                    
                    {/* Taxman Column */}
                    <div className="flex flex-col items-center flex-1">
                      <div className="text-center mb-2 font-bold">${taxmanPays.toFixed(2)}</div>
                      <div className="w-full flex flex-col justify-end items-center relative" style={{ height: '120px' }}>
                        <div 
                          className="absolute text-center font-bold text-black w-full"
                          style={{ 
                            bottom: `${(taxmanPays / totalWeeklyForGraph * 100)}%`,
                            transform: 'translateY(-4px)'
                          }}
                        >
                          {((taxmanPays / totalWeeklyForGraph) * 100).toFixed(0)}%
                        </div>
                        <div 
                          className="bg-blue-500 w-full rounded-t"
                          style={{ height: `${(taxmanPays / totalWeeklyForGraph * 100)}%` }}
                        >
                        </div>
                      </div>
                      <div className="text-center mt-1 font-semibold">Taxman</div>
                    </div>
                  </div>
                  
                  {/* Total */}
                  <div className="pt-2 border-t border-gray-300">
                    <div className="flex justify-between">
                      <span className="font-bold">Total Weekly</span>
                      <span className="font-bold text-lg">${totalWeekly.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* My Saved Calculations Section */}
      <Card className="border-2 border-blue-300">
        <CardHeader className="bg-gradient-to-r from-blue-100 to-cyan-100 py-3">
          <CardTitle className="text-lg font-bold">My Saved Calculations</CardTitle>
        </CardHeader>
        <CardContent className="p-4">
          {savedCalculations.length > 0 ? (
            <div className="overflow-x-auto">
              <div className="border rounded-lg min-w-[800px]">
                {/* Fixed Header */}
                <div className="bg-gray-50 border-b px-4 py-3 text-xs font-semibold text-gray-700">
                  <div className="flex">
                    <div className="w-48 flex-shrink-0">Property Address</div>
                    <div className="w-32 flex-shrink-0">Suburb</div>
                    <div className="w-40 flex-shrink-0">Saved Date</div>
                    <div className="w-32 flex-shrink-0">User</div>
                    <div className="w-32 flex-shrink-0 text-right">Actions</div>
                  </div>
                </div>
                
                {/* Scrollable Content */}
                <div className="max-h-[50vh] overflow-y-auto">
                  {savedCalculations.map((calc) => {
                    const savedDate = new Date(calc.saved_date);
                    const formattedDate = savedDate.toLocaleDateString('en-AU', { 
                      day: '2-digit', 
                      month: 'short', 
                      year: 'numeric' 
                    });
                    const displayName = `${calc.suburb} - ${calc.property_address}`;
                    
                    return (
                      <div key={calc.id} className="flex border-b border-gray-100 hover:bg-blue-50 px-4 py-3 text-sm">
                        <div className="w-48 flex-shrink-0 font-medium truncate" title={calc.property_address}>
                          {calc.property_address}
                        </div>
                        <div className="w-32 flex-shrink-0 truncate" title={calc.suburb}>
                          {calc.suburb}
                        </div>
                        <div className="w-40 flex-shrink-0">{formattedDate}</div>
                        <div className="w-32 flex-shrink-0 text-xs truncate" title={calc.user_email}>
                          {calc.user_email}
                        </div>
                        <div className="w-32 flex-shrink-0 text-right">
                          <div className="flex gap-1 justify-end">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleLoadCalculation(calc.id)}
                              className="h-8 w-8 p-0"
                              title="Load Calculation"
                            >
                              <Eye className="h-3 w-3 text-blue-600" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteCalculation(calc.id, displayName)}
                              className="h-8 w-8 p-0"
                              title="Delete Calculation"
                            >
                              <Trash2 className="h-3 w-3 text-red-500" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <Calculator className="mx-auto h-12 w-12 mb-3 text-gray-400" />
              <p className="text-base mb-1">No saved calculations yet</p>
              <p className="text-sm">Fill in the calculator above and click "Save" to store your calculations</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <Toaster position="top-right" />
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/*" element={
            <ProtectedRoute>
              <Layout>
                <Routes>
                  <Route path="/" element={<HomePage />} />
                  <Route path="/group/home" element={<GroupPage groupKey="home" />} />
                  <Route path="/group/clients" element={<GroupPage groupKey="clients" />} />
                  <Route path="/group/properties" element={<GroupPage groupKey="properties" />} />
                  <Route path="/group/financial" element={<GroupPage groupKey="financial" />} />
                  <Route path="/group/operations" element={<GroupPage groupKey="operations" />} />
                  <Route path="/group/settings" element={<GroupPage groupKey="settings" />} />
                  <Route path="/dashboard" element={<RoleBasedRoute adminOnly={true}><Dashboard /></RoleBasedRoute>} />
                  <Route path="/invoices" element={<RoleBasedRoute adminOnly={true}><InvoicesList /></RoleBasedRoute>} />
                  <Route path="/invoices/create" element={<RoleBasedRoute adminOnly={true}><InvoiceForm /></RoleBasedRoute>} />
                  <Route path="/invoices/edit/:id" element={<RoleBasedRoute adminOnly={true}><InvoiceForm editMode={true} /></RoleBasedRoute>} />
                  <Route path="/invoices/:id" element={<RoleBasedRoute adminOnly={true}><InvoiceView /></RoleBasedRoute>} />
                  <Route path="/customers" element={<RoleBasedRoute adminOnly={true}><ClientsManagement /></RoleBasedRoute>} />
                  <Route path="/client-invoices" element={<RoleBasedRoute adminOnly={true}><ClientInvoicesManagement /></RoleBasedRoute>} />
                  <Route path="/contacts" element={<RoleBasedRoute adminOnly={true}><ContactsManagement /></RoleBasedRoute>} />
                  <Route path="/referral-partners" element={<RoleBasedRoute adminOnly={true}><ReferralPartnersManagement /></RoleBasedRoute>} />
                  <Route path="/purchasers" element={<RoleBasedRoute adminOnly={true}><PurchasersManagement /></RoleBasedRoute>} />
                  <Route path="/properties" element={<RoleBasedRoute adminOnly={true}><PropertiesManagement /></RoleBasedRoute>} />
                  <Route path="/commissions" element={<RoleBasedRoute adminOnly={true}><CommissionsManagement /></RoleBasedRoute>} />
                  <Route path="/reports" element={<RoleBasedRoute adminOnly={true}><ReportsManagement /></RoleBasedRoute>} />
                  <Route path="/time-tracking" element={<RoleBasedRoute adminOnly={true}><TimeTracking /></RoleBasedRoute>} />
                  <Route path="/cashflow-calculator" element={<CashFlowCalculator />} />
                  <Route path="/settings" element={<RoleBasedRoute adminOnly={true}><SettingsPage /></RoleBasedRoute>} />
                </Routes>
              </Layout>
            </ProtectedRoute>
          } />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;